import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser, useClerk, SignInButton, SignUpButton, SignedIn, SignedOut } from "@clerk/clerk-react";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const PROXY_URL = process.env.REACT_APP_PROXY_URL || "http://localhost:3001";

// Persistent anonymous device ID — survives page reloads, unique per browser
function getAnonId() {
  const key = "limji_anon_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(key, id);
  }
  return id;
}
const ANON_ID = getAnonId();
const MAX_HISTORY = 50;
const MAX_TEMPLATES = 50;

const ls = {
  get: (k, fb) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

async function callClaude(prompt, clerkUserId) {
  const headers = { "Content-Type": "application/json" };
  if (clerkUserId) headers["x-clerk-user-id"] = clerkUserId;
  else headers["x-anon-id"] = ANON_ID;

  const res = await fetch(`${PROXY_URL}/api/claude`, {
    method: "POST",
    headers,
    body: JSON.stringify({ max_tokens: 2000, messages: [{ role: "user", content: prompt }] }),
  });

  if (res.status === 429) {
    const e = await res.json().catch(() => ({}));
    const err = new Error(e.error || "daily_limit_reached");
    err.limitData = e;
    throw err;
  }

  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Error ${res.status}`); }
  const data = await res.json();

  // Update remaining count from server response
  if (typeof data.remaining === "number") {
    window.dispatchEvent(new CustomEvent("limji_usage_update", { detail: { remaining: data.remaining, limit: data.limit, isPro: data.isPro } }));
  }

  const raw = data.content.map(b => b.text || "").join("");

  // ── Robust JSON extraction ──────────────────────────────────────────────
  // 1. Strip markdown code fences
  let text = raw.replace(/```json\s*/g, "").replace(/```\s*/g, "").trim();

  // 2. Extract only the JSON object (first { ... })
  const firstBrace = text.indexOf("{");
  const lastBrace  = text.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    text = text.slice(firstBrace, lastBrace + 1);
  }

  // 3. Try direct parse
  try {
    return JSON.parse(text);
  } catch {
    // 4. Fix common JSON issues: trailing commas, unescaped newlines in strings
    const fixed = text
      .replace(/,\s*([}\]])/g, "$1")           // trailing commas
      .replace(/\n/g, "\\n")                    // bare newlines inside strings
      .replace(/\r/g, "")                       // carriage returns
      .replace(/[\u0000-\u001F\u007F]/g, " ");  // eslint-disable-line no-control-regex
    try {
      return JSON.parse(fixed);
    } catch (e2) {
      throw new Error("Could not parse AI response. Please try again. (" + e2.message + ")");
    }
  }
}

// Each category now has its own toggles — see AD_CATEGORIES

const ALL_TOGGLES = {
  urgentSale:           { label: "🔴 Urgent Sale",               prompt: "URGENT SALE — seller must sell quickly. Use urgency in the copy without being fake." },
  priceNegotiable:      { label: "🤝 Price Negotiable",          prompt: "Price is negotiable. Mention this naturally to invite offers." },
  accidentFree:         { label: "✅ Accident Free",             prompt: "The vehicle is accident-free. Highlight this as a trust signal." },
  singleOwner:          { label: "👤 Single Owner",              prompt: "Single owner vehicle. Mention this as it increases buyer confidence." },
  fullServiceHistory:   { label: "📋 Full Service History",      prompt: "Full service history available. This is a strong selling point — mention it." },
  warrantyIncluded:     { label: "🛡️ Warranty Included",         prompt: "Warranty is still included. Highlight this to reduce buyer hesitation." },
  furnished:            { label: "🛋️ Fully Furnished",           prompt: "Property is fully furnished. Mention this clearly as many buyers specifically look for this." },
  billsIncluded:        { label: "💡 Bills Included",            prompt: "Bills are included in the price. This is a major value-add — highlight it." },
  petFriendly:          { label: "🐾 Pet Friendly",              prompt: "Property is pet friendly. Mention this as it is a dealbreaker for many renters." },
  directFromOwner:      { label: "🏷️ Direct from Owner",         prompt: "Listing is direct from owner with no agent fees. This is a strong selling point — highlight it." },
  originalBox:          { label: "📦 Original Box Included",     prompt: "Comes with original box and packaging. Mention this as it adds value." },
  warrantyActive:       { label: "🛡️ Warranty Still Active",     prompt: "Warranty is still active. This significantly reduces buyer risk — highlight it." },
  accessoriesIncluded:  { label: "🎁 All Accessories Included",  prompt: "All original accessories are included. Mention what is in the box." },
  authentic:            { label: "✅ Authentic / Original",      prompt: "Item is 100% authentic. This is critical for luxury or branded items — emphasize it." },
  neverWorn:            { label: "🏷️ Never Worn",                prompt: "Item has never been worn. Treat this as near-new condition." },
  boxAndDustbag:        { label: "📦 Box and Dustbag Included",  prompt: "Comes with original box and dustbag. Mention this as it preserves resale value." },
  assemblyIncluded:     { label: "🔧 Assembly Included",         prompt: "Assembly is included in the price. Mention this as it removes friction for buyers." },
  deliveryAvailable:    { label: "🚚 Delivery Available",        prompt: "Delivery is available. Mention this to make it easier for buyers to say yes." },
  sellingDueToMoving:   { label: "📦 Selling Due to Moving",     prompt: "Seller is moving — this explains why a good item is being sold and builds trust." },
  freeConsultation:     { label: "🎁 Free First Consultation",   prompt: "Offering a free first consultation. This reduces risk for new clients — mention it." },
  referencesAvailable:  { label: "⭐ References Available",      prompt: "References are available upon request. Builds credibility — mention it." },
  certifiedLicensed:    { label: "🏅 Certified / Licensed",      prompt: "Service provider is certified or licensed. Highlight this as it builds professional trust." },
  sameDayAvailable:     { label: "⚡ Same Day Available",        prompt: "Same day service is available. Highlight this for clients who need urgent help." },
  urgentHiring:         { label: "🔴 Urgent Hiring",             prompt: "This is an urgent hire — the role needs to be filled immediately. Convey urgency professionally." },
  visaSponsorship:      { label: "✈️ Visa Sponsorship",          prompt: "Visa sponsorship is available. This is a major draw for expat candidates — highlight it." },
  competitiveSalary:    { label: "💰 Competitive Salary",        prompt: "The salary package is competitive. Mention this to attract quality candidates." },
  remoteHybrid:         { label: "🏠 Remote / Hybrid",           prompt: "The role is remote or hybrid. Highlight this as it greatly expands the candidate pool." },
  homemade:             { label: "👩‍🍳 Homemade",                 prompt: "Food is homemade. Emphasize the personal home-cooked quality." },
  freeDelivery:         { label: "🚚 Free Delivery",             prompt: "Free delivery is included. This is a strong incentive — highlight it clearly." },
  halalCertified:       { label: "☪️ Halal Certified",           prompt: "Food is Halal certified. Mention this as it is important for many customers in the region." },
  preOrderAvailable:    { label: "📅 Pre-Order Available",       prompt: "Pre-orders are available. Mention this to capture demand early." },
  cateringAvailable:    { label: "🍱 Catering Available",        prompt: "Catering for events is available. Mention this to attract bulk and event orders." },
  sealedUnopened:       { label: "🔒 Sealed / Unopened",         prompt: "Item is sealed and unopened. This is maximum trust for health products — highlight it." },
  authenticBrand:       { label: "✅ Authentic Brand",           prompt: "Item is an authentic branded product. Emphasize this to justify the price." },
  fullSet:              { label: "🎁 Full Set Included",         prompt: "Full set with all components included. Mention everything that comes with it." },
  limitedStock:         { label: "⚡ Limited Stock",              prompt: "Limited stock available. Mention scarcity naturally to encourage action without being fake." },
  freeShipping:         { label: "🚚 Free Shipping",              prompt: "Free shipping is included. Highlight this clearly as it removes a common purchase barrier." },
  moneyBackGuarantee:   { label: "🛡️ Money-Back Guarantee",      prompt: "Comes with a money-back guarantee. This significantly reduces buyer risk — mention it." },
  bundleDeal:           { label: "🎁 Bundle Deal Available",     prompt: "A bundle deal is available. Mention this to increase average order value." },
  fastDispatch:         { label: "⚡ Fast Dispatch",              prompt: "Fast dispatch — orders ship quickly. Highlight this to reduce purchase hesitation." },
};

const AD_CATEGORIES = [
  { id: "electronics", icon: "📱", label: "Electronics and Gadgets",
    question: "What's the item?",
    fields: ["title", "brand", "model", "condition", "keySpecs", "price", "location"],
    toggles: ["originalBox", "warrantyActive", "accessoriesIncluded", "priceNegotiable", "urgentSale"],
    placeholders: { title: "e.g. iPhone 14 Pro 256GB Space Black", brand: "e.g. Apple, Samsung, Sony", model: "e.g. iPhone 14 Pro, Galaxy S23", condition: "e.g. Like new, Good, Minor scratches", keySpecs: "e.g. 256GB, battery health 91%, Face ID working perfectly", price: "e.g. AED 2,800", location: "e.g. Dubai Marina" } },
  { id: "vehicles", icon: "🚗", label: "Vehicles and Cars",
    question: "Tell us about the vehicle",
    fields: ["title", "brand", "model", "year", "mileage", "condition", "keySpecs", "price", "location"],
    toggles: ["accidentFree", "singleOwner", "fullServiceHistory", "warrantyIncluded", "priceNegotiable", "urgentSale"],
    placeholders: { title: "e.g. Toyota Camry 2020 — Low Mileage", brand: "e.g. Toyota, BMW, Nissan", model: "e.g. Camry, X5, Patrol", year: "e.g. 2021", mileage: "e.g. 45,000 km", condition: "e.g. Excellent, Very Good, Good", keySpecs: "e.g. 2.5L engine, leather seats, sunroof, rear camera", price: "e.g. AED 68,000", location: "e.g. Dubai, Abu Dhabi" } },
  { id: "realestate", icon: "🏠", label: "Real Estate and Rentals",
    question: "Describe the property",
    fields: ["title", "propertyType", "bedrooms", "bathrooms", "area", "location", "keyFeatures", "price", "saleOrRent"],
    toggles: ["furnished", "billsIncluded", "petFriendly", "directFromOwner", "priceNegotiable", "urgentSale"],
    placeholders: { title: "e.g. Spacious 2BR with Sea View — JBR", propertyType: "e.g. Apartment, Villa, Studio", bedrooms: "e.g. 2", bathrooms: "e.g. 2", area: "e.g. 1,200 sqft", location: "e.g. JBR, Downtown Dubai, Business Bay", keyFeatures: "e.g. Sea view, gym, pool, covered parking, near metro", price: "e.g. AED 95,000/year", saleOrRent: "e.g. For Rent / For Sale / Short-term" } },
  { id: "fashion", icon: "👗", label: "Fashion and Clothing",
    question: "What's the item?",
    fields: ["title", "brand", "category", "size", "condition", "keyDetails", "price", "location"],
    toggles: ["authentic", "neverWorn", "boxAndDustbag", "priceNegotiable"],
    placeholders: { title: "e.g. Gucci GG Marmont Belt — Black", brand: "e.g. Gucci, Zara, Nike", category: "e.g. Belt, Sneakers, Handbag, Dress", size: "e.g. M, EU 42, fits UK 10", condition: "e.g. Brand new, Worn twice, Good", keyDetails: "e.g. Size 90cm, gold buckle, comes with dustbag", price: "e.g. AED 850", location: "e.g. Dubai, Ships nationwide" } },
  { id: "furniture", icon: "🛋️", label: "Furniture and Home",
    question: "What's the item?",
    fields: ["title", "category", "material", "dimensions", "condition", "keyDetails", "price", "location"],
    toggles: ["assemblyIncluded", "deliveryAvailable", "sellingDueToMoving", "priceNegotiable", "urgentSale"],
    placeholders: { title: "e.g. IKEA MALM King Bed Frame — White", category: "e.g. Sofa, Dining table, Bed, Wardrobe", material: "e.g. Solid oak, MDF, Leather, Fabric", dimensions: "e.g. 200x90cm, L-shape 280x200cm", condition: "e.g. Like new, Good, Minor wear", keyDetails: "e.g. Grey fabric, barely used 6 months, no stains", price: "e.g. AED 1,200", location: "e.g. Dubai Silicon Oasis" } },
  { id: "jobs", icon: "💼", label: "Jobs and Hiring",
    question: "Describe the role",
    fields: ["title", "companyName", "jobType", "experienceRequired", "responsibilities", "salary", "location", "workStyle"],
    toggles: ["urgentHiring", "visaSponsorship", "competitiveSalary", "remoteHybrid"],
    placeholders: { title: "e.g. Senior Sales Manager — Dubai", companyName: "e.g. Acme Corp, Al Futtaim Group", jobType: "e.g. Full-time, Part-time, Contract", experienceRequired: "e.g. 3-5 years in B2B sales", responsibilities: "e.g. Lead a team of 5, manage key accounts, hit monthly targets", salary: "e.g. AED 15,000-20,000 + commission", location: "e.g. Dubai, Abu Dhabi, Remote", workStyle: "e.g. On-site, Hybrid, Remote" } },
  { id: "services", icon: "🛠️", label: "Services",
    question: "What do you offer?",
    fields: ["title", "serviceOrRole", "experienceYears", "skills", "availability", "location", "rate"],
    toggles: ["freeConsultation", "referencesAvailable", "certifiedLicensed", "sameDayAvailable"],
    placeholders: { title: "e.g. Freelance Graphic Designer — Logos and Branding", serviceOrRole: "e.g. Graphic Designer, Electrician, Private Chef", experienceYears: "e.g. 7 years", skills: "e.g. Logo design, Figma, Adobe Suite — 50+ clients served", availability: "e.g. Weekdays, Full-time, Evenings", location: "e.g. Dubai — remote or on-site", rate: "e.g. AED 200/hour or project-based" } },
  { id: "food", icon: "🍽️", label: "Food and Restaurants",
    question: "What are you offering?",
    fields: ["title", "businessName", "cuisine", "offer", "location", "openingHours", "contact"],
    toggles: ["homemade", "freeDelivery", "halalCertified", "preOrderAvailable", "cateringAvailable"],
    placeholders: { title: "e.g. Homemade Kabsa Trays — Orders Open Now", businessName: "e.g. Mama Fatima's Kitchen", cuisine: "e.g. Saudi, Lebanese, Homemade desserts", offer: "e.g. Full tray serves 8-10, AED 120. Order 24hrs in advance.", location: "e.g. Delivery across Dubai, Sharjah pickup", openingHours: "e.g. Orders daily, delivery 12pm-8pm", contact: "e.g. WhatsApp 050-xxx-xxxx" } },
  { id: "health", icon: "💊", label: "Health and Beauty",
    question: "What's the item?",
    fields: ["title", "category", "brand", "keyDetails", "condition", "price", "location"],
    toggles: ["sealedUnopened", "authenticBrand", "fullSet", "priceNegotiable"],
    placeholders: { title: "e.g. Dyson Airwrap Complete — All Attachments", category: "e.g. Hair tool, Skincare set, Gym equipment", brand: "e.g. Dyson, La Mer, GHD, Philips", keyDetails: "e.g. Includes 6 attachments, used 3 times, original box", condition: "e.g. Like new, Sealed, Lightly used", price: "e.g. AED 1,400", location: "e.g. Abu Dhabi, Ships across UAE" } },
  { id: "store", icon: "🏪", label: "Online Store Product",
    question: "Describe your product",
    fields: ["title", "productCategory", "productFeatures", "targetAudience", "productTone", "productPrice", "productLength"],
    toggles: ["limitedStock", "freeShipping", "moneyBackGuarantee", "bundleDeal", "fastDispatch"],
    placeholders: { title: "e.g. Minimalist Leather Wallet — Slim and RFID Blocking", productCategory: "e.g. Accessories, Skincare, Home decor, Tech", productFeatures: "e.g. Full grain leather, 6 card slots, 6mm slim profile, RFID blocking, handstitched", targetAudience: "e.g. Professionals, gift buyers, minimalists", productTone: "e.g. Premium, Casual, Fun, Technical", productPrice: "e.g. $79, AED 290", productLength: "medium" } },
];

const AD_FIELD_CONFIG = {
  title: { label: "Ad Title *", placeholder: "e.g. iPhone 14 Pro 256GB Space Black" },
  brand: { label: "Brand", placeholder: "e.g. Apple, Toyota, Nike" },
  model: { label: "Model", placeholder: "e.g. Pro Max, Camry, Air Force 1" },
  year: { label: "Year", placeholder: "e.g. 2021" },
  mileage: { label: "Mileage", placeholder: "e.g. 45,000 km" },
  condition: { label: "Condition", pillOptions: ["Like New", "Good", "Lightly Used", "Heavy Wear", "For Parts"] },
  keySpecs: { label: "Key Details / Specs *", placeholder: "Most important features, specs, or selling points", textarea: true },
  keyFeatures: { label: "Key Features *", placeholder: "What makes this property special", textarea: true },
  keyDetails: { label: "Key Details *", placeholder: "Important details buyers need to know", textarea: true },
  price: { label: "Price", placeholder: "e.g. $499, AED 1,800, Negotiable" },
  location: { label: "Location", placeholder: "e.g. Dubai Marina, Brooklyn NY" },
  propertyType: { label: "Property Type", pillOptions: ["Apartment", "Villa", "Studio", "Townhouse", "Office", "Shop", "Warehouse"] },
  bedrooms: { label: "Bedrooms", pillOptions: ["Studio", "1", "2", "3", "4", "5+"] },
  bathrooms: { label: "Bathrooms", pillOptions: ["1", "2", "3", "4+"] },
  area: { label: "Area (sqft/sqm)", placeholder: "e.g. 1,200 sqft" },
  saleOrRent: { label: "For Sale or Rent?", pillOptions: ["For Sale", "For Rent", "Short-term Rental"] },
  category: { label: "Category", placeholder: "e.g. Dress, Sneakers, Watch" },
  size: { label: "Size", placeholder: "e.g. M, 42, 32x30" },
  material: { label: "Material", placeholder: "e.g. Solid oak, Velvet, Leather" },
  dimensions: { label: "Dimensions", placeholder: "e.g. 200x90cm" },
  serviceOrRole: { label: "Service / Role", placeholder: "e.g. Web Designer, Plumber, Private Chef" },
  experienceYears: { label: "Years of Experience", placeholder: "e.g. 7 years" },
  skills: { label: "Key Skills / Services *", placeholder: "What you offer and do best", textarea: true },
  availability: { label: "Availability", pillOptions: ["Full-time", "Part-time", "Weekdays", "Weekends", "Flexible", "Remote Only"] },
  rate: { label: "Rate / Salary", placeholder: "e.g. $40/hr, AED 8,000/month" },
  companyName: { label: "Company Name", placeholder: "e.g. Acme Corp, Al Futtaim Group" },
  jobType: { label: "Job Type", pillOptions: ["Full-time", "Part-time", "Contract", "Freelance", "Internship"] },
  experienceRequired: { label: "Experience Required", placeholder: "e.g. 3-5 years in B2B sales" },
  responsibilities: { label: "Key Responsibilities *", placeholder: "Main duties and what success looks like", textarea: true },
  salary: { label: "Salary / Package", placeholder: "e.g. AED 15,000-20,000 + commission" },
  workStyle: { label: "Work Style", pillOptions: ["On-site", "Hybrid", "Remote", "Flexible"] },
  businessName: { label: "Business Name", placeholder: "e.g. Mama's Kitchen" },
  cuisine: { label: "Cuisine / Food Type", placeholder: "e.g. Lebanese, Homemade desserts" },
  offer: { label: "Offer / What You're Selling *", placeholder: "What exactly are you offering", textarea: true },
  openingHours: { label: "Hours / Availability", placeholder: "e.g. Daily 10am-10pm, Order by 6pm" },
  contact: { label: "Contact / Order Info", placeholder: "e.g. WhatsApp 050-xxx-xxxx" },
  productCategory: { label: "Product Category *", placeholder: "e.g. Accessories, Skincare, Home decor, Tech" },
  productFeatures: { label: "Features & Details *", placeholder: "e.g. Full grain leather, 6 card slots, RFID blocking, handstitched", textarea: true },
  targetAudience: { label: "Target Audience", placeholder: "e.g. Professionals, gift buyers, minimalists" },
  productTone: { label: "Brand Tone", pillOptions: ["Premium", "Casual & Friendly", "Playful", "Technical", "Warm & Personal"] },
  productPrice: { label: "Price", placeholder: "e.g. $79, AED 290" },
  productLength: { label: "Description Length", selectOptions: [{ value: "short", label: "Short", hint: "1-2 sentences — for product cards" }, { value: "medium", label: "Medium", hint: "2-3 paragraphs — for most product pages" }, { value: "detailed", label: "Detailed", hint: "Full breakdown — for hero products" }] },
};

const AD_FORMATS = [
  { id: "facebook", icon: "📘", label: "Facebook / Instagram" },
  { id: "google", icon: "🔍", label: "Google Search Ad" },
  { id: "marketplace", icon: "🛒", label: "Marketplace Listing" },
  { id: "whatsapp", icon: "💬", label: "WhatsApp Broadcast" },
  { id: "sms", icon: "📲", label: "SMS Promotion" },
  { id: "email", icon: "✉️", label: "Promo Email" },
];

function buildAdPrompt(categoryId, fields, formats, tone, activeToggles) {
  const cat = AD_CATEGORIES.find(c => c.id === categoryId);
  const fieldLines = Object.entries(fields).filter(([, v]) => v).map(([k, v]) => `${AD_FIELD_CONFIG[k]?.label?.replace(" *", "") || k}: ${v}`).join("\n");

  const toggleLines = activeToggles.length > 0
    ? "\nSELLER BADGES (incorporate these naturally into the copy):\n" + activeToggles.map(id => `- ${ALL_TOGGLES[id]?.prompt}`).join("\n")
    : "";

  // Online Store Product — special output with full product page copy
  if (categoryId === "store") {
    const lengthMap = { short: "1-2 sentences. Ultra punchy. For product cards.", medium: "2-3 paragraphs. Balanced detail. For most product pages.", detailed: "3-4 paragraphs with full breakdown. For hero products." };
    const lengthGuide = lengthMap[(fields.productLength || "medium").toLowerCase()] || lengthMap.medium;
    return `You are a product copywriter for an online store. Write compelling product copy.
PRODUCT DETAILS (use ONLY what is provided — never invent features or claims):
${fieldLines}${toggleLines}
Length: ${lengthGuide} | Tone: ${fields.productTone || tone}
RULES: Only use provided info. Never invent. Match brand tone. Write for buyer already on product page. Bullets must be benefit-first not feature-first. No fake hype words.
Return ONLY JSON (no markdown, no backticks):
{"headline":"punchy product headline","descriptionA":{"label":"angle e.g. Benefit-Led","body":"description following length guide"},"descriptionB":{"label":"different angle e.g. Story-Led","body":"description different approach"},"bullets":["benefit point 1","benefit point 2","benefit point 3","benefit point 4"],"seo":{"title":"SEO title under 60 chars","meta":"meta description 150-160 chars","keywords":["kw1","kw2","kw3","kw4","kw5"]},"social":{"instagram":"caption with emojis and hashtags","facebook":"fb post","twitter":"tweet under 280 chars"},"tip":"one specific tip to improve this listing conversion rate"}`;
  }

  const categoryContext = {
    jobs: `This is a JOB POSTING. You are writing on behalf of a company recruiting talent.
GOAL: Attract the right candidates — be specific, credible, and compelling.
- Lead with the role and what makes it a great opportunity
- Highlight salary, growth, work style and company name prominently if provided
- Responsibilities should read as exciting challenges, not a task list
- Use action verbs: "Lead", "Build", "Drive", "Grow", "Manage"
- Visa sponsorship and remote/hybrid options are major selling points — make them stand out
- Target tone: professional but human — like a great recruiter talking to a candidate`,

    services: `This is a FREELANCER / SERVICE PROVIDER ad. The person is marketing themselves.
GOAL: Build trust and get the reader to reach out immediately.
- Lead with the skill/service and years of experience — this is the credibility hook
- Skills and past work details are social proof — highlight them
- Availability and rate reduce friction — include them clearly
- Free consultation and certifications are trust signals — use them as reasons to act now
- "Licensed" and "certified" carry weight — mention them in every format if provided
- Target tone: confident but approachable — like a pro who knows their worth but isn't arrogant`,
  }[categoryId] || "";

  const isJob     = categoryId === "jobs";
  const isService = categoryId === "services";

  const formatGuides = {
    facebook: isJob
      ? "2-3 short paragraphs. Open with the role + company. Second paragraph: key requirements and what makes this opportunity stand out (salary, growth, visa, remote). End with a clear apply/DM CTA. Professional but warm tone. Use 1-2 relevant emojis naturally."
      : isService
      ? "2-3 short paragraphs. Open with the skill and experience years. Second paragraph: what they do best and a credibility signal (clients, certifications, portfolio). End with a direct contact CTA. Use 1-2 relevant emojis naturally."
      : "2-3 short paragraphs. Hook first line. Conversational. End with clear call to action. Use 1-2 relevant emojis naturally — no emoji at the very start of the text.",
    google: isJob
      ? "Return exactly: Headline1 = Role name (max 30 chars) | Headline2 = Company or key perk (max 30 chars) | Headline3 = Location + work style (max 30 chars) | Description = top requirement + apply CTA (max 90 chars)."
      : isService
      ? "Return exactly: Headline1 = Service + years exp (max 30 chars) | Headline2 = Key skill or credential (max 30 chars) | Headline3 = Location + availability (max 30 chars) | Description = what they offer + contact CTA (max 90 chars)."
      : "Return exactly: Headline1 (max 30 chars) | Headline2 (max 30 chars) | Headline3 (max 30 chars) | Description (max 90 chars). Keyword-rich.",
    marketplace: isJob
      ? "Job title + company name as heading. Short intro sentence. Then a structured breakdown using this exact format:\n- Role Type: [value]\n- Location: [value]\n- Salary: [value]\n- Requirements: [3-4 bullet points]\n- What We Offer: [2-3 bullet points]\nEnd with how to apply."
      : isService
      ? "Service title as heading. Short intro (1 sentence). Then structured breakdown:\n- Services: [list]\n- Experience: [value]\n- Availability: [value]\n- Rate: [value]\n- Location: [value]\nEnd with contact instructions."
      : "Title as heading. Short intro paragraph (1-2 sentences). Then Key Features as a bullet list using dashes (- item). Then Price and Location. End with contact step.",
    whatsapp: isJob
      ? "Max 3 sentences. Role, company, one standout perk (salary/visa/remote). End: 'Interested? Drop us a message.' No emojis."
      : isService
      ? "Max 3 sentences. What they do, years of experience, one trust signal. End: 'Available now — send a message to get started.' No emojis."
      : "Max 3 sentences. Friendly. Key info only. End with a call to action. No emojis.",
    sms: "Max 160 characters total. Key selling point + one action. No emojis.",
    email: "Subject line + email body. Opening paragraph (1-2 sentences). Then key benefits or highlights as a bullet list (- item). Closing paragraph with clear CTA. No emojis in the body.",
  };

  const selectedFormats = formats.map(f => {
    const fmt = AD_FORMATS.find(af => af.id === f);
    return `"${f}": "write the ${fmt?.label} ad here following this guide: ${formatGuides[f]}"`;
  }).join(",\n  ");

  return `You are an expert advertising copywriter. Write ads for this listing.

Category: ${cat?.label}
Tone: ${tone}
${categoryContext ? `\n${categoryContext}\n` : ""}
LISTING DETAILS (use ONLY what is provided — never invent specs, prices, or features):
${fieldLines}${toggleLines}

RULES:
1. Only use provided information. Never invent details, salaries, or requirements.
2. Fields may be keywords or short notes — expand them naturally into proper copy.
3. If a detail is missing write around it naturally — never say "details not provided".
4. Match energy to category: Cars=excitement, Real estate=aspiration, Jobs=opportunity & credibility, Services=trust & expertise, Food=appetite.
5. Follow each format guide exactly for length and style.
6. Incorporate seller badges naturally — do not just list them, weave them into the copy.
7. Sound like a real person not a marketing department.
8. For Jobs: always name the company and role in the first line. For Services: always open with the skill and years of experience.

Return ONLY JSON (no markdown, no backticks):
{
  ${selectedFormats},
  "tip": "one specific actionable tip to make this listing get more responses"
}`;
}

const TONES_AD = ["Energetic", "Professional", "Friendly", "Luxury", "Urgent", "Casual"];
const TONES_EMAIL = ["Professional", "Friendly", "Formal", "Warm", "Direct", "Empathetic", "Confident"];

const TONE_PREVIEWS = {
  Professional: "Thank you for your message. I wanted to follow up on...",
  Friendly:     "Hey! Just checking in — wanted to make sure you got my last...",
  Formal:       "I am writing to formally address the matter of...",
  Warm:         "It was great connecting with you. I have been thinking about...",
  Direct:       "Quick update: the deadline is Friday. I need your approval by...",
  Empathetic:   "I completely understand this has been a frustrating situation...",
  Confident:    "Here is what I recommend, and here is why it will work for you...",
};

const EMAIL_LENGTHS = [
  { value: "short",    label: "Short",    hint: "2 paragraphs — quick & punchy" },
  { value: "standard", label: "Standard", hint: "3-4 paragraphs — balanced" },
  { value: "detailed", label: "Detailed", hint: "4-5 paragraphs — full context" },
];



function Toggle({ id, active, onToggle, t }) {
  const cfg = ALL_TOGGLES[id];
  return (
    <button onClick={() => onToggle(id)} style={{
      padding: "7px 12px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 400, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
      border: `1px solid ${active ? t.accent : t.border}`,
      background: active ? t.accentBg : t.card,
      color: active ? t.accentText : t.textMuted,
    }}>
      {cfg?.label}
    </button>
  );
}

const EMAIL_CATEGORIES = [
  { label: "Business", types: [
    { id: "invoice",      icon: "📄", label: "Invoice Follow-Up",         tags: ["payment", "chase", "overdue", "money", "bill", "unpaid", "collect", "debt"],
      fields: ["yourName", "businessName", "clientName", "invoiceNumber", "amount", "dueDate", "daysOverdue", "invoiceResolution", "recipientRelationship"] },
    { id: "proposal",     icon: "🤝", label: "Proposal Follow-Up",        tags: ["quote", "pitch", "offer", "no reply", "silence", "waiting", "sales"],
      fields: ["yourName", "businessName", "clientName", "proposalSubject", "sentDate", "proposalValue", "proposalConcerns", "recipientRelationship"] },
    { id: "cold",         icon: "🚀", label: "Cold Outreach",             tags: ["prospect", "new lead", "intro", "reach out", "sales", "pitch", "linkedin", "outbound"],
      fields: ["yourName", "businessName", "recipientName", "recipientCompany", "howYouFoundThem", "yourOffer", "desiredAction", "recipientRelationship"] },
    { id: "project",      icon: "📋", label: "Project Update",            tags: ["status", "progress", "update", "client", "milestone", "delivery", "timeline"],
      fields: ["yourName", "clientName", "projectName", "updateSummary", "nextSteps", "deadline", "recipientRelationship"] },
    { id: "meeting",      icon: "📅", label: "Meeting Request",           tags: ["schedule", "call", "zoom", "calendar", "book", "appointment", "discuss"],
      fields: ["yourName", "recipientName", "meetingPurpose", "suggestedDates", "duration", "meetingFormat", "recipientRelationship"] },
    { id: "announcement", icon: "📢", label: "Announcement",              tags: ["news", "launch", "update", "new", "change", "inform", "notify"],
      fields: ["yourName", "businessName", "audience", "announcementSubject", "keyDetails"] },
    { id: "badnews",      icon: "⚠️", label: "Bad News / Delay",          tags: ["delay", "problem", "issue", "late", "setback", "disappointed", "sorry"],
      fields: ["yourName", "recipientName", "projectOrOrder", "whatHappened", "solution", "newTimeline", "recipientRelationship"] },
    { id: "checkin",      icon: "🔁", label: "Check-In / Follow-Up",      tags: ["follow up", "nudge", "no reply", "reminder", "touching base", "ping"],
      fields: ["yourName", "recipientName", "context", "whatYouNeed", "recipientRelationship"] },
    { id: "recap",        icon: "🧾", label: "Meeting Recap",             tags: ["summary", "minutes", "action items", "decisions", "after call", "notes"],
      fields: ["yourName", "clientName", "meetingTopic", "attendees", "decisionsМade", "actionItems", "recipientRelationship"] },
    { id: "pricing",      icon: "💲", label: "Reply to Pricing Request",  tags: ["quote", "cost", "rate", "how much", "package", "offer", "pricing", "proposal"],
      fields: ["yourName", "businessName", "clientName", "whatYouOffer", "pricingStructure", "priceRange", "whatsIncluded", "nextStepCTA", "recipientRelationship"] },
    { id: "approval",     icon: "✅", label: "Request Approval",          tags: ["sign off", "approve", "green light", "permission", "review", "authorize", "feedback"],
      fields: ["yourName", "recipientName", "whatNeedsApproval", "approvalDeadline", "delayImpact", "recipientRelationship"] },
    { id: "objection",    icon: "🛡️", label: "Objection Reply",           tags: ["too expensive", "not interested", "competitor", "budget", "pushback", "handle", "negotiate"],
      fields: ["yourName", "recipientName", "objectionType", "objectionContext", "offerFlexibility", "recipientRelationship"] },
    { id: "support",      icon: "🎫", label: "Support Acknowledgment",    tags: ["ticket", "complaint", "issue", "customer", "help", "problem", "acknowledge"],
      fields: ["yourName", "businessName", "clientName", "issuesummary", "ticketNumber", "expectedResponse"] },
    { id: "refund",       icon: "💸", label: "Refund Response",           tags: ["refund", "money back", "return", "cancel", "dispute", "chargeback", "compensation"],
      fields: ["yourName", "businessName", "clientName", "refundReason", "refundStance", "refundTimeline", "recipientRelationship"] },
  ]},
  { label: "Relationship", types: [
    { id: "apology",  icon: "😬", label: "Apology Email",       tags: ["sorry", "mistake", "error", "mess up", "wrong", "fix"],
      fields: ["yourName", "recipientName", "whatWentWrong", "impact", "remedy", "recipientRelationship"] },
    { id: "thankyou", icon: "🎉", label: "Thank You Email",      tags: ["thanks", "grateful", "appreciate", "gratitude"],
      fields: ["yourName", "recipientName", "whatFor", "specificDetail", "recipientRelationship"] },
    { id: "favor",    icon: "🙏", label: "Asking a Favor",       tags: ["ask", "request", "help", "need something", "introduction"],
      fields: ["yourName", "recipientName", "favorDetails", "whyThem", "deadline", "recipientRelationship"] },
    { id: "intro",    icon: "👋", label: "Introduction Email",   tags: ["introduce", "hello", "first contact", "networking", "connect", "meet"],
      fields: ["yourName", "recipientName", "howYouKnowThem", "yourBackground", "purpose", "recipientRelationship"] },
  ]},
  { label: "Personal", types: [
    { id: "jobapp",  icon: "💼", label: "Job Application",       tags: ["job", "apply", "cv", "resume", "hire", "position", "career"],
      fields: ["yourName", "role", "company", "keyStrength", "whyThisRole", "attachmentNote"] },
    { id: "ooo",     icon: "🏖️", label: "Out of Office",         tags: ["vacation", "away", "holiday", "absence", "leave", "unavailable"],
      fields: ["yourName", "startDate", "endDate", "backDate", "contactName", "contactEmail"] },
    { id: "clarify", icon: "🤔", label: "Clarification Request", fields: ["yourName", "recipientName", "topic", "whatIsUnclear", "whyItMatters", "recipientRelationship"],
      tags: ["confused", "unclear", "question", "confirm", "clarify", "misunderstanding"] },
  ]},
  { label: "New", types: [
    { id: "reply",    icon: "↩️", label: "Reply to Email",       tags: ["reply", "respond", "answer", "email back", "received", "got an email"],
      fields: ["yourName", "recipientName", "receivedEmail", "replyIntent", "replyTone", "recipientRelationship"] },
    { id: "reminder", icon: "⏰", label: "Reminder Email",       tags: ["reminder", "nudge", "due", "upcoming", "don't forget", "deadline", "schedule"],
      fields: ["yourName", "recipientName", "reminderSubject", "reminderDeadline", "reminderContext", "urgencyLevel", "recipientRelationship"] },
  ]},
];

const ALL_EMAIL_TYPES = EMAIL_CATEGORIES.flatMap(c => c.types);

// ── CONDITIONAL FIELD LOGIC ────────────────────────────────────────────────
function getConditionalFields(typeId, fields) {
  const extras = [];
  if (typeId === "meeting") {
    const fmt = fields.meetingFormat;
    if (fmt === "Zoom")            extras.push("zoomLink");
    if (fmt === "Google Meet")     extras.push("meetLink");
    if (fmt === "Microsoft Teams") extras.push("teamsLink");
    if (fmt === "Phone Call")      { extras.push("phoneNumber"); extras.push("whoCallsWho"); }
    if (["In Person", "Your Office", "My Office"].includes(fmt)) extras.push("meetingAddress");
    extras.push("meetingNotes");
  }
  if (typeId === "invoice") {
    if (["30–60 days", "60+ days"].includes(fields.daysOverdue)) extras.push("invoiceEscalation");
  }
  if (typeId === "cold") {
    if (fields.howYouFoundThem === "Referral") extras.push("referralName");
  }
  return extras;
}

const EMAIL_FIELD_CONFIG = {
  // Identity
  yourName:           { label: "Your Name",                  placeholder: "e.g. Sarah Ahmed" },
  businessName:       { label: "Your Business",              placeholder: "e.g. Nova Studio" },
  clientName:         { label: "Client Name",                placeholder: "e.g. Jordan Media Ltd." },
  recipientName:      { label: "Recipient Name",             placeholder: "e.g. John Smith" },
  recipientCompany:   { label: "Their Company",              placeholder: "e.g. Acme Corp" },
  recipientRelationship: { label: "Recipient Relationship",  pillOptions: ["New Prospect", "Existing Client", "Partner", "Internal Colleague", "Vendor / Supplier"] },
  // Invoice
  invoiceNumber:      { label: "Invoice #",                  placeholder: "e.g. INV-2025-047" },
  amount:             { label: "Amount Due",                 placeholder: "e.g. AED 4,500" },
  dueDate:            { label: "Due Date",                   placeholder: "e.g. Feb 1, 2025" },
  daysOverdue:        { label: "Days Overdue",               pillOptions: ["1–7 days", "8–14 days", "15–30 days", "30–60 days", "60+ days"] },
  invoiceResolution:  { label: "Preferred Resolution",       pillOptions: ["Full payment", "Partial payment", "Payment plan", "Just a reminder"] },
  invoiceEscalation:  { label: "Escalation Tone",            pillOptions: ["Still polite", "Firm but professional", "Final warning before legal"] },
  // Proposal
  proposalSubject:    { label: "Proposal Subject",           placeholder: "e.g. Website Redesign Package" },
  sentDate:           { label: "Sent Date",                  placeholder: "e.g. Jan 20, 2025" },
  proposalValue:      { label: "Proposal Value",             placeholder: "e.g. AED 18,000" },
  proposalConcerns:   { label: "Concerns to Address?",       placeholder: "e.g. Price might be too high, timeline concerns", textarea: true },
  // Cold outreach
  yourOffer:          { label: "Your Offer / Service",       placeholder: "e.g. Social media management for e-commerce brands", textarea: true },
  desiredAction:      { label: "What You Want",              placeholder: "e.g. A 15-min discovery call" },
  howYouFoundThem:    { label: "How You Found Them",         pillOptions: ["LinkedIn", "Referral", "Their website", "Social media", "Event / Conference", "Cold search"] },
  referralName:       { label: "Who Referred You?",          placeholder: "e.g. Ahmed Al Rashidi" },
  // Project
  projectName:        { label: "Project Name",               placeholder: "e.g. Brand Identity Refresh" },
  updateSummary:      { label: "Update Summary",             placeholder: "e.g. Logo drafts complete, working on color palette", textarea: true },
  nextSteps:          { label: "Next Steps",                 placeholder: "e.g. Client review needed by Friday" },
  deadline:           { label: "Deadline",                   placeholder: "e.g. March 15, 2025" },
  // Meeting
  meetingPurpose:     { label: "Meeting Purpose",            placeholder: "e.g. Discuss Q2 marketing strategy" },
  suggestedDates:     { label: "Suggested Dates",            placeholder: "e.g. Mon–Wed next week, mornings preferred" },
  duration:           { label: "Duration",                   pillOptions: ["15 min", "30 min", "45 min", "1 hour", "1.5 hours", "2 hours", "Half day"] },
  meetingFormat:      { label: "Format",                     pillOptions: ["Zoom", "Google Meet", "Microsoft Teams", "Phone Call", "In Person", "Your Office", "My Office"] },
  // Conditional meeting
  zoomLink:           { label: "Zoom Link (optional)",       placeholder: "e.g. https://zoom.us/j/123456" },
  meetLink:           { label: "Google Meet Link (optional)",placeholder: "e.g. https://meet.google.com/abc-def" },
  teamsLink:          { label: "Teams Link (optional)",      placeholder: "e.g. https://teams.microsoft.com/..." },
  phoneNumber:        { label: "Phone Number",               placeholder: "e.g. +971 50 123 4567" },
  whoCallsWho:        { label: "Who Calls Who?",             pillOptions: ["I will call them", "They should call me", "Either works"] },
  meetingAddress:     { label: "Address / Location",         placeholder: "e.g. Office Tower B, Dubai Marina, 3rd floor" },
  meetingNotes:       { label: "Additional Notes (optional)",placeholder: "e.g. Agenda items, prep needed, parking info", textarea: true },
  // Announcement
  audience:           { label: "Audience",                   placeholder: "e.g. All clients, team members" },
  announcementSubject:{ label: "What Are You Announcing?",   placeholder: "e.g. New service launch, office move" },
  keyDetails:         { label: "Key Details",                placeholder: "e.g. Launching March 1, 20% intro discount", textarea: true },
  // Bad news
  projectOrOrder:     { label: "Project / Order",            placeholder: "e.g. Website project, Order #1234" },
  whatHappened:       { label: "What Went Wrong",            placeholder: "e.g. Delivery delayed due to supplier issue" },
  solution:           { label: "Your Solution",              placeholder: "e.g. Expediting shipment, 10% discount offered" },
  newTimeline:        { label: "New Timeline",               placeholder: "e.g. Delivered by Feb 28" },
  // Check-in
  context:            { label: "Context / Background",       placeholder: "e.g. We spoke last week about the proposal" },
  whatYouNeed:        { label: "What Do You Need?",          placeholder: "e.g. Feedback on the proposal, a quick call" },
  // Personal
  whatWentWrong:      { label: "What Went Wrong",            placeholder: "e.g. Missed the deadline, sent wrong file" },
  impact:             { label: "Impact on Them",             placeholder: "e.g. Caused a delay in their launch" },
  remedy:             { label: "How You're Fixing It",       placeholder: "e.g. Delivering by tomorrow, offering a revision" },
  whatFor:            { label: "What Are You Thanking For?", placeholder: "e.g. The referral, their patience" },
  specificDetail:     { label: "Specific Detail",            placeholder: "e.g. The intro to Ahmed really opened doors" },
  favorDetails:       { label: "The Favor",                  placeholder: "e.g. Review my portfolio and give feedback", textarea: true },
  whyThem:            { label: "Why Are You Asking Them?",   placeholder: "e.g. Your opinion matters most in this field" },
  howYouKnowThem:     { label: "How Do You Know Them?",      placeholder: "e.g. Met at Dubai Expo, mutual connection via Ahmed" },
  yourBackground:     { label: "Your Background",            placeholder: "e.g. Designer with 5 years in brand identity" },
  purpose:            { label: "Purpose of Intro",           placeholder: "e.g. Explore collaboration on upcoming projects" },
  // Career
  role:               { label: "Role / Position",            placeholder: "e.g. Senior UX Designer" },
  company:            { label: "Company",                    placeholder: "e.g. Google" },
  keyStrength:        { label: "Your Key Strength",          placeholder: "e.g. 3 years React, shipped 10 products", textarea: true },
  whyThisRole:        { label: "Why This Role?",             placeholder: "e.g. Passionate about fintech, love their product", textarea: true },
  attachmentNote:     { label: "Attachments",                pillOptions: ["CV attached", "Portfolio link", "Both", "None"] },
  startDate:          { label: "Out From",                   placeholder: "e.g. Feb 20" },
  endDate:            { label: "Out Until",                  placeholder: "e.g. Feb 28" },
  backDate:           { label: "Back On",                    placeholder: "e.g. March 1" },
  contactName:        { label: "Contact Person",             placeholder: "e.g. Ahmed Hassan" },
  contactEmail:       { label: "Their Email",                placeholder: "e.g. ahmed@company.com" },
  topic:              { label: "Topic / Subject",            placeholder: "e.g. The new payment terms in the contract" },
  whatIsUnclear:      { label: "What's Unclear?",            placeholder: "e.g. Whether the 30 days starts from invoice or delivery" },
  whyItMatters:       { label: "Why It Matters",             placeholder: "e.g. Need to plan cash flow accordingly" },
  // Meeting Recap
  meetingTopic:       { label: "Meeting Topic",              placeholder: "e.g. Q2 Campaign Strategy" },
  attendees:          { label: "Attendees (optional)",       placeholder: "e.g. Sarah, John, Reem" },
  decisionsМade:      { label: "Decisions Made",             placeholder: "e.g. Budget approved at AED 20k, launch date set to March 15", textarea: true },
  actionItems:        { label: "Action Items",               placeholder: "e.g. Sarah: send brief by Friday. John: confirm venue by Monday.", textarea: true },
  // Pricing reply
  whatYouOffer:       { label: "What You're Offering",       placeholder: "e.g. Monthly social media management", textarea: true },
  pricingStructure:   { label: "Pricing Structure",          pillOptions: ["Fixed price", "Monthly retainer", "Hourly rate", "Project-based", "Packages"] },
  priceRange:         { label: "Price / Starting From",      placeholder: "e.g. AED 3,500/month or from AED 8,000" },
  whatsIncluded:      { label: "What's Included",            placeholder: "e.g. 12 posts/month, stories, monthly report", textarea: true },
  nextStepCTA:        { label: "Next Step / CTA",            placeholder: "e.g. Book a call, send full proposal, visit link" },
  // Approval request
  whatNeedsApproval:  { label: "What Needs Approval",        placeholder: "e.g. Final design mockups for the homepage" },
  approvalDeadline:   { label: "Deadline for Approval",      placeholder: "e.g. By Thursday EOD" },
  delayImpact:        { label: "Impact if Delayed (optional)",placeholder: "e.g. Will push the launch by one week" },
  // Objection reply
  objectionType:      { label: "Their Objection",            pillOptions: ["Too expensive", "Bad timing", "Using a competitor", "Need approval", "Not a priority"] },
  objectionContext:   { label: "One-Line Context",           placeholder: "e.g. They said budget is tight this quarter" },
  offerFlexibility:   { label: "Can You Be Flexible?",       pillOptions: ["Yes — discount available", "Yes — smaller plan", "Yes — trial offer", "No — holding firm"] },
  // Support acknowledgment
  issuesummary:       { label: "Issue Summary",              placeholder: "e.g. Login not working after password reset" },
  ticketNumber:       { label: "Ticket / Reference #",       placeholder: "e.g. #TKT-2025-441" },
  expectedResponse:   { label: "Expected Response Time",     pillOptions: ["Within a few hours", "Within 24 hours", "1-2 business days", "3-5 business days"] },
  // Refund response
  refundReason:       { label: "Reason for Refund Request",  placeholder: "e.g. Product arrived damaged, service not as described" },
  refundStance:       { label: "Your Decision",              pillOptions: ["Full refund approved", "Partial refund", "Refund declined", "Investigating first"] },
  refundTimeline:     { label: "Processing Time",            placeholder: "e.g. 3-5 business days" },
  // Reply email
  receivedEmail:      { label: "Paste the Email You Received", placeholder: "Paste the email content here so the AI can write a relevant reply...", textarea: true },
  replyIntent:        { label: "What's Your Reply?",           placeholder: "e.g. Agree to the terms, decline politely, ask for more time", textarea: true },
  replyTone:          { label: "Reply Stance",                 pillOptions: ["Agree & confirm", "Decline politely", "Ask for more info", "Negotiate terms", "Express interest", "Push back"] },
  // Reminder email
  reminderSubject:    { label: "What's the Reminder About?",   placeholder: "e.g. Pending invoice, overdue report, upcoming meeting" },
  reminderDeadline:   { label: "Due Date / Deadline",          placeholder: "e.g. This Friday, March 15, End of week" },
  reminderContext:    { label: "Context (optional)",           placeholder: "e.g. We agreed on this in our last call, third reminder" },
  urgencyLevel:       { label: "Urgency",                      pillOptions: ["Gentle reminder", "Moderate urgency", "Urgent — action needed today"] },
};

// ── EMAIL PROMPT ───────────────────────────────────────────────────────────
function buildEmailPrompt(typeId, fields, tone) {
  const typeLabel = ALL_EMAIL_TYPES.find(t => t.id === typeId)?.label || typeId;
  const fieldLines = Object.entries(fields).filter(([k, v]) => v && k !== "type").map(([k, v]) => `${EMAIL_FIELD_CONFIG[k]?.label || k}: ${v}`).join("\n");
  const typeGuides = {
    invoice: { length: "3-4 short paragraphs.", personality: "Polite but clear. Do not beg." },
    proposal: { length: "3-4 short paragraphs.", personality: "Confident. Remind value briefly." },
    cold: { length: "3 short paragraphs MAX.", personality: "Lead with their world. One clear ask. No buzzwords." },
    project: { length: "4-5 short paragraphs.", personality: "State done, next, what you need. Be specific." },
    meeting: { length: "2-3 short paragraphs.", personality: "State purpose. Suggest times. Easy to say yes." },
    announcement: { length: "3-4 paragraphs.", personality: "Lead with news. What it means for them. Next steps." },
    badnews: { length: "3-4 paragraphs.", personality: "Don't bury news. Acknowledge impact. Lead with solution." },
    checkin: { length: "2-3 short paragraphs.", personality: "Light and easy. One clear ask." },
    apology: { length: "3-4 short paragraphs.", personality: "Own it fully. No excuses. Focus on fixing." },
    thankyou: { length: "2-3 short paragraphs.", personality: "Specific about what you're thankful for." },
    favor: { length: "3 short paragraphs.", personality: "Clear about what you need. Easy to say no." },
    intro: { length: "3 short paragraphs.", personality: "Connection, who you are briefly, purpose." },
    jobapp: { length: "3-4 short paragraphs MAX.", personality: "Lead with passion and fit. Specific. No fluff." },
    ooo: { length: "2-3 sentences only.", personality: "State dates, contact. Nothing more." },
    clarify: { length: "2-3 short paragraphs.", personality: "State what's unclear and why it matters." },
    recap:    { length: "structured recap format.", personality: "Clear. Factual. Bullet action items. No fluff." },
    pricing:  { length: "3-4 paragraphs.", personality: "Confident about value. Clear about what's included. End with a strong CTA." },
    approval: { length: "2-3 short paragraphs.", personality: "State the ask clearly upfront. Make it easy to say yes." },
    objection:{ length: "3-4 paragraphs.", personality: "Calm and confident. Acknowledge their concern. Reframe value. One clear next step." },
    support:  { length: "2-3 short paragraphs.", personality: "Empathetic. Reassuring. Give them a timeline. Build trust." },
    refund:   { length: "3-4 paragraphs.", personality: "Professional and calm. Be clear about the decision. No over-apologizing." },
    reply:    { length: "2-4 paragraphs.", personality: "Match the tone of the received email. Be direct and human. Don't over-explain." },
    reminder: { length: "2-3 short paragraphs.", personality: "Polite but purposeful. State what's needed and by when. Never passive-aggressive." },
  };
  const guide = typeGuides[typeId] || { length: "3-4 short paragraphs.", personality: "Clear and human." };
  const relationship = fields.recipientRelationship;
  const relationshipNote = relationship ? `\nRecipient Relationship: ${relationship} — adjust formality, warmth, and assumptions accordingly.` : "";
  const emailLength = fields.emailLength || "standard";
  const lengthOverride = emailLength === "short" ? "Keep it SHORT — 2 short paragraphs max. Every sentence must earn its place." : emailLength === "detailed" ? "DETAILED — 4-5 paragraphs, include more context and reasoning." : guide.length;

  const versionInstructions = {
    invoice:    { A: "Lead with invoice number and amount owed in the first sentence. Factual, firm, specific.", B: "Open by assuming good faith — maybe it slipped. Warmer tone, same clear ask at the end." },
    proposal:   { A: "Reference the specific proposal value and deliverables. Confident close around ROI.", B: "Focus on their outcome — what changes for their business if they move forward. Softer, curiosity-driven close." },
    cold:       { A: "Lead with a sharp specific insight about their business. Offer. One clear ask.", B: "Start with a genuine connection point or compliment. Build rapport before the ask." },
    project:    { A: "Structure: Done / In Progress / Blocked / Next steps. Bullet-style clarity.", B: "Narrative style — tell the project story. What is going well, what needs attention, what comes next." },
    meeting:    { A: "State the meeting purpose in the first sentence. Offer 2-3 specific time slots. Short.", B: "Start with why this meeting matters for them specifically. Then suggest times. Warmer invite." },
    badnews:    { A: "Lead with the news in the first sentence. No softening. Then solution. Then new timeline.", B: "Acknowledge the impact on them first. Then what happened. Then solution. More empathetic." },
    pricing:    { A: "Lead with the price and exactly what is included. Structured, clear, strong CTA.", B: "Lead with the outcome and transformation they get. Price comes second. Value-first framing." },
    objection:  { A: "Name the objection directly. Reframe with data or specifics. Hold firm on value.", B: "Validate their concern fully before reframing. More empathetic, less defensive energy." },
    apology:    { A: "Own it in the first sentence. What happened, what you are doing, new timeline. Direct.", B: "Lead with the impact on them — show you understand what it cost them. Then the remedy." },
    refund:     { A: "State the decision in the first sentence. Brief reasoning. Clear timeline.", B: "Acknowledge their frustration first. Then the decision. Softer framing of the same outcome." },
    reply:      { A: "Answer every point raised in their email directly and in order. Structured and clear.", B: "Start by acknowledging the overall message before addressing specific points. More conversational." },
    reminder:   { A: "State the reminder subject and deadline in the first sentence. No preamble.", B: "Brief friendly opener acknowledging they are busy, then the reminder. Lighter touch." },
    checkin:    { A: "Reference the specific context immediately. One clear ask. Short.", B: "Warmer opener that shows you have been thinking about them. Then the ask." },
    announcement:{ A: "Lead with the news headline. Then what it means for them. Then next steps.", B: "Build a little anticipation before the reveal. More story-like announcement." },
    support:    { A: "Confirm you received their issue with the ticket number. Timeline. What happens next.", B: "Lead with empathy for their frustration before the logistics. More human acknowledgment." },
  };
  const vA = versionInstructions[typeId]?.A || "Factual, structured, lead with the key point immediately.";
  const vB = versionInstructions[typeId]?.B || "Warmer, relationship-first, same substance but more human framing.";

  return `You are a business email writer. Write a ${typeLabel} email.
PROVIDED INFORMATION (use ONLY this — never invent details):
${fieldLines}${relationshipNote}
Tone: ${tone} | Length: ${lengthOverride} | Personality: ${guide.personality}

RULES:
1. Use ONLY the provided facts. Never invent names, numbers, dates, or details not given.
2. Never open with: "I hope this finds you well", "I wanted to reach out", "I am writing to", or any filler.
3. Write like a real human professional — not a template, not a robot.
4. If a field looks like a keyword, expand it naturally into a real sentence.
5. Subject lines must use real names and numbers from the inputs — never generic placeholders.
6. The two versions must feel STRUCTURALLY different — not just different adjectives on the same email.
   Version A: ${vA}
   Version B: ${vB}
7. WhatsApp: use first name only, no formal sign-off, natural contractions — how a real person texts a contact, not how they email a client.
8. Follow-up (if included): must NOT repeat or summarize the original. Reference it in one sentence, then bring a new angle, a different question, or a next step not mentioned before.

Return ONLY valid JSON (no markdown, no backticks, no trailing commas):
{
  "subjects": ["subject with real names/numbers", "softer angle", "outcome-focused"],
  "versionA": {"label": "3-word label e.g. Direct & Firm", "body": "email body — \n\n between paragraphs, \n before sign-off name"},
  "whatsapp": "1-2 sentence WhatsApp — casual, first name only, no sign-off",
  "tip": "one specific actionable tip for this exact situation"
}`}

// ── Email prompt part 2: Version B + Follow-up (called lazily) ─────────────
function buildEmailPromptB(typeId, fields, tone, versionABody) {
  const typeLabel  = ALL_EMAIL_TYPES.find(t => t.id === typeId)?.label || typeId;
  const fieldLines = Object.entries(fields)
    .filter(([k, v]) => v && k !== "type" && k !== "emailLength")
    .map(([k, v]) => `${EMAIL_FIELD_CONFIG[k]?.label || k}: ${v}`)
    .join("\n");
  const typeGuides = {
    invoice: "Polite but clear.", proposal: "Confident. Remind value.", cold: "Lead with their world. One ask.",
    project: "State done, next, what you need.", meeting: "State purpose. Easy to say yes.",
    announcement: "Lead with news. What it means for them.", badnews: "Acknowledge impact. Lead with solution.",
    checkin: "Light and easy. One clear ask.", apology: "Own it fully. Focus on fixing.",
    thankyou: "Specific about what you're thankful for.", favor: "Clear ask. Easy to say no.",
    intro: "Connection, who you are, purpose.", jobapp: "Lead with passion and fit.",
    ooo: "State dates, contact. Nothing more.", clarify: "State what's unclear and why.",
    recap: "Clear. Factual. No fluff.", pricing: "Confident about value. Strong CTA.",
    approval: "State the ask clearly. Easy to say yes.", objection: "Calm. Acknowledge. Reframe.",
    support: "Empathetic. Give a timeline.", refund: "Professional. Clear decision.",
    reply: "Direct. Match their tone.", reminder: "Polite but purposeful.",
  };
  const personality = typeGuides[typeId] || "Clear and human.";
  const versionInstructions = {
    invoice: "Open by assuming good faith — maybe it slipped. Warmer tone, same clear ask.",
    proposal: "Focus on their outcome — what changes for their business if they move forward.",
    cold: "Start with a genuine connection point or compliment. Build rapport before the ask.",
    project: "Narrative style — tell the project story. What is going well, what comes next.",
    meeting: "Start with why this meeting matters for them. Then suggest times. Warmer.",
    badnews: "Acknowledge the impact on them first. Then what happened. Then solution.",
    pricing: "Lead with the outcome they get. Price comes second. Value-first framing.",
    objection: "Validate their concern fully before reframing. More empathetic, less defensive.",
    apology: "Lead with the impact on them — show you understand what it cost them.",
    refund: "Acknowledge their frustration first. Then the decision. Softer framing.",
    reply: "Start by acknowledging the overall message before addressing specific points.",
    reminder: "Brief friendly opener acknowledging they are busy, then the reminder.",
    checkin: "Warmer opener that shows you have been thinking about them. Then the ask.",
    announcement: "Build anticipation before the reveal. More story-like.",
    support: "Lead with empathy for their frustration before the logistics.",
  };
  const vB = versionInstructions[typeId] || "Warmer, relationship-first, same substance but more human framing.";
  const needsFollowUp = !["ooo", "thankyou", "announcement", "support", "reminder", "reply"].includes(typeId);
  const emailLength = fields.emailLength || "standard";
  const lengthNote  = emailLength === "short" ? "SHORT — 2 paragraphs max." : emailLength === "detailed" ? "DETAILED — 4-5 paragraphs." : "3-4 short paragraphs.";

  return `You are a business email writer. Write an alternative Version B of a ${typeLabel} email.
PROVIDED INFORMATION (use ONLY this — never invent details):
${fieldLines}
Tone: ${tone} | Length: ${lengthNote} | Personality: ${personality}

Version A summary (DO NOT repeat — write a structurally DIFFERENT approach):
${versionABody ? versionABody.slice(0, 250) + "..." : ""}

Version B must: ${vB}
Rules: No filler openers. Human professional. Same facts, different structure and angle.

Return ONLY valid JSON:
{
  "versionB": {"label": "3-word label", "body": "email body — \\n\\n between paragraphs, \\n before sign-off name"}${needsFollowUp ? `,
  "followUp": "2-3 paragraph follow-up — \\n\\n between paragraphs — new angle, not a repeat"` : ""}
}`}

// ── THEME ──────────────────────────────────────────────────────────────────
function getTheme(dark) {
  return dark ? {
    bg: "#0c0a09", surface: "#111110", card: "#1c1917", border: "#292524", border2: "#44403c",
    text: "#fafaf9", textMuted: "#a8a29e", textDim: "#57534e",
    accent: "#d97706", accentBg: "#1c1407", accentText: "#fbbf24",
    success: "#065f46", successText: "#6ee7b7", danger: "#7f1d1d", dangerText: "#fca5a5",
    pillBg: "#292524", pillText: "#a8a29e",
  } : {
    bg: "#fafaf9", surface: "#f5f5f4", card: "#ffffff", border: "#e7e5e4", border2: "#d6d3d1",
    text: "#1c1917", textMuted: "#57534e", textDim: "#a8a29e",
    accent: "#d97706", accentBg: "#fef3c7", accentText: "#92400e",
    success: "#d1fae5", successText: "#065f46", danger: "#fee2e2", dangerText: "#991b1b",
    pillBg: "#e7e5e4", pillText: "#78716c",
  };
}

// ── UI COMPONENTS ──────────────────────────────────────────────────────────
function CopyBtn({ text, label = "Copy", t }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text || ""); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
      style={{ background: copied ? t.success : t.card, color: copied ? t.successText : t.textMuted, border: `1px solid ${copied ? t.success : t.border}`, borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit" }}>
      {copied ? "✓ Copied" : label}
    </button>
  );
}

function Field({ label, children, t }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.textDim, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

function getInp(t) {
  return { width: "100%", background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, padding: "10px 14px", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
}

function TabToggle({ options, value, onChange, t }) {
  return (
    <div style={{ display: "flex", background: t.card, borderRadius: 8, padding: 3, border: `1px solid ${t.border}`, marginBottom: 16, flexWrap: "wrap", gap: 2 }}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)} style={{ flex: 1, minWidth: 80, padding: "8px 6px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, background: value === o.value ? t.surface : "transparent", color: value === o.value ? t.text : t.textDim, transition: "all 0.2s", fontFamily: "inherit", whiteSpace: "nowrap" }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── GENERATION LIMIT ──────────────────────────────────────────────────────
const ANON_LIMIT  = 10; // lifetime
const FREE_LIMIT  = 3;  // per day
const LIMIT_KEY = "limji_usage";

const usage = {
  get: () => {
    try {
      const v = JSON.parse(localStorage.getItem(LIMIT_KEY) || "{}");
      const today = new Date().toDateString();
      if (v.date !== today) return { date: today, count: 0 };
      return v;
    } catch { return { date: new Date().toDateString(), count: 0 }; }
  },
  increment: () => {
    const v = usage.get();
    const next = { ...v, count: v.count + 1 };
    try { localStorage.setItem(LIMIT_KEY, JSON.stringify(next)); } catch {}
    return next;
  },
  remaining: () => Math.max(0, FREE_LIMIT - usage.get().count),
};

// ── WELCOME MODAL ─────────────────────────────────────────────────────────
function WelcomeModal({ onClose, t }) {
  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 300, backdropFilter: "blur(4px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 301, width: "min(560px, 92vw)", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, overflow: "hidden", boxShadow: "0 24px 64px rgba(0,0,0,0.5)" }}>
        {/* Amber header band */}
        <div style={{ background: t.accent, padding: "28px 32px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 36, height: 36, background: "rgba(0,0,0,0.15)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✦</div>
            <span style={{ fontFamily: "'Instrument Serif', serif", fontSize: 24, color: "#0c0a09", letterSpacing: "-0.02em" }}>Welcome to Limji</span>
          </div>
          <p style={{ color: "#3d2e00", fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            Your AI writing toolkit for small businesses. Tell it what you need — it writes the copy in seconds.
          </p>
        </div>

        {/* Tools explanation */}
        <div style={{ padding: "24px 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 24 }}>
            <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 20, marginBottom: 8 }}>📢</div>
              <div style={{ fontWeight: 700, fontSize: 12, color: t.text, marginBottom: 4 }}>Smart Ads</div>
              <div style={{ fontSize: 11, color: t.textDim, lineHeight: 1.6 }}>Selling a car, phone, or anything else? Get ready-to-post ads for Facebook, WhatsApp, and more.</div>
            </div>
            <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ fontSize: 20, marginBottom: 8 }}>✉️</div>
              <div style={{ fontWeight: 700, fontSize: 12, color: t.text, marginBottom: 4 }}>Smart Email</div>
              <div style={{ fontSize: 11, color: t.textDim, lineHeight: 1.6 }}>Invoices, proposals, cold outreach? Two polished versions with subject lines — ready to send.</div>
            </div>
            <div style={{ background: t.card, border: `1px solid ${t.accent}30`, borderRadius: 12, padding: 14, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 8, right: 8, background: t.accent, borderRadius: 6, padding: "1px 6px", fontSize: 9, fontWeight: 800, color: "#0c0a09" }}>NEW</div>
              <div style={{ fontSize: 20, marginBottom: 8 }}>📦</div>
              <div style={{ fontWeight: 700, fontSize: 12, color: t.text, marginBottom: 4 }}>Smart Orders</div>
              <div style={{ fontSize: 11, color: t.textDim, lineHeight: 1.6 }}>Track customer orders, update status, and send WhatsApp messages — all in one place.</div>
            </div>
          </div>

          {/* Free limit notice */}
          <div style={{ background: t.accentBg, border: `1px solid ${t.accent}`, borderRadius: 10, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>🎁</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.accentText }}>10 free tries — no sign-up needed</div>
              <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>After that: free account gets 3/day · Pro gets 150/day</div>
            </div>
          </div>

          <button onClick={onClose} style={{ width: "100%", padding: "13px 0", background: t.accent, color: "#0c0a09", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em" }}>
            ✦ Let's get started
          </button>
        </div>
      </div>
    </>
  );
}

// ── LIMIT REACHED MODAL ───────────────────────────────────────────────────
function SignInModal({ onClose, t, remaining, anonLimit }) {
  const triesLeft = remaining ?? anonLimit;
  const outOfTries = triesLeft <= 0;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 300, backdropFilter: "blur(4px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 301, width: "min(400px, 92vw)", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 32, boxShadow: "0 24px 64px rgba(0,0,0,0.5)", textAlign: "center" }}>

        {/* Icon */}
        <div style={{ width: 52, height: 52, borderRadius: 14, background: t.accentBg, border: `1px solid ${t.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, margin: "0 auto 16px" }}>✦</div>

        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, color: t.text, marginBottom: 8, fontWeight: 800 }}>
          This is a Pro feature
        </div>
        <p style={{ color: t.textDim, fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
          {outOfTries
            ? <>You've used all your free tries.<br />Create a free account to keep going — <strong style={{ color: t.text }}>3 generations/day</strong>, no credit card needed.</>
            : <>You still have <strong style={{ color: t.text }}>{triesLeft} free {triesLeft === 1 ? "try" : "tries"}</strong> remaining.<br />Sign up free to unlock this feature and more.</>
          }
        </p>

        {/* Feature list */}
        <div style={{ background: t.accentBg, border: `1px solid ${t.accent}55`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left", fontSize: 12, color: t.textDim, lineHeight: 1.9 }}>
          <div style={{ fontWeight: 700, color: t.accentText, marginBottom: 4, fontSize: 11, letterSpacing: "0.06em" }}>✦ PRO INCLUDES</div>
          ✓ Version B — alternative email approach<br />
          ✓ WhatsApp message ready to send<br />
          ✓ 48h follow-up email<br />
          ✓ 150 generations/day<br />
          ✓ History saved across devices
        </div>

        <a href="/sign-up" style={{ display: "block", width: "100%", padding: "13px 0", background: t.accent, color: "#0c0a09", borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginBottom: 8, textDecoration: "none", boxSizing: "border-box" }}>
          ✦ Create free account
        </a>
        <a href="/sign-in" style={{ display: "block", width: "100%", padding: "11px 0", background: "transparent", color: t.textDim, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: "inherit", textDecoration: "none", boxSizing: "border-box", marginBottom: 10 }}>
          Sign in to existing account
        </a>
        <button onClick={onClose} style={{ background: "none", border: "none", color: t.textMuted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Maybe later
        </button>
      </div>
    </>
  );
}

function PlanModal({ onClose, t, onUpgrade }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 300, backdropFilter: "blur(4px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 301, width: "min(400px, 92vw)", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 32, boxShadow: "0 24px 64px rgba(0,0,0,0.5)", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>✦</div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, color: t.text, marginBottom: 8, fontWeight: 800 }}>Upgrade to Pro</div>
        <p style={{ color: t.textDim, fontSize: 13, lineHeight: 1.7, marginBottom: 24 }}>150 generations/day · All tools · Priority support</p>
        <button onClick={() => onUpgrade("monthly")} style={{ width: "100%", padding: "13px 0", background: t.accent, color: "#0c0a09", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
          ✦ Monthly — $19/mo
        </button>
        <button onClick={() => onUpgrade("yearly")} style={{ width: "100%", padding: "13px 0", background: "transparent", color: t.accentText, border: `1px solid ${t.accent}`, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 16 }}>
          ✦ Annual — $149/yr <span style={{ fontSize: 11, opacity: 0.8 }}>(save 34%)</span>
        </button>
        <button onClick={onClose} style={{ background: "none", border: "none", color: t.textMuted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Maybe later
        </button>
      </div>
    </>
  );
}

function LimitModal({ onClose, t, onUpgrade, isSignedIn, remaining, anonLimit }) {
  if (!isSignedIn) {
    // Anonymous user — priority is sign up, mention Pro as benefit
    return (
      <>
        <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 300, backdropFilter: "blur(4px)" }} />
        <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 301, width: "min(400px, 92vw)", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 32, boxShadow: "0 24px 64px rgba(0,0,0,0.5)", textAlign: "center" }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>⏳</div>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, color: t.text, marginBottom: 10, fontWeight: 800 }}>You've used your free tries</div>
          <p style={{ color: t.textDim, fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
            You've seen what Limji can do.<br />
            Create a free account to keep going — 3 generations/day, no credit card needed.
          </p>
          <div style={{ background: t.accentBg, border: `1px solid ${t.accent}`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left", fontSize: 12, color: t.textDim, lineHeight: 1.7 }}>
            ✓ Free account — 3 generations/day<br />
            ✓ History saved across devices<br />
            ✓ Upgrade to Pro anytime — $19/month · 150 generations/day
          </div>
          <a href="/sign-up" style={{ display: "block", width: "100%", padding: "13px 0", background: t.accent, color: "#0c0a09", borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginBottom: 8, textDecoration: "none", boxSizing: "border-box" }}>
            ✦ Create free account
          </a>
          <a href="/sign-in" style={{ display: "block", width: "100%", padding: "11px 0", background: "transparent", color: t.textDim, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: "inherit", textDecoration: "none", boxSizing: "border-box", marginBottom: 10 }}>
            Sign in to existing account
          </a>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.textMuted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
            Maybe later
          </button>
        </div>
      </>
    );
  }

  // Signed-in free user — upsell Pro
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 300, backdropFilter: "blur(4px)" }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 301, width: "min(420px, 92vw)", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 32, boxShadow: "0 24px 64px rgba(0,0,0,0.5)", textAlign: "center" }}>
        <div style={{ fontSize: 44, marginBottom: 14 }}>⏳</div>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, color: t.text, marginBottom: 10, fontWeight: 800 }}>Daily limit reached</div>
        <p style={{ color: t.textDim, fontSize: 13, lineHeight: 1.7, marginBottom: 20 }}>
          You've used your {FREE_LIMIT} free generations today.<br />
          Your limit resets at midnight.
        </p>
        <div style={{ background: t.accentBg, border: `1px solid ${t.accent}`, borderRadius: 12, padding: "14px 18px", marginBottom: 20, textAlign: "left" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.accentText, marginBottom: 6 }}>✦ Upgrade to Pro</div>
          <div style={{ fontSize: 12, color: t.textDim, lineHeight: 1.7 }}>
            150 generations/day · All tools · Priority support
          </div>
        </div>
        <button onClick={() => onUpgrade("monthly")} style={{ width: "100%", padding: "13px 0", background: t.accent, color: "#0c0a09", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginBottom: 8 }}>
          ✦ Monthly — $19/mo
        </button>
        <button onClick={() => onUpgrade("yearly")} style={{ width: "100%", padding: "12px 0", background: "transparent", color: t.accentText, border: `1px solid ${t.accent}`, borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
          ✦ Annual — $149/yr <span style={{ fontSize: 11, opacity: 0.75 }}>(save 34%)</span>
        </button>
        <button onClick={onClose} style={{ background: "none", border: "none", color: t.textMuted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Come back tomorrow
        </button>
      </div>
    </>
  );
}
const LOADING_MESSAGES = {
  ads: [
    "Reading your listing details...",
    "Choosing the best angles...",
    "Writing your headline...",
    "Crafting your ad copy...",
    "Polishing the words...",
    "Almost ready...",
  ],
  email: [
    "Understanding your situation...",
    "Finding the right tone...",
    "Drafting version A...",
    "Drafting version B...",
    "Refining the subject lines...",
    "Almost ready...",
  ],
};

function LoadingScreen({ tab, t }) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const messages = LOADING_MESSAGES[tab] || LOADING_MESSAGES.ads;

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMsgIndex(i => (i + 1) % messages.length);
        setVisible(true);
      }, 300);
    }, 2200);
    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 28, padding: "48px 24px", minHeight: 280 }}>
      {/* Animated logo */}
      <div style={{ position: "relative", width: 56, height: 56 }}>
        <div style={{ width: 56, height: 56, borderRadius: 14, background: t.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, animation: "pulse 1.6s ease-in-out infinite" }}>✦</div>
        <div style={{ position: "absolute", inset: -6, borderRadius: 20, border: `2px solid ${t.accent}`, opacity: 0.25, animation: "pulse 1.6s ease-in-out infinite 0.3s" }} />
      </div>

      {/* Progress bar */}
      <div style={{ width: "100%", maxWidth: 240, height: 2, background: t.border, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", background: t.accent, borderRadius: 2, animation: "barGrow 12s linear forwards" }} />
      </div>

      {/* Cycling message */}
      <div style={{ textAlign: "center", minHeight: 44 }}>
        <p style={{ color: t.textMuted, fontSize: 14, fontWeight: 600, transition: "opacity 0.3s, transform 0.3s", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(-4px)" }}>
          {messages[msgIndex]}
        </p>
        <p style={{ color: t.textDim, fontSize: 12, marginTop: 6 }}>
          {tab === "ads" ? "Writing multiple formats at once" : "Creating 2 different versions"}
        </p>
      </div>
    </div>
  );
}

function Card({ children, accent, t, animIndex }) {
  const cls = animIndex ? `card-in card-in-${animIndex}` : "";
  return <div className={cls} style={{ background: t.card, border: `1px solid ${accent || t.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>{children}</div>;
}

function CardHeader({ title, badge, action, t }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.1em" }}>{title}</span>
        {badge && <span style={{ background: t.pillBg, color: t.pillText, fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 600 }}>{badge}</span>}
      </div>
      {action}
    </div>
  );
}

function SubmitBtn({ loading, disabled, onClick, children, t }) {
  return (
    <button onClick={onClick} disabled={loading || disabled} style={{ width: "100%", padding: "13px 0", marginTop: 8, background: loading || disabled ? t.surface : t.accent, color: loading || disabled ? t.textDim : "#0c0a09", border: `1px solid ${loading || disabled ? t.border : t.accent}`, borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: loading || disabled ? "not-allowed" : "pointer", letterSpacing: "0.03em", transition: "all 0.2s", fontFamily: "inherit" }}>
      {children}
    </button>
  );
}

function StarBtn({ isFav, onClick, t }) {
  return (
    <button onClick={onClick} style={{ background: "transparent", border: `1px solid ${t.border}`, borderRadius: 6, padding: "4px 10px", fontSize: 16, cursor: "pointer", color: isFav ? t.accent : t.textDim, transition: "color 0.2s" }}>
      {isFav ? "★" : "☆"}
    </button>
  );
}


function ExportBtn({ result, t }) {
  const [open, setOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeSubjects:  true,
    includeVersionB:  true,
    includeWhatsapp:  true,
    includeFollowUp:  true,
    includeTip:       true,
    includeSummary:   true,
  });
  function toggleOpt(k) { setExportOptions(p => ({ ...p, [k]: !p[k] })); }

  function buildPlainText(result) {
    const { tool, data, input } = result;
    const lines = [];
    const hr = "----------------------------------------------------";
    if (tool === "ads" && (data.headline || input?.category === "store")) {
      lines.push("LIMJI AI - PRODUCT COPY", hr, "");
      lines.push("PRODUCT: " + (input?.title || ""), "");
      if (data.headline) { lines.push("HEADLINE", data.headline, ""); }
      if (data.descriptionA) { lines.push("DESCRIPTION A - " + (data.descriptionA.label || ""), data.descriptionA.body, ""); }
      if (data.descriptionB) { lines.push("DESCRIPTION B - " + (data.descriptionB.label || ""), data.descriptionB.body, ""); }
      if (data.bullets?.length) { lines.push("KEY BENEFITS"); data.bullets.forEach(b => lines.push("  - " + b)); lines.push(""); }
      if (data.seo) { lines.push("SEO", "Title: " + data.seo.title, "Meta: " + data.seo.meta, "Keywords: " + (data.seo.keywords || []).join(", "), ""); }
      if (data.social) { lines.push("SOCIAL MEDIA"); if (data.social.instagram) lines.push("Instagram: " + data.social.instagram); if (data.social.facebook) lines.push("Facebook: " + data.social.facebook); if (data.social.twitter) lines.push("Twitter: " + data.social.twitter); lines.push(""); }
    } else if (tool === "ads") {
      lines.push("LIMJI AI - SMART ADS", hr, "");
      lines.push("LISTING: " + (input?.title || ""), "");
      AD_FORMATS.forEach(fmt => {
        if (data[fmt.id]) {
          lines.push(fmt.label.toUpperCase());
          const c = typeof data[fmt.id] === "object" ? data[fmt.id]?.content : data[fmt.id];
          lines.push(c, "");
        }
      });
    } else if (tool === "email") {
      lines.push("LIMJI AI - SMART EMAIL", hr, "");
      if (data.subjects?.length) { lines.push("SUBJECT LINES", ...data.subjects.map((s,i) => `${i+1}. ${s}`), ""); }
      if (data.versionA) { lines.push("VERSION A - " + (data.versionA.label || ""), "", data.versionA.body, ""); }
      if (data.versionB) { lines.push("VERSION B - " + (data.versionB.label || ""), "", data.versionB.body, ""); }
      if (data.whatsapp) { lines.push("WHATSAPP VERSION", data.whatsapp, ""); }
      if (data.followUp) { lines.push("48H FOLLOW-UP", data.followUp, ""); }
    }
    if (data.tip) { lines.push(hr, "TIP: " + data.tip); }
    lines.push("", hr, "Generated by Limji AI - " + formatDate());
    return lines.join("\n");
  }

  function exportTxt() {
    const text = buildPlainText(result);
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "limji-" + (result.input?.title || result.tool).toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") + "-" + formatDate().replace(/\//g, "") + ".txt";
    a.click();
    URL.revokeObjectURL(url);
    setOpen(false);
  }

  function formatDate() {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  function buildTextBlocks(result, opts = {}) {
    const o = { includeSubjects: true, includeVersionB: true, includeWhatsapp: true, includeFollowUp: true, includeTip: true, includeSummary: true, ...opts };
    const { tool, data, input } = result;
    const blocks = []; // { type: "title"|"section"|"body"|"tip", text }

    if (tool === "ads" && (data.headline || input?.category === "store")) {
      if (data.headline) {
        blocks.push({ type: "section", text: "HEADLINE" });
        blocks.push({ type: "headline", text: data.headline });
      }
      if (data.descriptionA) {
        blocks.push({ type: "section", text: "DESCRIPTION A — " + (data.descriptionA.label || "") });
        blocks.push({ type: "body", text: data.descriptionA.body || "" });
      }
      if (data.descriptionB) {
        blocks.push({ type: "section", text: "DESCRIPTION B — " + (data.descriptionB.label || "") });
        blocks.push({ type: "body", text: data.descriptionB.body || "" });
      }
      if (data.bullets?.length) {
        blocks.push({ type: "section", text: "KEY BENEFITS" });
        data.bullets.forEach(b => blocks.push({ type: "bullet", text: b }));
      }
      if (data.seo) {
        blocks.push({ type: "section", text: "SEO" });
        blocks.push({ type: "body", text: "Title: " + (data.seo.title || "") });
        blocks.push({ type: "body", text: "Meta: " + (data.seo.meta || "") });
        if (data.seo.keywords?.length) blocks.push({ type: "body", text: "Keywords: " + data.seo.keywords.join(", ") });
      }
      if (data.social) {
        blocks.push({ type: "section", text: "SOCIAL MEDIA" });
        if (data.social.instagram) blocks.push({ type: "body", text: "Instagram:\n" + data.social.instagram });
        if (data.social.facebook) blocks.push({ type: "body", text: "Facebook:\n" + data.social.facebook });
        if (data.social.twitter) blocks.push({ type: "body", text: "Twitter/X:\n" + data.social.twitter });
      }
    } else if (tool === "ads") {
      AD_FORMATS.forEach(fmt => {
        if (data[fmt.id]) {
          blocks.push({ type: "section", text: fmt.label.toUpperCase() });
          const c = typeof data[fmt.id] === "object" ? data[fmt.id]?.content : data[fmt.id];
          blocks.push({ type: "body", text: c || "" });
        }
      });
    } else if (tool === "email") {
      const ei = result.input || {};
      const et = ei.type || "";

      // ── Type-specific structured info block ─────────────────────────────
      if (et === "invoice" && (ei.invoiceNumber || ei.amount || ei.dueDate || ei.clientName)) {
        blocks.push({ type: "infoblock", rows: [
          ei.invoiceNumber  ? ["Invoice #",  ei.invoiceNumber]  : null,
          ei.clientName     ? ["Client",     ei.clientName]     : null,
          ei.amount         ? ["Amount Due", ei.amount]         : null,
          ei.dueDate        ? ["Due Date",   ei.dueDate]        : null,
          ei.daysOverdue    ? ["Overdue",    ei.daysOverdue]    : null,
          ei.invoiceResolution ? ["Resolution", ei.invoiceResolution] : null,
        ].filter(Boolean) });
      }
      if (et === "pricing" && (ei.priceRange || ei.pricingStructure || ei.whatsIncluded)) {
        blocks.push({ type: "pricebox",
          price:     ei.priceRange || "",
          structure: ei.pricingStructure || "",
          includes:  ei.whatsIncluded || "",
          client:    ei.clientName || "",
        });
      }
      if (et === "meeting" && (ei.meetingPurpose || ei.suggestedDates || ei.duration || ei.meetingFormat)) {
        blocks.push({ type: "infoblock", rows: [
          ei.meetingPurpose  ? ["Purpose",  ei.meetingPurpose]  : null,
          ei.meetingFormat   ? ["Format",   ei.meetingFormat]   : null,
          ei.duration        ? ["Duration", ei.duration]        : null,
          ei.suggestedDates  ? ["Dates",    ei.suggestedDates]  : null,
        ].filter(Boolean) });
      }
      if (et === "recap" && (ei.decisionsМade || ei.actionItems)) {
        blocks.push({ type: "recaptable",
          decisions:   ei.decisionsМade || "",
          actions:     ei.actionItems || "",
          attendees:   ei.attendees || "",
          meetingTopic: ei.meetingTopic || "",
        });
      }
      if (et === "reminder" && (ei.reminderSubject || ei.reminderDeadline)) {
        blocks.push({ type: "infoblock", rows: [
          ei.reminderSubject  ? ["Reminder", ei.reminderSubject]  : null,
          ei.reminderDeadline ? ["Deadline", ei.reminderDeadline] : null,
          ei.urgencyLevel     ? ["Urgency",  ei.urgencyLevel]     : null,
        ].filter(Boolean) });
      }
      if (et === "badnews" && (ei.whatHappened || ei.solution || ei.newTimeline)) {
        blocks.push({ type: "infoblock", rows: [
          ei.projectOrOrder ? ["Project / Order", ei.projectOrOrder] : null,
          ei.whatHappened   ? ["What Happened",   ei.whatHappened]   : null,
          ei.solution       ? ["Solution",        ei.solution]       : null,
          ei.newTimeline    ? ["New Timeline",    ei.newTimeline]    : null,
        ].filter(Boolean) });
      }

      // ── Standard email blocks ────────────────────────────────────────────
      if (o.includeSubjects && data.subjects?.length) {
        blocks.push({ type: "section", text: "SUBJECT LINES" });
        data.subjects.forEach((s, i) => blocks.push({ type: "subject", text: (i+1) + ".  " + s }));
      }
      if (data.versionA) {
        blocks.push({ type: "section", text: "VERSION A  —  " + (data.versionA.label || "") });
        blocks.push({ type: "body", text: data.versionA.body || "" });
      }
      if (o.includeVersionB && data.versionB) {
        blocks.push({ type: "section", text: "VERSION B  —  " + (data.versionB.label || "") });
        blocks.push({ type: "body", text: data.versionB.body || "" });
      }
      if (o.includeWhatsapp && data.whatsapp) {
        blocks.push({ type: "section", text: "WHATSAPP VERSION" });
        blocks.push({ type: "body", text: data.whatsapp });
      }
      if (o.includeFollowUp && data.followUp) {
        blocks.push({ type: "section", text: "48H FOLLOW-UP" });
        blocks.push({ type: "body", text: data.followUp });
      }
    }
    if (o.includeTip && data.tip) blocks.push({ type: "tip", text: data.tip });
    return blocks;
  }

  // Strip only true emoji/symbol blocks — preserve dashes, newlines, punctuation
  function stripEmojis(str) {
    return String(str || "")
      .replace(/[\u{1F000}-\u{1FFFF}]/gu, "")   // emoji blocks
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, "")    // more emoji
      .replace(/[\u{1F600}-\u{1F64F}]/gu, "")    // emoticons
      .replace(/[\u{2600}-\u{26FF}]/gu, "")      // misc symbols (not dashes)
      .replace(/[\uFE00-\uFE0F]/g, "")           // variation selectors
      .replace(/\uFFFD/g, "")                    // replacement char
      // replace typographic quotes/dashes with ASCII equivalents
      .replace(/[\u2018\u2019]/g, "'")           // smart single quotes -> '
      .replace(/[\u201C\u201D]/g, '"')           // smart double quotes -> "
      .replace(/[\u2013]/g, "-")                 // en-dash -> -
      .replace(/[\u2014]/g, " - ")               // em-dash -> spaced hyphen
      .replace(/[\u2026]/g, "...")               // ellipsis -> ...
      // strip remaining non-latin extended (but keep newlines and tabs)
      .replace(/[^\t\n\r\u0020-\u007E\u00A0-\u024F]/g, "");  // eslint-disable-line no-control-regex
      // NOTE: do NOT collapse whitespace here — newlines must survive for paragraph splitting
  }

  async function exportPdf(opts) {
    setOpen(false);

    if (!window.jspdf) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }

    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 52;
    const contentW = pageW - margin * 2;
    const LINE_H = 20;   // body line height
    const FOOTER_H = 52; // reserved footer space
    let y = margin;

    function checkPage(needed = 24) {
      if (y + needed > pageH - FOOTER_H) {
        doc.addPage();
        y = margin;
      }
    }

    const PARA_GAP = 14;  // gap after a paragraph break (\n\n)
    const LINE_GAP = 4;   // gap after a soft line break (\n — sign-offs etc.)

    // Parse raw body text into paragraph objects [{text, sep}]
    function parseParagraphs(rawText) {
      const clean = stripEmojis(rawText);
      const parts = clean.split(/(\n\n|\n)/);
      const paras = [];
      let i = 0;
      while (i < parts.length) {
        const text = (parts[i] || "").trim();
        const sep  = parts[i + 1] || "";
        i += 2;
        if (text) paras.push({ text, sep });
      }
      return paras;
    }

    // Measure total rendered height of a body text block (without drawing)
    function measureBody(rawText, maxW, fontSize = 12) {
      doc.setFontSize(fontSize);
      doc.setFont("times", "normal"); // must match drawBody font for accurate measurement
      const paras = parseParagraphs(rawText);
      let h = 0;
      paras.forEach(({ text, sep }, pi) => {
        h += doc.splitTextToSize(text, maxW).length * LINE_H;
        if (pi < paras.length - 1) h += sep === "\n\n" ? PARA_GAP : LINE_GAP;
      });
      return h;
    }

    // Render body text — never splits mid-paragraph across pages
    function drawBody(rawText, x, maxW, fontSize = 12, color = [68, 64, 60], bold = false) {
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);
      doc.setFont("times", bold ? "bold" : "normal");
      const paras = parseParagraphs(rawText);

      paras.forEach(({ text, sep }, pi) => {
        const wrapped = doc.splitTextToSize(text, maxW);
        const paraH   = wrapped.length * LINE_H;
        const gap     = pi < paras.length - 1 ? (sep === "\n\n" ? PARA_GAP : LINE_GAP) : 0;
        // If this paragraph alone won't fit, start a fresh page (unless it's taller than a full page)
        if (y + paraH > pageH - FOOTER_H && paraH < pageH - FOOTER_H - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(wrapped, x, y, { maxWidth: maxW });
        y += paraH + gap;
      });
    }

    // ── Per-type color palette ──
    const TYPE_COLORS = {
      // Business — blue slate
      invoice:      [37,  99, 235],   // blue-600
      proposal:     [37,  99, 235],
      cold:         [37,  99, 235],
      project:      [14, 116, 144],   // cyan-700
      meeting:      [14, 116, 144],
      announcement: [14, 116, 144],
      badnews:      [185,  28,  28],  // red-700
      checkin:      [37,  99, 235],
      recap:        [14, 116, 144],
      pricing:      [21, 128,  61],   // green-700
      approval:     [21, 128,  61],
      objection:    [185,  28,  28],
      support:      [67,  56, 202],   // indigo-700
      refund:       [185,  28,  28],
      // Relationship — teal/green
      apology:      [13, 148, 136],   // teal-600
      thankyou:     [13, 148, 136],
      favor:        [13, 148, 136],
      intro:        [13, 148, 136],
      // Personal — violet
      jobapp:       [109,  40, 217],  // violet-700
      ooo:          [109,  40, 217],
      clarify:      [109,  40, 217],
      // New
      reply:        [37,  99, 235],
      reminder:     [185,  28,  28],
    };
    const emailType   = result.tool === "email" ? (result.input?.type || "") : "";

    // ── Export ID — meaningful: SE-COLD-0301 / SA-0301 ──
    const typeAbbrev = {
      invoice: "INV", proposal: "PROP", cold: "COLD", project: "PROJ",
      meeting: "MTG", announcement: "ANN", badnews: "NEWS", checkin: "CHK",
      recap: "RECAP", pricing: "PRICE", approval: "APPR", objection: "OBJ",
      support: "SUPP", refund: "REFUND", apology: "APOL", thankyou: "THX",
      favor: "FAVOR", intro: "INTRO", jobapp: "JOB", ooo: "OOO",
      clarify: "CLR", reply: "REPLY", reminder: "REMIND",
    };
    const now      = new Date();
    const ddmm     = String(now.getDate()).padStart(2, "0") + String(now.getMonth() + 1).padStart(2, "0");
    const typeCode = emailType ? (typeAbbrev[emailType] || emailType.toUpperCase().slice(0, 5)) : "";
    const exportId = (result.tool === "email" ? "SE" : "SA") +
                     (typeCode ? "-" + typeCode : "") + "-" + ddmm;
    const accentRgb   = TYPE_COLORS[emailType] || [217, 119, 6]; // fallback amber
    const accentLight = accentRgb.map(v => Math.min(255, v + 160)); // tint for text
    const accentMid   = accentRgb.map(v => Math.min(255, v + 80));

    // Resolve display info
    const typeInfo    = ALL_EMAIL_TYPES.find(t => t.id === emailType);
    const typeLabel   = typeInfo?.label || (result.tool === "email" ? "Smart Email" : "Smart Ads");

    // Smart recipient for title and filename
    const inp = result.input || {};
    const recipient   = inp.clientName || inp.recipientName || inp.company || "";
    const titleText   = recipient ? `${typeLabel} — ${recipient}` : typeLabel;

    // ── Header band ──
    // Layout (top to bottom, left to right):
    //   Row 1: [L] LIMJI (wordmark)          Export ID (right)
    //   Row 2: Title — Recipient             Type pill (right)
    //   Row 3: Sender • Business • Date
    const headerH = 78;
    doc.setFillColor(...accentRgb);
    doc.roundedRect(margin, y, contentW, headerH, 8, 8, "F");

    // ── Row 1: Logo wordmark (left) + Export ID (right) ──
    // White square "L" badge
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin + 16, y + 11, 16, 16, 3, 3, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...accentRgb);
    doc.text("L", margin + 21, y + 22);
    // LIMJI wordmark
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("LIMJI", margin + 38, y + 22);
    // Export ID right-aligned
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...accentLight);
    doc.text(exportId, pageW - margin - 16, y + 22, { align: "right" });

    // ── Row 2: Title (left) + Type pill (right) ──
    const titleRight = 110; // reserve space for pill
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    const titleLines = doc.splitTextToSize(stripEmojis(titleText), contentW - titleRight);
    doc.text(titleLines, margin + 16, y + 40, { maxWidth: contentW - titleRight });
    // Type pill — right side, vertically centered on row 2
    if (typeInfo) {
      const pillLabel = typeInfo.label.toUpperCase();
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      const pillTextW = doc.getTextWidth(pillLabel);
      const pillW     = pillTextW + 16;
      const pillH     = 16;
      const pillX     = margin + contentW - pillW - 4;
      const pillY     = y + 30;
      doc.setFillColor(
        Math.min(255, accentRgb[0] + 50),
        Math.min(255, accentRgb[1] + 50),
        Math.min(255, accentRgb[2] + 50)
      );
      doc.roundedRect(pillX, pillY, pillW, pillH, 4, 4, "F");
      doc.setTextColor(255, 255, 255);
      doc.text(pillLabel, pillX + pillW / 2, pillY + 11, { align: "center" });
    }

    // ── Row 3: Sender • Business • Date ──
    const senderLine = stripEmojis([
      inp.yourName || "",
      inp.businessName || "",
      formatDate(),
    ].filter(Boolean).join("  •  "));
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...accentMid);
    doc.text(senderLine, margin + 16, y + headerH - 10);

    y += headerH + 20;

    // ── At-a-glance summary block (email only) ──
    if (result.tool === "email" && opts?.includeSummary !== false) {
      const tone        = inp.tone || "";
      const vALabel     = result.data?.versionA?.label || "";
      const vBLabel     = result.data?.versionB?.label || "";
      const channels    = ["Email", result.data?.whatsapp ? "WhatsApp" : null, result.data?.followUp ? "48h Follow-Up" : null].filter(Boolean).join("  +  ");
      const summaryRows = [
        typeLabel                      ? ["TYPE",     typeLabel]              : null,
        (inp.yourName || inp.businessName) ? ["FROM",  stripEmojis((inp.yourName || "") + (inp.businessName ? " • " + inp.businessName : ""))] : null,
        recipient                      ? ["TO",       recipient]              : null,
        tone                           ? ["TONE",     tone]                   : null,
        (vALabel || vBLabel)           ? ["VERSIONS", [vALabel, vBLabel].filter(Boolean).join("  vs  ")] : null,
        channels                       ? ["CHANNELS", channels]               : null,
      ].filter(Boolean);

      if (summaryRows.length) {
        const labelColW = 72;   // fixed label column width
        const valueColW = contentW - labelColW - 28; // remaining for value
        const rowPad    = 7;    // vertical padding per row
        const rowFontSz = 10;

        // Pre-measure each row height based on value wrapping
        doc.setFontSize(rowFontSz);
        doc.setFont("times", "normal");
        const rowHeights = summaryRows.map(([, value]) => {
          const lines = doc.splitTextToSize(String(value), valueColW);
          return lines.length * 13 + rowPad * 2;
        });
        const headerAreaH = 22; // "AT A GLANCE" label + divider
        const sumH = headerAreaH + rowHeights.reduce((a, b) => a + b, 0) + 8;

        checkPage(sumH + 8);

        // Card background + border
        doc.setFillColor(250, 250, 253);
        doc.roundedRect(margin, y, contentW, sumH, 6, 6, "F");
        doc.setDrawColor(...accentRgb.map(v => Math.min(255, v + 110)));
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, y, contentW, sumH, 6, 6, "S");
        // Left accent bar
        doc.setFillColor(...accentRgb);
        doc.roundedRect(margin, y, 4, sumH, 6, 6, "F");

        // "AT A GLANCE" section label
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...accentRgb);
        doc.text("AT A GLANCE", margin + 14, y + 14);

        // Divider
        doc.setDrawColor(...accentRgb.map(v => Math.min(255, v + 130)));
        doc.setLineWidth(0.3);
        doc.line(margin + 14, y + 17, margin + contentW - 14, y + 17);

        // Rows — vertical list, label left + value right
        let rowY = y + headerAreaH;
        summaryRows.forEach(([label, value], ri) => {
          const rh    = rowHeights[ri];
          const textY = rowY + rowPad + 9; // baseline

          // Alternating row tint
          if (ri % 2 === 1) {
            doc.setFillColor(244, 244, 250);
            doc.rect(margin + 4, rowY, contentW - 4, rh, "F");
          }

          // Label
          doc.setFontSize(7.5);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...accentRgb);
          doc.text(label, margin + 14, textY);

          // Value — wraps within its column
          doc.setFontSize(rowFontSz);
          doc.setFont("times", "normal");
          doc.setTextColor(30, 26, 22);
          const vLines = doc.splitTextToSize(String(value), valueColW);
          doc.text(vLines, margin + 14 + labelColW, textY, { maxWidth: valueColW });

          // Row divider (not last)
          if (ri < summaryRows.length - 1) {
            doc.setDrawColor(230, 228, 240);
            doc.setLineWidth(0.25);
            doc.line(margin + 14, rowY + rh, margin + contentW - 14, rowY + rh);
          }
          rowY += rh;
        });
        y += sumH + 16;
      }
    }

    // ── Content blocks ──
    const blocks = buildTextBlocks(result, opts);

    blocks.forEach((block, bi) => {

      // ── Structured info block (invoice, meeting, reminder, etc.) ──
      if (block.type === "infoblock") {
        y += 6;
        const rows     = block.rows;
        const rowH     = 22;
        const totalH   = rows.length * rowH + 16;
        checkPage(totalH + 10);
        // Container
        doc.setFillColor(248, 248, 252);
        doc.roundedRect(margin, y, contentW, totalH, 5, 5, "F");
        doc.setDrawColor(...accentRgb);
        doc.setLineWidth(0.6);
        doc.roundedRect(margin, y, contentW, totalH, 5, 5, "S");
        // Left accent bar
        doc.setFillColor(...accentRgb);
        doc.roundedRect(margin, y, 4, totalH, 5, 5, "F");
        // Rows
        const colLabelW = 110;
        rows.forEach(([label, value], ri) => {
          const ry = y + 10 + ri * rowH;
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...accentRgb);
          doc.text(label.toUpperCase(), margin + 14, ry);
          doc.setFontSize(10);
          doc.setFont("times", "normal");
          doc.setTextColor(30, 27, 24);
          const valLines = doc.splitTextToSize(String(value), contentW - colLabelW - 20);
          doc.text(valLines, margin + colLabelW, ry);
          // row divider (not last)
          if (ri < rows.length - 1) {
            doc.setDrawColor(220, 220, 230);
            doc.setLineWidth(0.3);
            doc.line(margin + 10, ry + 10, margin + contentW - 10, ry + 10);
          }
        });
        y += totalH + 14;

      // ── Price highlight box ──
      } else if (block.type === "pricebox") {
        y += 6;
        const priceH = 70;
        checkPage(priceH + 10);
        doc.setFillColor(...accentRgb);
        doc.roundedRect(margin, y, contentW, priceH, 6, 6, "F");
        // Price — large white
        doc.setFontSize(22);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text(stripEmojis(block.price) || "See email", margin + 20, y + 30);
        // Structure badge
        if (block.structure) {
          doc.setFontSize(8.5);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...accentLight);
          doc.text(block.structure.toUpperCase(), margin + 20, y + 46);
        }
        // What's included — right side
        if (block.includes) {
          doc.setFontSize(9);
          doc.setFont("times", "normal");
          doc.setTextColor(255, 255, 255);
          const incLines = doc.splitTextToSize(stripEmojis(block.includes), contentW / 2 - 20);
          doc.text(incLines.slice(0, 3), margin + contentW / 2, y + 18, { maxWidth: contentW / 2 - 10 });
        }
        y += priceH + 14;

      // ── Recap table: decisions + action items ──
      } else if (block.type === "recaptable") {
        y += 6;

        // Meeting topic heading
        if (block.meetingTopic) {
          checkPage(28);
          doc.setFontSize(13);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...accentRgb);
          doc.text(stripEmojis(block.meetingTopic), margin, y);
          y += 18;
        }

        // Attendees line
        if (block.attendees) {
          checkPage(16);
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(120, 113, 108);
          doc.text("Attendees: " + stripEmojis(block.attendees), margin, y);
          y += 14;
        }

        // Parse input fields into clean line arrays
        // Split on newline, period-then-capital (sentence boundary), or semicolon
        const parseRecapItems = (raw) => {
          const clean = stripEmojis(raw || "");
          const byNewline = clean.split("\n").map(s => s.replace(/^[-*]\s*/, "").trim()).filter(s => s.length > 3);
          if (byNewline.length > 1) return byNewline;
          return clean.split(/;\s*|\.\s+(?=[A-Z])/).map(s => s.replace(/^[-*]\s*/, "").trim()).filter(s => s.length > 3);
        };

        const decisionItems = parseRecapItems(block.decisions);
        const actionItems   = parseRecapItems(block.actions);
        const maxItems      = Math.max(decisionItems.length, actionItems.length, 1);

        // Two-column table — synchronized rows (both columns same row height)
        const gap   = 10;
        const colW  = (contentW - gap) / 2;
        const tblX  = [margin, margin + colW + gap];
        const hdrH  = 22;

        // Header row
        checkPage(hdrH + 30);
        ["DECISIONS MADE", "ACTION ITEMS"].forEach((h, ci) => {
          doc.setFillColor(...accentRgb);
          doc.roundedRect(tblX[ci], y, colW, hdrH, 3, 3, "F");
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(255, 255, 255);
          doc.text(h, tblX[ci] + 10, y + 15);
        });
        y += hdrH;

        // Item rows — one decision + one action per row, row height = tallest of the two
        const ITEM_PAD = 8;
        doc.setFontSize(10);
        doc.setFont("times", "normal");

        for (let ri = 0; ri < maxItems; ri++) {
          const dText  = decisionItems[ri] ? decisionItems[ri] : "";
          const aText  = actionItems[ri]   ? actionItems[ri]   : "";
          const dLines = dText ? doc.splitTextToSize(dText, colW - 22) : [];
          const aLines = aText ? doc.splitTextToSize(aText, colW - 22) : [];
          const rowH   = Math.max(dLines.length, aLines.length, 1) * 14 + ITEM_PAD * 2;

          checkPage(rowH + 4);

          // Draw both cells at same y with same height — inline to avoid no-loop-func
          const rowBgColor = ri % 2 === 0 ? [252, 250, 248] : [246, 244, 241];
          doc.setFillColor(...rowBgColor);
          doc.rect(tblX[0], y, colW, rowH, "F");
          doc.setDrawColor(225, 220, 215);
          doc.setLineWidth(0.3);
          doc.rect(tblX[0], y, colW, rowH, "S");
          doc.setFillColor(...rowBgColor);
          doc.rect(tblX[1], y, colW, rowH, "F");
          doc.setDrawColor(225, 220, 215);
          doc.setLineWidth(0.3);
          doc.rect(tblX[1], y, colW, rowH, "S");

          // Accent dot marker
          doc.setFillColor(...accentRgb);
          if (dLines.length) {
            doc.circle(tblX[0] + 7, y + ITEM_PAD + 6, 2, "F");
            doc.setTextColor(40, 36, 30);
            doc.text(dLines, tblX[0] + 14, y + ITEM_PAD + 10, { maxWidth: colW - 22 });
          }
          if (aLines.length) {
            doc.setFillColor(...accentRgb);
            doc.circle(tblX[1] + 7, y + ITEM_PAD + 6, 2, "F");
            doc.setTextColor(40, 36, 30);
            doc.text(aLines, tblX[1] + 14, y + ITEM_PAD + 10, { maxWidth: colW - 22 });
          }
          y += rowH;
        }
        y += 14;

      } else if (block.type === "section") {
        // Measure section header + full body that follows — keep them together
        const nextBlock = blocks[bi + 1];
        const SECTION_LABEL_H = 40; // label + divider
        let sectionH = SECTION_LABEL_H;
        if (nextBlock && nextBlock.type === "body") {
          sectionH += measureBody(nextBlock.text, contentW) + 12;
        }
        const usablePageH = pageH - FOOTER_H - margin;
        if (bi > 0) y += 14;
        // If section + body fits on a fresh page but not current page → new page
        if (sectionH <= usablePageH && y + sectionH > pageH - FOOTER_H) {
          doc.addPage();
          y = margin;
        } else {
          checkPage(SECTION_LABEL_H); // at minimum ensure label fits
        }

        // Label pill
        const labelText = stripEmojis(block.text).toUpperCase();
        doc.setFontSize(7.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(120, 113, 108);

        // left accent bar
        doc.setFillColor(...accentRgb);
        doc.rect(margin, y - 10, 3, 14, "F");

        // section label
        doc.setTextColor(100, 92, 84);
        doc.text(labelText, margin + 10, y);
        y += 6;

        // thin divider
        doc.setDrawColor(220, 215, 210);
        doc.setLineWidth(0.5);
        doc.line(margin, y, margin + contentW, y);
        y += 12;

      } else if (block.type === "headline") {
        checkPage(36);
        doc.setFontSize(15);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(28, 25, 23);
        const hl = doc.splitTextToSize(stripEmojis(block.text), contentW);
        doc.text(hl, margin, y, { maxWidth: contentW });
        y += hl.length * 20 + 6;

      } else if (block.type === "subject") {
        // Subject line row with subtle background
        const subjectText = stripEmojis(block.text).trim();
        const subjectWrapped = doc.splitTextToSize(subjectText, contentW - 16);
        const rowH = subjectWrapped.length * LINE_H + 12;
        checkPage(rowH + 4);
        doc.setFillColor(250, 248, 244);
        doc.roundedRect(margin, y - 10, contentW, rowH, 3, 3, "F");
        doc.setDrawColor(235, 228, 218);
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, y - 10, contentW, rowH, 3, 3, "S");
        doc.setFontSize(12);
        doc.setFont("times", "normal");
        doc.setTextColor(50, 44, 38);
        doc.text(subjectWrapped, margin + 10, y, { maxWidth: contentW - 16 });
        y += rowH + 4;

      } else if (block.type === "bullet") {
        doc.setFontSize(12);
        doc.setFont("times", "normal");
        doc.setTextColor(68, 64, 60);
        const bulletText = stripEmojis(block.text).trim();
        const wrapped = doc.splitTextToSize(bulletText, contentW - 20);
        checkPage(wrapped.length * LINE_H + 4);
        // draw a small square bullet
        doc.setFillColor(...accentRgb);
        doc.rect(margin + 2, y - 7, 4, 4, "F");
        doc.text(wrapped, margin + 14, y, { maxWidth: contentW - 20 });
        y += wrapped.length * LINE_H + 4;

      } else if (block.type === "tip") {
        y += 10;
        const tipText = stripEmojis(block.text).trim();
        const tipWrapped = doc.splitTextToSize(tipText, contentW - 32);
        const tipBoxH = tipWrapped.length * LINE_H + 36;
        checkPage(tipBoxH + 10);

        // box
        doc.setFillColor(255, 251, 242);
        doc.roundedRect(margin, y, contentW, tipBoxH, 5, 5, "F");
        doc.setDrawColor(...accentRgb);
        doc.setLineWidth(0.75);
        doc.roundedRect(margin, y, contentW, tipBoxH, 5, 5, "S");
        // left bar
        doc.setFillColor(...accentRgb);
        doc.rect(margin, y, 4, tipBoxH, "F");

        // PRO TIP label
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(180, 120, 20);
        doc.text("PRO TIP", margin + 14, y + 14);

        // tip text
        doc.setFontSize(12);
        doc.setFont("times", "normal");
        doc.setTextColor(100, 70, 10);
        doc.text(tipWrapped, margin + 14, y + 26, { maxWidth: contentW - 32 });
        y += tipBoxH + 14;

      } else {
        // body — drawBody handles its own page breaking
        y += 2;
        drawBody(block.text, margin, contentW);
        y += 10;
      }
    });

    // ── Footer on every page ──
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setDrawColor(220, 215, 210);
      doc.setLineWidth(0.5);
      doc.line(margin, pageH - 38, pageW - margin, pageH - 38);
      doc.setFontSize(8.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(160, 155, 150);
      doc.text("Generated with Limji  •  limji.app", margin, pageH - 22);
      doc.text(exportId + "  •  Page " + i + " / " + totalPages, pageW - margin, pageH - 22, { align: "right" });
    }

    // Smart filename: limji-[type]-[recipient]-[date].pdf
    const fileTypePart  = emailType ? emailType : (result.tool || "export");
    const fileRecipient = recipient ? recipient.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) : "";
    const fileDate      = formatDate().replace(/\//g, "");
    const filename      = "limji-" + fileTypePart + (fileRecipient ? "-" + fileRecipient : "") + "-" + fileDate + ".pdf";
    doc.save(filename);
  }

  if (!result) return null;

  const optLabels = [
    { key: "includeSummary",  label: "At-a-glance summary" },
    { key: "includeSubjects", label: "Subject lines" },
    { key: "includeVersionB", label: "Version B" },
    { key: "includeWhatsapp", label: "WhatsApp version" },
    { key: "includeFollowUp", label: "48h follow-up" },
    { key: "includeTip",      label: "Pro tip" },
  ];

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => { setOpen(o => !o); }}
        style={{ background: "transparent", border: `1px solid ${t.border}`, color: t.textMuted, borderRadius: 8, padding: "4px 12px", cursor: "pointer", fontSize: 11, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
        {"⬇ Export "}{open ? "▲" : "▼"}
      </button>
      {open && <>
        <div onClick={() => { setOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
        <div style={{ position: "absolute", top: "110%", right: 0, background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: 6, minWidth: 210, zIndex: 50, boxShadow: "0 8px 24px rgba(0,0,0,0.2)" }}>

          {/* What to include toggles */}
          <div style={{ padding: "6px 10px 4px", borderBottom: `1px solid ${t.border}`, marginBottom: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: t.textDim, letterSpacing: "0.06em", marginBottom: 6 }}>INCLUDE IN EXPORT</div>
            {optLabels.map(({ key, label }) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", cursor: "pointer", fontSize: 12, color: t.textMuted }}>
                <input type="checkbox" checked={exportOptions[key]} onChange={() => toggleOpt(key)}
                  style={{ accentColor: t.accent, width: 13, height: 13, cursor: "pointer" }} />
                {label}
              </label>
            ))}
          </div>

          {/* Export actions */}
          <button onClick={() => { exportPdf(exportOptions); setOpen(false); }}
            style={{ width: "100%", padding: "9px 12px", borderRadius: 7, border: "none", background: "transparent", color: t.text, fontSize: 13, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
            onMouseEnter={e => e.currentTarget.style.background = t.accentBg}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            {"📄 Save as PDF"}
          </button>
          <button onClick={() => { exportTxt(); setOpen(false); }}
            style={{ width: "100%", padding: "9px 12px", borderRadius: 7, border: "none", background: "transparent", color: t.text, fontSize: 13, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
            onMouseEnter={e => e.currentTarget.style.background = t.accentBg}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            {"📝 Save as .txt"}
          </button>
        </div>
      </>}
    </div>
  );
}


// ── FIELD TIPS ─────────────────────────────────────────────────────────────
// Base tips — used when no category-specific override exists
const FIELD_TIPS = {
  title:               "Be specific — include the detail that makes someone stop scrolling. 'iPhone 14 Pro 256GB Space Black, like new' gets 3× more clicks than 'iPhone for sale'.",
  price:               "Include currency and be specific. 'AED 2,500 (negotiable)' gets more responses than just 'negotiable'. A number anchors the conversation.",
  location:            "Buyers and clients filter by area. 'Dubai Marina' is better than 'Dubai'. The more specific, the more relevant inquiries you get.",
  condition:           "Honest descriptions build trust. If there's a small scratch, say so — surprises during meetups kill deals.",
  brand:               "Brand recognition is a trust signal and helps buyers search. Always include it even if it feels obvious.",
  model:               "Buyers search by model. 'Camry 2.5 LE' attracts more serious buyers than just 'Camry'.",

  // Electronics & Gadgets — keySpecs
  keySpecs:            "Cover: storage, battery health, any issues, what's included (original box, charger, case, extras). Buyers compare listings side by side — the more detail you give, the more trust you build.",

  // Fashion & Clothing — keyDetails
  keyDetails:          "Include size details, what's in the package (dustbag, box, tags), how many times worn, and any flaws. Buyers of fashion items check everything before messaging.",

  // Furniture & Home — shared keyDetails, overridden per category
  // Health & Beauty — shared keyDetails, overridden per category

  // Property
  keyFeatures:         "Lead with the lifestyle, not the specs. 'Morning sun, pool view, 10 min walk to beach' moves people more than '1,200 sqft south-facing'. Emotion sells property.",
  saleOrRent:          "Make this crystal clear upfront — mixing it up wastes everyone's time and filters the wrong audience.",
  bedrooms:            "Bedroom count is the first thing renters and buyers filter on. Select the right one — it affects who sees your listing.",
  area:                "Mention area in sqft or sqm and note anything that makes the space feel bigger — high ceilings, open plan, storage rooms.",

  // Vehicles
  mileage:             "Mileage is one of the first things buyers check. Low mileage for the year is a major selling point — highlight it and explain why (second car, short commute, etc.).",
  year:                "Year matters for depreciation and insurance. If the car was registered late in the year, mention it — 'Dec 2020 reg' is almost a 2021 car in buyers' minds.",

  // Jobs
  companyName:         "Real company names get significantly more applicants. If you can share it, do — candidates research before applying and a known name builds instant credibility.",
  jobType:             "Be clear upfront. Candidates filter hard on full-time vs contract vs part-time. Ambiguity loses good people before they even read the rest.",
  experienceRequired:  "Be realistic and specific. '3–5 years in B2B sales, UAE experience a plus' reads far better than just '5 years experience required'.",
  responsibilities:    "Write these as achievements, not chores. 'Drive regional growth and lead a team of 5' is more compelling than 'manage team and attend meetings'. What will they actually build?",
  salary:              "Listings with salary ranges get 30–50% more applicants. Even a wide range beats nothing. Include commission, bonus, or benefits if relevant.",
  workStyle:           "Remote and hybrid are major selling points in the UAE right now. If you offer flexibility, make it prominent — it's a competitive advantage over other listings.",

  // Services
  serviceOrRole:       "The more specific, the better. 'Licensed Electrician — Villa and Commercial fit-outs' beats 'Electrician available'. Niche = fewer but much better leads.",
  experienceYears:     "Add context to your years. '7 years — 50+ clients across Dubai and Abu Dhabi' is far more credible than just '7 years experience'.",
  skills:              "This is your trust-builder. Name your tools, techniques, and past results. 'Figma, Adobe Suite, 50+ logo projects, 5-star rated' beats a generic skill list every time.",
  availability:        "Tell them when you can start and how you work. 'Available immediately — weekdays, some weekends, on-site or remote' removes friction and gets replies faster.",
  rate:                "Give a number even if it's a range. 'AED 150–250/hour depending on scope' pre-qualifies serious clients and saves you from timewasters asking 'how much?'",

  // Food
  businessName:        "Your business name builds recognition. If you're a home cook, a name makes you feel like a real business — people remember and recommend names.",
  cuisine:             "Be specific about the food type. 'Homemade Emirati, Yemeni Mandi, Lebanese Mezze' tells people exactly what they're getting and who you're for.",
  offer:               "Make it sensory and practical. 'Slow-cooked lamb Mandi, serves 6–8, delivered hot in a tray, AED 120' creates appetite and answers every question in one line.",
  openingHours:        "For food, delivery times matter as much as hours. 'Orders by 12pm, delivered 4–7pm daily' sets clear expectations and reduces back-and-forth messages.",
  contact:             "WhatsApp numbers get the most responses for food sellers. Make it easy — one tap and they're ordering.",

  // Store product
  productFeatures:     "List the features that directly benefit the buyer, not just specs. 'RFID blocking — your cards are safe' is better than 'RFID technology included'. Benefit-first always wins.",
  targetAudience:      "Who is this for? The clearer you are, the better the copy matches the buyer. 'Professionals aged 25–40 who want a slim wallet' gives the AI much better direction.",
  productTone:         "Tone shapes everything. 'Premium' gives refined, confident language. 'Casual' gives friendly, everyday language. Pick what matches your brand.",
};

// Category-specific tip overrides for shared field names
const FIELD_TIPS_BY_CATEGORY = {
  vehicles: {
    keySpecs: "Cover: engine size, trim level, extras (sunroof, leather, camera), service history, accident history, and reason for selling. These are the 6 things every car buyer wants to know.",
    condition: "UAE car buyers care a lot about accident history. 'No accidents, agency serviced, single owner' are the three most powerful words in a car listing.",
  },
  fashion: {
    keyDetails: "Include exact size details, what comes with it (dustbag, original box, authenticity card, tags), how many times worn, and any flaws. Fashion buyers check everything before they message.",
    condition: "For fashion, be ultra-specific. 'Worn once to a wedding, no marks, no alterations, comes with original dustbag' tells the whole story.",
  },
  furniture: {
    keyDetails: "Include dimensions, material, color, how long you've had it, any marks or wear, and whether delivery/assembly is available. Buyers need to know if it fits before they visit.",
    condition: "For furniture, mention if it's from a pet-free or smoke-free home — buyers with allergies filter on this. Also note any repair history.",
  },
  health: {
    keyDetails: "For beauty tools: include attachments included, how many times used, and if the original box is there. For skincare: expiry date, opened or sealed, how much is left.",
    condition: "Sealed/unopened is a massive trust signal for health and beauty items. If it's sealed, lead with that. If opened, be specific about usage.",
  },
};

// Get tip — category-specific overrides base tip
function getFieldTip(fieldKey, categoryId) {
  return FIELD_TIPS_BY_CATEGORY[categoryId]?.[fieldKey] || FIELD_TIPS[fieldKey] || null;
}

// Quality score: how many of the category's fields are filled
function getQualityScore(fields, catConfig) {
  if (!catConfig?.fields) return { score: 0, filled: 0, total: 0, label: "", color: "" };
  const scoreable = catConfig.fields.filter(f => {
    const cfg = AD_FIELD_CONFIG[f];
    return cfg && !cfg.pillOptions && !cfg.selectOptions; // only text/textarea fields
  });
  const filled = scoreable.filter(f => fields[f]?.trim()).length;
  const total  = scoreable.length;
  const pct    = total === 0 ? 0 : filled / total;

  let label, color;
  if (pct === 0)       { label = "Add details to get started";    color = "#6b7280"; }
  else if (pct < 0.4)  { label = "Basic — add more for better ads"; color = "#ef4444"; }
  else if (pct < 0.7)  { label = "Good — a few more details help";  color = "#f97316"; }
  else if (pct < 1.0)  { label = "Great — almost complete";         color = "#eab308"; }
  else                  { label = "Excellent — ready to generate ✦"; color = "#22c55e"; }

  return { score: pct, filled, total, label, color };
}

function QualityIndicator({ fields, catConfig, t }) {
  const { score, label, color } = getQualityScore(fields, catConfig);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color, transition: "color 0.3s" }}>{label}</span>
        <span style={{ fontSize: 10, color: t.textMuted }}>{Math.round(score * 100)}% complete</span>
      </div>
      <div style={{ height: 4, background: t.card, borderRadius: 4, overflow: "hidden", border: `1px solid ${t.border}` }}>
        <div style={{ height: "100%", width: `${score * 100}%`, background: color, borderRadius: 4, transition: "width 0.4s ease, background 0.3s ease" }} />
      </div>
    </div>
  );
}

function FieldTip({ fieldKey, categoryId, visible, t }) {
  const tip = getFieldTip(fieldKey, categoryId);
  if (!tip || !visible) return null;
  return (
    <div style={{ marginTop: 6, padding: "8px 12px", background: t.accentBg, border: `1px solid ${t.accent}44`, borderRadius: 8, fontSize: 11, color: t.textDim, lineHeight: 1.6, animation: "fadeIn 0.2s ease" }}>
      <span style={{ color: t.accent, fontWeight: 700 }}>💡 </span>{tip}
    </div>
  );
}

function SmartAdsForm({ onResult, loading, setLoading, t, templates, onSaveTemplate, pendingTemplate, onTemplateMounted, isMobile, checkLimit, clerkUserId, onLimitReached, isPro, onShowPlanModal }) {
  const [category, setCategory] = useState(null);
  const [fields, setFields] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [formats, setFormats] = useState(["facebook", "marketplace"]);
  const [tone, setTone] = useState("Energetic");
  const [activeToggles, setActiveToggles] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const setF = k => e => setFields(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (pendingTemplate && pendingTemplate.tool === "ads") {
      const cat = AD_CATEGORIES.find(c => c.id === pendingTemplate.category);
      if (cat) {
        setCategory(cat);
        setFields(pendingTemplate.fields || {});
        setFormats(pendingTemplate.formats || ["facebook", "marketplace"]);
        setTone(pendingTemplate.tone || "Energetic");
        setActiveToggles(pendingTemplate.activeToggles || []);
      }
      if (onTemplateMounted) onTemplateMounted();
    }
  }, [pendingTemplate, onTemplateMounted]);

  function toggleFormat(id) {
    setFormats(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  }
  function toggleBadge(id) {
    setActiveToggles(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  }

  function saveTemplate(name) {
    const preview = fields.title || fields.brand || Object.values(fields).find(Boolean) || "";
    onSaveTemplate({ name, tool: "ads", category: category.id, fields, formats, tone, activeToggles, preview });
    setShowSaveModal(false);
  }

  async function submit() {
    if (checkLimit && !checkLimit()) return;
    setLoading(true);
    onResult(null, {});
    try {
      onResult(await callClaude(buildAdPrompt(category.id, fields, formats, tone, activeToggles), clerkUserId), { category: category.id, ...fields });
    } catch (e) {
      if (e.message === "daily_limit_reached") { if (onLimitReached) onLimitReached(); }
      else onResult({ error: e.message }, {});
    } finally { setLoading(false); }
  }

  const catTemplates = category ? (templates || []).filter(tp => tp.tool === "ads" && tp.category === category.id) : [];

  if (!category) {
    const adTemplates = (templates || []).filter(tp => tp.tool === "ads");
    return (
      <div>
        {adTemplates.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>📋 Your Templates</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {adTemplates.slice(0, 5).map(tp => {
                const cat = AD_CATEGORIES.find(c => c.id === tp.category);
                return (
                  <button key={tp.id} onClick={() => {
                    const c = AD_CATEGORIES.find(c => c.id === tp.category);
                    if (c) { setCategory(c); setFields(tp.fields || {}); setFormats(tp.formats || ["facebook", "marketplace"]); setTone(tp.tone || "Energetic"); setActiveToggles(tp.activeToggles || []); }
                  }}
                    style={{ padding: "7px 13px", borderRadius: 20, border: `1px solid ${t.accent}`, background: t.accentBg, color: t.accentText, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
                    {cat?.icon} {tp.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <p style={{ color: t.textDim, fontSize: 13, marginBottom: 16 }}>Choose a category to get started</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {AD_CATEGORIES.map(cat => (
            <button key={cat.id} onClick={() => { setCategory(cat); setFields({}); setActiveToggles([]); }}
              style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: "14px", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; e.currentTarget.style.background = t.accentBg; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.background = t.card; }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{cat.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{cat.label}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const catConfig = AD_CATEGORIES.find(c => c.id === category.id);
  const requiredFields = catConfig?.fields?.slice(0, 2) || [];
  const hasRequired = requiredFields.every(f => fields[f]);
  const categoryToggles = catConfig?.toggles || [];

  return (
    <div>
      {showSaveModal && <SaveTemplateModal onSave={saveTemplate} onClose={() => setShowSaveModal(false)} t={t} />}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <button onClick={() => setCategory(null)} style={{ background: "transparent", border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, color: t.textMuted, fontFamily: "inherit" }}>← Back</button>
        <div style={{ background: t.accentBg, border: `1px solid ${t.accent}`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600, color: t.accentText }}>{category.icon} {category.label}</div>
        {catTemplates.length > 0 && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            {catTemplates.slice(0, 3).map(tp => (
              <button key={tp.id} onClick={() => { setFields(tp.fields || {}); setFormats(tp.formats || ["facebook","marketplace"]); setTone(tp.tone || "Energetic"); setActiveToggles(tp.activeToggles || []); }}
                style={{ padding: "4px 10px", borderRadius: 20, border: `1px solid ${t.accent}`, background: t.accentBg, color: t.accentText, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit" }}>
                📋 {tp.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, color: t.text, marginBottom: 12 }}>{catConfig?.question || "Tell us the details"}</p>

      <QualityIndicator fields={fields} catConfig={catConfig} t={t} />

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
        {catConfig?.fields?.map((fieldKey, idx) => {
          const cfg = AD_FIELD_CONFIG[fieldKey];
          if (!cfg) return null;
          const isTextarea = cfg.textarea;
          const isSelect = !!cfg.selectOptions;
          const isPills = !!cfg.pillOptions;
          const isRequired = idx < 2;
          const placeholder = catConfig?.placeholders?.[fieldKey] || cfg.placeholder;
          const isFocused = focusedField === fieldKey;
          return (
            <div key={fieldKey} style={{ gridColumn: (isTextarea || isSelect || isPills) ? "1 / -1" : "auto" }}>
              <Field label={`${cfg.label?.replace(" *", "")}${isRequired ? " *" : ""}`} t={t}>
                {isSelect ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    {cfg.selectOptions.map(opt => (
                      <button key={opt.value} onClick={() => setFields(p => ({ ...p, [fieldKey]: opt.value }))}
                        style={{ flex: 1, padding: "10px 8px", borderRadius: 8, border: `1px solid ${(fields[fieldKey] || "medium") === opt.value ? t.accent : t.border}`, background: (fields[fieldKey] || "medium") === opt.value ? t.accentBg : t.card, color: (fields[fieldKey] || "medium") === opt.value ? t.accentText : t.textDim, cursor: "pointer", fontSize: 12, fontWeight: (fields[fieldKey] || "medium") === opt.value ? 700 : 400, transition: "all 0.2s", fontFamily: "inherit", textAlign: "center" }}>
                        <div>{opt.label}</div>
                        <div style={{ fontSize: 10, marginTop: 3, opacity: 0.75, lineHeight: 1.3 }}>{opt.hint}</div>
                      </button>
                    ))}
                  </div>
                ) : isPills ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {cfg.pillOptions.map(opt => (
                      <button key={opt} onClick={() => setFields(p => ({ ...p, [fieldKey]: p[fieldKey] === opt ? "" : opt }))}
                        style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${fields[fieldKey] === opt ? t.accent : t.border}`, background: fields[fieldKey] === opt ? t.accentBg : t.card, color: fields[fieldKey] === opt ? t.accentText : t.textMuted, cursor: "pointer", fontSize: 12, fontWeight: fields[fieldKey] === opt ? 700 : 400, transition: "all 0.15s", fontFamily: "inherit" }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : isTextarea ? (
                  <textarea style={{ ...getInp(t), minHeight: 72, resize: "vertical" }} value={fields[fieldKey] || ""} onChange={setF(fieldKey)} placeholder={placeholder}
                    onFocus={() => setFocusedField(fieldKey)} onBlur={() => setFocusedField(null)} />
                ) : (
                  <input style={getInp(t)} value={fields[fieldKey] || ""} onChange={setF(fieldKey)} placeholder={placeholder}
                    onFocus={() => setFocusedField(fieldKey)} onBlur={() => setFocusedField(null)} />
                )}
                <FieldTip fieldKey={fieldKey} categoryId={category?.id} visible={isFocused} t={t} />
              </Field>
            </div>
          );
        })}
      </div>

      <Field label="Listing Badges" t={t}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {categoryToggles.map(id => <Toggle key={id} id={id} active={activeToggles.includes(id)} onToggle={toggleBadge} t={t} />)}
        </div>
      </Field>

      <Field label="Generate Ads For" t={t}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {AD_FORMATS.map(fmt => {
            const isSelected = formats.includes(fmt.id);
            const isLocked = !isPro && !isSelected && formats.length >= 2;
            return (
              <button key={fmt.id} onClick={() => {
                if (isLocked) { onShowPlanModal(); return; }
                toggleFormat(fmt.id);
              }} title={isLocked ? "Upgrade to Pro to use all formats" : ""} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${isSelected ? t.accent : isLocked ? t.border : t.border}`, background: isSelected ? t.accentBg : t.card, color: isSelected ? t.accentText : isLocked ? t.textMuted : t.textMuted, cursor: isLocked ? "not-allowed" : "pointer", fontSize: 12, fontWeight: isSelected ? 700 : 400, transition: "all 0.15s", fontFamily: "inherit", opacity: isLocked ? 0.45 : 1, position: "relative" }}>
                {fmt.icon} {fmt.label} {isLocked ? "🔒" : ""}
              </button>
            );
          })}
        </div>
        {!isPro && <div style={{ fontSize: 11, color: t.textMuted, marginTop: 6 }}>Free — up to 2 formats · <button onClick={onShowPlanModal} style={{ background: "none", border: "none", color: t.accent, cursor: "pointer", fontSize: 11, fontWeight: 700, padding: 0, fontFamily: "inherit" }}>Go Pro for all 5 ✦</button></div>}
      </Field>

      <Field label="Ad Tone" t={t}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {TONES_AD.map(tn => (
            <button key={tn} onClick={() => setTone(tn)} style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${tone === tn ? t.accent : t.border}`, background: tone === tn ? t.accentBg : t.card, color: tone === tn ? t.accentText : t.textMuted, cursor: "pointer", fontSize: 12, fontWeight: tone === tn ? 700 : 400, transition: "all 0.15s", fontFamily: "inherit" }}>
              {tn}
            </button>
          ))}
        </div>
      </Field>

      <SubmitBtn loading={loading} disabled={!hasRequired || formats.length === 0} onClick={submit} t={t}>
        {loading ? `Writing your ${formats.length} ad${formats.length > 1 ? "s" : ""}...` : `Generate ${formats.length} Ad Format${formats.length > 1 ? "s" : ""}`}
      </SubmitBtn>
      {hasRequired && (
        <button onClick={() => setShowSaveModal(true)} style={{ width: "100%", marginTop: 8, padding: "9px 0", background: "transparent", border: `1px solid ${t.border}`, borderRadius: 10, color: t.textDim, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.03em", transition: "all 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; e.currentTarget.style.color = t.accentText; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.textDim; }}>
          📋 Save as Template
        </button>
      )}
    </div>
  );
}

function StoreResult({ r, t, onFav, isFav }) {
  const [descTab, setDescTab] = useState("descriptionA");
  const [socialTab, setSocialTab] = useState("instagram");
  if (r.error) return <div style={{ background: t.danger, borderRadius: 12, padding: 20 }}><p style={{ color: t.dangerText, margin: 0 }}>⚠ {r.error}</p></div>;

  const desc = r[descTab];
  const social = { instagram: r.social?.instagram, facebook: r.social?.facebook, twitter: r.social?.twitter };

  return (
    <div>
      {/* Headline */}
      <div className="card-in card-in-1" style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.1em" }}>Product Headline</span>
          <div style={{ display: "flex", gap: 6 }}><CopyBtn text={r.headline} t={t} />{onFav && <StarBtn isFav={isFav} onClick={onFav} t={t} />}</div>
        </div>
        <p style={{ fontSize: 22, fontWeight: 700, color: t.text, margin: 0, lineHeight: 1.3 }}>{r.headline}</p>
      </div>

      {/* Description A/B */}
      <div className="card-in card-in-2" style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.1em" }}>Product Description</span>
          <CopyBtn text={desc?.body} t={t} />
        </div>
        <TabToggle options={[{ value: "descriptionA", label: `A — ${r.descriptionA?.label || "Version 1"}` }, { value: "descriptionB", label: `B — ${r.descriptionB?.label || "Version 2"}` }]} value={descTab} onChange={setDescTab} t={t} />
        <p style={{ color: t.textMuted, fontSize: 14, lineHeight: 1.8, margin: 0, whiteSpace: "pre-line" }}>{desc?.body}</p>
      </div>

      {/* Bullet Points */}
      <div className="card-in card-in-3" style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.1em" }}>Key Benefits</span>
          <CopyBtn text={r.bullets?.map(b => `• ${b}`).join("\n")} t={t} />
        </div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {r.bullets?.map((b, i) => <li key={i} style={{ color: t.textMuted, marginBottom: 8, fontSize: 14, lineHeight: 1.6 }}>{b}</li>)}
        </ul>
      </div>

      {/* Social Captions */}
      <div className="card-in card-in-4" style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.1em" }}>Social Media Captions</span>
        </div>
        <TabToggle options={[{ value: "instagram", label: "Instagram" }, { value: "facebook", label: "Facebook" }, { value: "twitter", label: "Twitter / X" }]} value={socialTab} onChange={setSocialTab} t={t} />
        <div style={{ position: "relative" }}>
          <p style={{ color: t.textMuted, fontSize: 13, lineHeight: 1.8, margin: 0, whiteSpace: "pre-line", paddingRight: 70 }}>{social[socialTab]}</p>
          <div style={{ position: "absolute", top: 0, right: 0 }}><CopyBtn text={social[socialTab]} t={t} /></div>
        </div>
      </div>

      {/* SEO */}
      <div className="card-in card-in-5" style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ marginBottom: 14 }}><span style={{ fontSize: 11, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.1em" }}>SEO Tags</span></div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: t.textDim, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>Title</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <span style={{ color: t.accentText, fontSize: 13 }}>{r.seo?.title}</span><CopyBtn text={r.seo?.title} t={t} />
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: t.textDim, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.08em" }}>Meta Description</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <span style={{ color: t.textMuted, fontSize: 13, lineHeight: 1.6 }}>{r.seo?.meta}</span><CopyBtn text={r.seo?.meta} t={t} />
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {r.seo?.keywords?.map((k, i) => <span key={i} style={{ background: t.pillBg, border: `1px solid ${t.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 11, color: t.textDim }}>{k}</span>)}
        </div>
      </div>

      {/* Tip */}
      <div className="card-in card-in-5" style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ marginBottom: 14 }}><span style={{ fontSize: 11, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.1em" }}>💡 Conversion Tip</span></div>
        <p style={{ color: t.accentText, fontSize: 13, margin: 0, lineHeight: 1.7 }}>{r.tip}</p>
      </div>
    </div>
  );
}

function SmartAdsResult({ r, t, onFav, isFav, selectedFormats, isStore }) {
  const [activeFormat, setActiveFormat] = useState(null);

  useEffect(() => {
    if (selectedFormats?.length > 0) setActiveFormat(selectedFormats[0]);
  }, [selectedFormats]);

  if (r.error) return <Card t={t} accent={t.danger}><p style={{ color: t.dangerText, margin: 0 }}>⚠ {r.error}</p></Card>;

  // Store product uses a completely different result layout
  if (isStore || r.headline) return <StoreResult r={r} t={t} onFav={onFav} isFav={isFav} />;

  const availableFormats = AD_FORMATS.filter(f => r[f.id]);

  return (
    <div>
      {/* Format Tabs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
        {availableFormats.map(fmt => (
          <button key={fmt.id} onClick={() => setActiveFormat(fmt.id)} style={{ padding: "8px 16px", borderRadius: 20, border: `1px solid ${activeFormat === fmt.id ? t.accent : t.border}`, background: activeFormat === fmt.id ? t.accentBg : t.card, color: activeFormat === fmt.id ? t.accentText : t.textMuted, cursor: "pointer", fontSize: 12, fontWeight: activeFormat === fmt.id ? 700 : 400, transition: "all 0.15s", fontFamily: "inherit" }}>
            {fmt.icon} {fmt.label}
          </button>
        ))}
      </div>

      {/* Active Format Content */}
      {activeFormat && r[activeFormat] && (
        <Card t={t} animIndex={1}>
          <CardHeader
            title={AD_FORMATS.find(f => f.id === activeFormat)?.label}
            t={t}
            action={
              <div style={{ display: "flex", gap: 6 }}>
                <CopyBtn text={typeof r[activeFormat] === "object" ? JSON.stringify(r[activeFormat]) : r[activeFormat]?.content || r[activeFormat]} t={t} />
                {onFav && <StarBtn isFav={isFav} onClick={onFav} t={t} />}
              </div>
            }
          />
          {activeFormat === "google" ? (
            // Google ads have structured headline/description format
            <div>
              <div style={{ background: t.surface, borderRadius: 8, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: t.textDim, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>Preview</div>
                <div style={{ color: "#4285f4", fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                  {typeof r[activeFormat] === "string" ? r[activeFormat].split("|")[0]?.trim() : "Ad Headline"}
                </div>
                <div style={{ color: t.textMuted, fontSize: 13 }}>
                  {typeof r[activeFormat] === "string" ? r[activeFormat].split("|").slice(3).join(" | ").trim() : ""}
                </div>
              </div>
              <div style={{ whiteSpace: "pre-line", color: t.textMuted, fontSize: 13, lineHeight: 1.8 }}>
                {typeof r[activeFormat] === "object" ? r[activeFormat]?.content : r[activeFormat]}
              </div>
            </div>
          ) : (
            <div style={{ whiteSpace: "pre-line", color: t.textMuted, fontSize: 14, lineHeight: 1.8 }}>
              {typeof r[activeFormat] === "object" ? r[activeFormat]?.content : r[activeFormat]}
            </div>
          )}
        </Card>
      )}

      {/* Pro Tip */}
      {r.tip && (
        <Card t={t} animIndex={2}>
          <CardHeader title="💡 Pro Tip to Sell Faster" t={t} />
          <p style={{ color: t.accentText, fontSize: 13, margin: 0, lineHeight: 1.7 }}>{r.tip}</p>
        </Card>
      )}
    </div>
  );
}

// ── SMART EMAIL FORM ───────────────────────────────────────────────────────
// ── ROTATING EMAIL EXAMPLES ───────────────────────────────────────────────
const EMAIL_EXAMPLES = {
  invoice: [
    { yourName: "Sarah Ahmed", businessName: "Nova Design Studio", clientName: "Gulf Media Ltd.", invoiceNumber: "INV-2026-041", amount: "AED 8,500", dueDate: "Feb 10, 2026", daysOverdue: "8–14 days", invoiceResolution: "Full payment" },
    { yourName: "Omar Hassan", businessName: "Apex Tech Solutions", clientName: "Al Futtaim Group", invoiceNumber: "INV-2026-018", amount: "AED 22,000", dueDate: "Jan 28, 2026", daysOverdue: "15–30 days", invoiceResolution: "Payment plan" },
  ],
  proposal: [
    { yourName: "Layla Karim", businessName: "Brandly Agency", clientName: "Noon.com", proposalSubject: "Social Media Management Package", sentDate: "Feb 1, 2026", proposalValue: "AED 15,000/month" },
    { yourName: "Tariq Al Sayegh", businessName: "PixelForge", clientName: "Emaar Properties", proposalSubject: "Website Redesign & SEO", sentDate: "Feb 5, 2026", proposalValue: "AED 45,000" },
  ],
  cold: [
    { yourName: "Nadia Rashid", businessName: "ContentCraft", recipientName: "Faisal Al Muhairi", recipientCompany: "Majid Al Futtaim", howYouFoundThem: "LinkedIn", yourOffer: "We help retail brands grow their Arabic-language social media presence — typically 3x engagement in 90 days.", desiredAction: "A 20-min discovery call this week" },
    { yourName: "Khalid Mansoor", businessName: "GrowthLab", recipientName: "Sara Bin Laden", recipientCompany: "STC", howYouFoundThem: "Their website", yourOffer: "Performance marketing for telecom brands — we specialize in reducing CAC by 30–40% through data-driven ad campaigns.", desiredAction: "A quick intro call or a Zoom this week" },
  ],
  project: [
    { yourName: "Reem Al Farsi", clientName: "Urban Nest", projectName: "Brand Identity Redesign", updateSummary: "Logo concepts complete — 3 directions ready for review. Color palette finalized. Working on typography system.", nextSteps: "Client review of logo directions by Friday", deadline: "March 10, 2026" },
    { yourName: "Jassim Al Thani", clientName: "Bloom Health", projectName: "E-commerce Website", updateSummary: "Homepage and product pages done. Payment gateway integrated. Currently testing mobile responsiveness.", nextSteps: "Need product photos and final copy for 12 remaining pages", deadline: "Feb 28, 2026" },
  ],
  meeting: [
    { yourName: "Hana Al Blooshi", recipientName: "Mohammed Al Rashidi", meetingPurpose: "Discuss Q1 marketing strategy and budget allocation", suggestedDates: "Mon–Wed next week, mornings preferred", duration: "1 hour", meetingFormat: "Google Meet" },
    { yourName: "Salim Osman", recipientName: "Amira Khalil", meetingPurpose: "Review the new product launch timeline and assign responsibilities", suggestedDates: "Thursday or Friday this week", duration: "45 min", meetingFormat: "In Person" },
  ],
  announcement: [
    { yourName: "Dina Al Sayed", businessName: "Studio Bloom", audience: "All clients and followers", announcementSubject: "New service launch — Brand Photography Packages", keyDetails: "Launching March 1st. Three packages from AED 1,500. Includes concept, shoot, and 30 edited images. Early bird: 20% off for bookings before Feb 28." },
    { yourName: "Faris Khalid", businessName: "Tech Horizons", audience: "Existing clients", announcementSubject: "Office relocation — new Dubai address", keyDetails: "Moving to Business Bay Tower, Level 12, March 15. Parking available. Same phone and email. Grand opening event March 18." },
  ],
  badnews: [
    { yourName: "Mona Al Qassimi", recipientName: "James Harper", projectOrOrder: "Brand guidelines project", whatHappened: "Lead designer had a family emergency and is on leave for 2 weeks", solution: "Reassigning to our senior designer. No compromise on quality.", newTimeline: "Delivery by March 5 instead of Feb 24" },
    { yourName: "Yousef Al Hamdan", recipientName: "Chen Wei", projectOrOrder: "Order #2847 — Custom furniture set", whatHappened: "Supplier shipment delayed at customs due to documentation issue", solution: "Expediting clearance and offering 10% discount on next order", newTimeline: "Expected delivery March 12" },
  ],
  checkin: [
    { yourName: "Aisha Al Nuaimi", recipientName: "Patrick O'Brien", context: "We sent over the revised proposal two weeks ago after your feedback", whatYouNeed: "Your thoughts on the updated pricing and timeline" },
    { yourName: "Bilal Farooq", recipientName: "Priya Sharma", context: "We met at the Dubai Fintech Summit last month and discussed a potential collaboration", whatYouNeed: "Whether you'd be open to a quick call to explore next steps" },
  ],
  apology: [
    { yourName: "Lina Al Zaabi", recipientName: "David Chen", whatWentWrong: "Sent the wrong version of the presentation before the board meeting", impact: "Caused confusion and an awkward moment in front of their executives", remedy: "Sending the correct version now with a summary of all changes highlighted" },
    { yourName: "Rashid Al Mansoori", recipientName: "Sophie Laurent", whatWentWrong: "Missed the agreed deadline by 4 days without giving advance notice", impact: "Delayed their product launch by a week", remedy: "Delivering complete work by tomorrow and offering one free revision cycle" },
  ],
  thankyou: [
    { yourName: "Nour Al Khalidi", recipientName: "Hassan Bin Issa", whatFor: "The warm introduction to the CEO of Emirates NBD", specificDetail: "The meeting led directly to a pilot project worth AED 120,000 — genuinely life-changing for our business" },
    { yourName: "Maya Saleh", recipientName: "Dr. Rania Abukhaled", whatFor: "Your patience and flexibility during our chaotic rebrand process", specificDetail: "The brand guidelines you approved are now being used across 3 countries — we couldn't have done it without your trust" },
  ],
  favor: [
    { yourName: "Karim Benali", recipientName: "Zaid Al Shehhi", favorDetails: "Would love a 20-minute portfolio review and honest feedback before I pitch to ADNOC next week", whyThem: "You've been in the industry 15 years and your eye for design is something I deeply respect", deadline: "By Feb 26 if possible" },
    { yourName: "Sara Al Hosani", recipientName: "Prof. Ibrahim Nasser", favorDetails: "A brief LinkedIn recommendation highlighting our work together on the Dubai Future Foundation project", whyThem: "Your name carries real weight in the innovation space and it would mean a lot coming from you", deadline: "No rush — whenever suits you" },
  ],
  intro: [
    { yourName: "Hamdan Al Rashidi", recipientName: "Chloe Martin", howYouKnowThem: "Connected through our mutual friend Ahmed Al Marzouqi at last month's Gitex", yourBackground: "Co-founder of a UAE-based EdTech startup — we've trained 12,000 students across the GCC", purpose: "Explore a potential content partnership for your European audience" },
    { yourName: "Fatima Zahra", recipientName: "Marco Rossi", howYouKnowThem: "I've been following your sustainability work at GreenBuild for two years — your TED talk changed how I think about urban design", yourBackground: "Architect and urban planner based in Abu Dhabi, currently working on a net-zero community project", purpose: "Discuss potential collaboration or advisory input on our project" },
  ],
  jobapp: [
    { yourName: "Ali Al Marzouqi", role: "Senior UX Designer", company: "Careem", keyStrength: "5 years designing mobile-first products — shipped 3 apps with 500K+ users. Expert in Figma, design systems, and user research. Led the redesign that increased Noon's checkout conversion by 22%.", whyThisRole: "Careem's mission to simplify life in the region aligns perfectly with my values. I've admired the product for years and want to help shape what comes next.", attachmentNote: "Both" },
    { yourName: "Hessa Al Suwaidi", role: "Marketing Manager", company: "Chalhoub Group", keyStrength: "7 years in luxury brand marketing across UAE, KSA, and Kuwait. Managed AED 4M+ campaigns for regional launches. Fluent in Arabic, English, and French.", whyThisRole: "Chalhoub Group is the gold standard for luxury retail in the region. I want to bring my regional expertise to a brand portfolio I genuinely love.", attachmentNote: "CV attached" },
  ],
  ooo: [
    { yourName: "Mariam Al Ketbi", startDate: "Feb 24", endDate: "Mar 3", backDate: "March 4", contactName: "Khalid Hassan", contactEmail: "khalid@company.com" },
    { yourName: "Tariq Saleh", startDate: "Mar 10", endDate: "Mar 17", backDate: "March 18", contactName: "Aisha Mohammed", contactEmail: "aisha@company.com" },
  ],
  clarify: [
    { yourName: "Noura Al Dhaheri", recipientName: "James Wilson", topic: "Payment terms in the new contract", whatIsUnclear: "Whether the 30-day period starts from invoice date or delivery date", whyItMatters: "Affects our cash flow planning for Q2 significantly" },
    { yourName: "Omar Abdulla", recipientName: "Lisa Park", topic: "Scope of the social media retainer", whatIsUnclear: "Whether Instagram Reels are included in the base package or billed separately", whyItMatters: "We need to finalize our content calendar by end of week" },
  ],
  reply: [
    { yourName: "Layla Karim", recipientName: "Ahmed Al Suwaidi", receivedEmail: "Hi Layla, following up on the social media proposal we discussed. We are interested but the monthly retainer feels a bit high for our current budget. Is there any flexibility on the pricing or a smaller package we could start with? Best, Ahmed", replyIntent: "Yes, we can offer a starter package at a lower price to get them started", replyTone: "Negotiate terms", recipientRelationship: "New Prospect" },
    { yourName: "Faris Al Otaibi", recipientName: "Dana Khalil", receivedEmail: "Hi Faris, we have reviewed your application for the Brand Manager position and would love to schedule an interview. Are you available next week? We are flexible on timing. Regards, Dana - HR Manager", replyIntent: "Confirm availability and express enthusiasm for the opportunity", replyTone: "Agree & confirm", recipientRelationship: "New Prospect" },
  ],
  reminder: [
    { yourName: "Salma Al Rashidi", recipientName: "Tom Harrison", reminderSubject: "Outstanding invoice INV-2026-033 — AED 12,500", reminderDeadline: "This Friday, March 6", reminderContext: "Second reminder — first sent two weeks ago", urgencyLevel: "Moderate urgency", recipientRelationship: "Existing Client" },
    { yourName: "Jassim Al Thani", recipientName: "Priya Mehta", reminderSubject: "Pending approval on homepage design mockup", reminderDeadline: "Thursday EOD", reminderContext: "Design team is on standby — cannot begin development without sign-off", urgencyLevel: "Urgent — action needed today", recipientRelationship: "Existing Client" },
  ],
  recap: [
    { yourName: "Leila Al Marri", clientName: "Horizon Ventures", meetingTopic: "Q1 Campaign Strategy & Budget Review", attendees: "Leila, Ahmed (Creative Lead), Sara (Client)", "decisionsМade": "Budget approved at AED 120,000. Launch date confirmed for March 15. Instagram and Google Ads as primary channels. Weekly reporting every Monday.", actionItems: "Leila: Send media plan by Friday. Ahmed: Deliver first creative concepts by March 3. Sara: Confirm brand assets by Feb 28." },
    { yourName: "Faisal Al Kuwaiti", clientName: "Pearl Real Estate", meetingTopic: "Website Redesign Kickoff", attendees: "Faisal, Reem (Designer), Tom (Client PM)", "decisionsМade": "5-page website scope agreed. Homepage wireframe to be reviewed first. No e-commerce needed for phase 1. Go-live target: April 1.", actionItems: "Faisal: Share project timeline by Monday. Reem: Submit homepage wireframe by March 5. Tom: Provide brand guidelines and logo files by Friday." },
  ],
  pricing: [
    { yourName: "Dana Al Sayed", businessName: "Bloom Creative", clientName: "Spinneys UAE", whatYouOffer: "Monthly social media management — content creation, scheduling, community management, and monthly performance reports", pricingStructure: "Monthly retainer", priceRange: "AED 8,000–12,000/month", whatsIncluded: "20 posts/month (feed + stories), 2 reels, community management, monthly analytics report, one strategy session", nextStepCTA: "A 30-minute call to walk through the packages and find the right fit" },
    { yourName: "Saud Al Harbi", businessName: "CodeNest", clientName: "Al Baraka Group", whatYouOffer: "Custom CRM integration connecting their existing Salesforce with their new e-commerce platform", pricingStructure: "Project-based", priceRange: "AED 35,000–45,000", whatsIncluded: "Discovery workshop, full API integration, data migration, UAT testing, 3 months post-launch support", nextStepCTA: "Review the detailed scope document and schedule a technical call with our lead developer" },
  ],
  approval: [
    { yourName: "Hind Al Mazrouei", recipientName: "Khalifa Al Suwaidi", whatNeedsApproval: "Final homepage design mockup before we hand off to development", approvalDeadline: "Thursday EOD", delayImpact: "Will push the development start date by one week and risk the April 1 launch" },
    { yourName: "Yasir Al Obeidi", recipientName: "Christine Müller", whatNeedsApproval: "The revised contract terms with the updated payment schedule and new deliverable milestones", approvalDeadline: "By end of next week", delayImpact: "Onboarding cannot begin until the contract is signed — team is on standby" },
  ],
  objection: [
    { yourName: "Raneem Al Otaibi", recipientName: "Mark Thompson", objectionType: "Too expensive", objectionContext: "They said the AED 15,000/month retainer is above their current budget for Q1", offerFlexibility: "Yes — smaller plan", recipientRelationship: "New Prospect" },
    { yourName: "Joud Al Rashidi", recipientName: "Priya Mehta", objectionType: "Using a competitor", objectionContext: "They mentioned they just renewed with another agency 3 months ago but were not fully satisfied", offerFlexibility: "Yes — trial offer", recipientRelationship: "New Prospect" },
  ],
  support: [
    { yourName: "Tariq Al Ameri", businessName: "CloudBase Solutions", clientName: "Desert Rose Hotels", issuesummary: "Dashboard not loading after the latest system update — user sees a blank screen after login", ticketNumber: "#TKT-2026-0847", expectedResponse: "Within a few hours" },
    { yourName: "Nadia Khalil", businessName: "PayLink UAE", clientName: "Mina Fashion", issuesummary: "Payment gateway throwing a 502 error on checkout — customers unable to complete purchases since this morning", ticketNumber: "#TKT-2026-1123", expectedResponse: "Within 24 hours" },
  ],
  refund: [
    { yourName: "Mazen Al Harthy", businessName: "NovaMed Supplies", clientName: "Sheikh Khalifa Medical City", refundReason: "Product arrived with damaged packaging and two units were non-functional on arrival", refundStance: "Full refund approved", refundTimeline: "3-5 business days", recipientRelationship: "Existing Client" },
    { yourName: "Lara Bishara", businessName: "Studio Lux", clientName: "Fatima Al Hashimi", refundReason: "Client says the final logo designs did not match the style brief agreed in writing at the start of the project", refundStance: "Partial refund", refundTimeline: "5 business days", recipientRelationship: "Existing Client" },
  ],
};

// ── EMAIL FIELD TIPS ───────────────────────────────────────────────────────
const EMAIL_FIELD_TIPS = {
  yourName:           "Use your full name — it builds trust and makes the email feel personal rather than automated.",
  businessName:       "Your business name anchors credibility. If it's well-known, it does half the selling for you.",
  clientName:         "Use their actual company name, not a generic 'Client'. It shows you did your homework.",
  recipientName:      "Use their first name only in the email — it's warmer and gets more replies than full names.",
  recipientRelationship: "This shapes formality and assumptions. A new prospect gets a different tone than a longtime client.",
  invoiceNumber:      "Always include the exact invoice number — it helps their accounts team locate it instantly without back-and-forth.",
  amount:             "Be specific with currency and amount. 'AED 8,500' gets actioned faster than 'the outstanding balance'.",
  dueDate:            "Reference the exact due date — it makes your follow-up factual, not emotional.",
  daysOverdue:        "The longer it's overdue, the firmer the tone should be. This field calibrates the urgency of the entire email.",
  invoiceResolution:  "Knowing your preferred resolution lets the AI offer the right path forward — a plan vs full payment changes the email entirely.",
  invoiceEscalation:  "This is your final lever. 'Final warning before legal' signals serious intent — only use it when you mean it.",
  proposalSubject:    "Name the proposal specifically — 'Website Redesign Package' is more compelling than just 'our proposal'.",
  sentDate:           "Mentioning when you sent it shows you're organized and makes the follow-up feel timely.",
  proposalValue:      "Including the value reminds them what's on the table — and signals you're confident about your pricing.",
  proposalConcerns:   "If you sense objections (price, timeline), name them here so the AI can address them proactively in the email.",
  yourOffer:          "Lead with the outcome, not the service. 'We help retail brands grow Arabic social media 3x in 90 days' beats 'we do social media management'.",
  desiredAction:      "One clear ask only. '15-min call' is easier to say yes to than 'let's explore opportunities together'.",
  howYouFoundThem:    "A specific source (referral, LinkedIn) makes the outreach feel targeted, not spammy.",
  referralName:       "Drop the mutual contact's name early — it's the single biggest trust booster in cold outreach.",
  projectName:        "Name the project specifically so they can place the email immediately without having to think.",
  updateSummary:      "Be specific about what's done. 'Logo concepts complete, 3 directions ready' is more reassuring than 'making good progress'.",
  nextSteps:          "Name who does what by when. Clear next steps prevent the 'what happens now?' reply.",
  deadline:           "Always include the deadline — it creates shared urgency and keeps both sides accountable.",
  meetingPurpose:     "One clear purpose. 'Review Q2 budget and assign owners' is better than 'touch base on a few things'.",
  suggestedDates:     "Offer a range, not a single date — it gives them flexibility and dramatically increases the chance of a yes.",
  duration:           "Matching duration to purpose shows respect for their time. A 15-min check-in vs a 1-hour strategy session signals very different intent.",
  meetingFormat:      "Format matters — in-person signals importance, Zoom signals efficiency. Match it to the relationship and purpose.",
  audience:           "Define your audience — 'all clients' vs 'existing subscribers' changes the tone and assumptions in the email.",
  announcementSubject:"Lead with the news, not the label. 'New Photography Packages Launching March 1' beats 'Important Announcement'.",
  projectOrOrder:     "Name the project or order number specifically — vague references slow everything down.",
  whatHappened:       "Be honest and direct. Don't bury the bad news — the AI will frame it professionally but needs the real story.",
  solution:           "The solution is the most important part. The more specific and concrete, the more trust you rebuild.",
  newTimeline:        "Give an exact new date, not 'soon' or 'as quickly as possible'. Specificity restores confidence.",
  context:            "Give enough background so they can recall the conversation — one clear sentence is enough.",
  whatYouNeed:        "One specific ask. 'Feedback on the updated pricing' is clear. 'Thoughts on everything' is not.",
  whatWentWrong:      "Own it completely — the AI will write it professionally, but it needs the honest version from you.",
  impact:             "Acknowledging the impact on them shows empathy and makes the apology feel genuine, not defensive.",
  remedy:             "What you're doing to fix it matters more than the apology itself. Be concrete about your solution.",
  whatFor:            "Specificity makes thank you emails memorable. 'The client referral last Tuesday' beats a generic 'your support'.",
  specificDetail:     "This is what separates a genuine thank you from a template. One real detail makes it land.",
  favorDetails:       "Be clear and specific about what you're asking. Vague favors are easy to ignore — concrete asks get responses.",
  whyThem:            "Flattery works when it's specific and true. Tell them exactly why you're asking them, not someone else.",
  howYouKnowThem:     "The connection context sets the tone for the whole email. Be honest and direct about how you met.",
  yourBackground:     "Keep it to one sentence — the most relevant credential for this specific introduction.",
  purpose:            "One clear purpose for the introduction. What do you both stand to gain?",
  role:               "Use the exact job title from the listing — it shows you read it carefully.",
  company:            "Research the company and mention something specific in your email — it's the difference between a form letter and a real application.",
  keyStrength:        "Your strongest relevant achievement in one sentence. Numbers beat adjectives every time.",
  whyThisRole:        "Be honest about why this role excites you specifically. Generic passion statements get ignored.",
  meetingTopic:       "Name the meeting exactly as it was — helps recipients recall it and sets context for the recap.",
  decisionsМade:      "Document decisions clearly — this is the most important part of a recap for accountability.",
  actionItems:        "Name + task + deadline for each item. Ambiguous action items are action items that never get done.",
  whatYouOffer:       "Lead with the outcome you deliver, not a list of services. What will their world look like after working with you?",
  priceRange:         "Be specific — a range like 'AED 3,500–5,000/month' is more credible than 'competitive pricing'.",
  whatsIncluded:      "List exactly what's in scope. The clearer this is, the fewer 'what does that include?' replies you'll get.",
  nextStepCTA:        "One clear next step only. 'Book a 30-min call at [link]' converts better than 'let me know if you have questions'.",
  whatNeedsApproval:  "Name it specifically — 'the homepage design mockup' not 'the design files'.",
  approvalDeadline:   "Always include a deadline for approval. Open-ended requests sit in inboxes forever.",
  delayImpact:        "If there's a real downstream impact, say so — it creates urgency without pressure.",
  objectionContext:   "Give the exact objection in their words if you can — the more specific, the better the reframe.",
  issuesummary:       "Describe the issue clearly in one sentence — be specific enough that they know you actually read their message.",
  ticketNumber:       "Always include a ticket number — it makes the support feel organized and easy to reference.",
  expectedResponse:   "Set a specific timeframe — 'within 24 hours' builds more trust than 'as soon as possible'.",
  receivedEmail:      "Paste their exact email — the AI reads it carefully and matches tone, addresses all points, and avoids repeating what they already know.",
  replyIntent:        "Tell the AI what position you want to take. One clear sentence. This is the most important field for a great reply.",
  replyTone:          "Your reply stance shapes everything — agreeing vs negotiating vs pushing back produce completely different emails.",
  reminderSubject:    "Be specific — 'Invoice INV-2026-033' gets actioned faster than 'the pending invoice'.",
  reminderDeadline:   "Always include the exact date or day — 'by Thursday' is more urgent than 'soon'.",
  reminderContext:    "Context like 'second reminder' or 'team on standby' increases urgency without sounding aggressive.",
  urgencyLevel:       "Match urgency to reality — over-escalating loses credibility, under-escalating gets ignored.",
  refundReason:       "Understanding their reason lets the AI acknowledge it properly — the acknowledgment often matters as much as the decision.",
  refundTimeline:     "Give a specific timeline — 'within 5 business days' is more reassuring than 'shortly'.",
};

// Email quality score based on filled text fields (excluding pill/toggle fields)
function getEmailQualityScore(fields, typeConfig) {
  if (!typeConfig?.fields) return { score: 0, label: "", color: "" };
  const scoreable = typeConfig.fields.filter(f => {
    const cfg = EMAIL_FIELD_CONFIG[f];
    return cfg && !cfg.pillOptions;
  });
  const filled = scoreable.filter(f => fields[f]?.trim()).length;
  const total  = scoreable.length;
  const pct    = total === 0 ? 0 : filled / total;

  let label, color;
  if (pct === 0)       { label = "Add details to get started";      color = "#6b7280"; }
  else if (pct < 0.4)  { label = "Basic — add more for better emails"; color = "#ef4444"; }
  else if (pct < 0.7)  { label = "Good — a few more details help";    color = "#f97316"; }
  else if (pct < 1.0)  { label = "Great — almost complete";           color = "#eab308"; }
  else                  { label = "Excellent — ready to generate ✦"; color = "#22c55e"; }

  return { score: pct, label, color };
}

function EmailQualityIndicator({ fields, typeConfig, t }) {
  const { score, label, color } = getEmailQualityScore(fields, typeConfig);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color, transition: "color 0.3s" }}>{label}</span>
        <span style={{ fontSize: 10, color: t.textMuted }}>{Math.round(score * 100)}% complete</span>
      </div>
      <div style={{ height: 4, background: t.card, borderRadius: 4, overflow: "hidden", border: `1px solid ${t.border}` }}>
        <div style={{ height: "100%", width: `${score * 100}%`, background: color, borderRadius: 4, transition: "width 0.4s ease, background 0.3s ease" }} />
      </div>
    </div>
  );
}

function EmailFieldTip({ fieldKey, visible, t }) {
  const tip = EMAIL_FIELD_TIPS[fieldKey];
  if (!tip || !visible) return null;
  return (
    <div style={{ marginTop: 6, padding: "8px 12px", background: t.accentBg, border: `1px solid ${t.accent}44`, borderRadius: 8, fontSize: 11, color: t.textDim, lineHeight: 1.6, animation: "fadeIn 0.2s ease" }}>
      <span style={{ color: t.accent, fontWeight: 700 }}>💡 </span>{tip}
    </div>
  );
}

function SmartEmailForm({ onResult, loading, setLoading, t, templates, onSaveTemplate, pendingTemplate, onTemplateMounted, isMobile, checkLimit, clerkUserId, onLimitReached }) {
  const [selectedType, setSelectedType] = useState(null);
  const [fields, setFields] = useState({});
  const [tone, setTone] = useState("Professional");
  const [search, setSearch] = useState("");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [exampleIdx, setExampleIdx] = useState(0);
  const [focusedField, setFocusedField] = useState(null);
  const [emailLength, setEmailLength] = useState("standard");
  const inp = getInp(t);
  const setF = k => e => setFields(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (pendingTemplate && pendingTemplate.tool === "email") {
      const et = ALL_EMAIL_TYPES.find(e => e.id === pendingTemplate.category);
      if (et) {
        setSelectedType(et);
        setFields(pendingTemplate.fields || {});
        setTone(pendingTemplate.tone || "Professional");
      }
      if (onTemplateMounted) onTemplateMounted();
    }
  }, [pendingTemplate, onTemplateMounted]);

  function saveTemplate(name) {
    const preview = fields.clientName || fields.recipientName || fields.company || Object.values(fields).find(Boolean) || "";
    onSaveTemplate({ name, tool: "email", category: selectedType.id, fields, tone, preview });
    setShowSaveModal(false);
  }

  async function submit() {
    if (checkLimit && !checkLimit()) return;
    setLoading(true);
    onResult(null, {});
    try {
      onResult(await callClaude(buildEmailPrompt(selectedType.id, { ...fields, emailLength }, tone), clerkUserId), { type: selectedType.id, ...fields });
    } catch (e) {
      if (e.message === "daily_limit_reached") { if (onLimitReached) onLimitReached(); }
      else onResult({ error: e.message }, {});
    } finally { setLoading(false); }
  }

  const filtered = search.trim()
    ? ALL_EMAIL_TYPES.filter(t => {
        const q = search.toLowerCase();
        return t.label.toLowerCase().includes(q) || (t.tags || []).some(tag => tag.includes(q));
      })
    : null;
  const emailTemplates = (templates || []).filter(tp => tp.tool === "email");

  if (!selectedType) {
    return (
      <div>
        {emailTemplates.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>📋 Your Templates</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {emailTemplates.slice(0, 5).map(tp => {
                const et = ALL_EMAIL_TYPES.find(e => e.id === tp.category);
                return (
                  <button key={tp.id} onClick={() => { setSelectedType(et || { id: tp.category, label: tp.category, icon: "✉️" }); setFields(tp.fields || {}); setTone(tp.tone || "Professional"); }}
                    style={{ padding: "7px 13px", borderRadius: 20, border: `1px solid ${t.accent}`, background: t.accentBg, color: t.accentText, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
                    {et?.icon} {tp.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ position: "relative", marginBottom: 16 }}>
          <input style={{ ...inp, paddingRight: search ? 36 : 14 }} placeholder="🔍  Search — try 'payment', 'delay', 'quote'..." value={search} onChange={e => setSearch(e.target.value)} />
          {search && (
            <button onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: t.textDim, cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
          )}
        </div>
        {(filtered ? [{ label: "Results", types: filtered }] : EMAIL_CATEGORIES).map(cat => (
          <div key={cat.label} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textDim, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>{cat.label}</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr", gap: 8 }}>
              {cat.types.map(type => (
                <button key={type.id} onClick={() => { setSelectedType(type); setFields({}); }} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: "12px 14px", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; e.currentTarget.style.background = t.accentBg; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.background = t.card; }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{type.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: t.text }}>{type.label}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
        {filtered && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "32px 0", color: t.textDim, fontSize: 13 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🤔</div>
            <div>No match for "<strong style={{ color: t.textMuted }}>{search}</strong>"</div>
            <div style={{ marginTop: 4, fontSize: 12 }}>Try: payment, delay, quote, sorry, follow up...</div>
          </div>
        )}
      </div>
    );
  }

  const typeConfig = ALL_EMAIL_TYPES.find(t => t.id === selectedType.id);
  const requiredFields = typeConfig?.fields?.slice(0, 2) || [];
  const hasRequired = requiredFields.every(f => fields[f]);
  const typeTemplates = (templates || []).filter(tp => tp.tool === "email" && tp.category === selectedType.id);
  const conditionalFields = getConditionalFields(selectedType.id, fields);
  const allFields = [...(typeConfig?.fields || []), ...conditionalFields];

  function renderEmailField(fieldKey, idx) {
    const cfg = EMAIL_FIELD_CONFIG[fieldKey];
    if (!cfg) return null;
    const isTextarea = cfg.textarea;
    const isPills = !!cfg.pillOptions;
    const isRequired = idx < 2;
    const isConditional = conditionalFields.includes(fieldKey);
    const isFocused = focusedField === fieldKey;
    return (
      <div key={fieldKey} style={{ gridColumn: (isTextarea || isPills) ? "1 / -1" : "auto", ...(isConditional ? { animation: "cardIn 0.3s ease forwards" } : {}) }}>
        <Field label={`${cfg.label}${isRequired ? " *" : ""}`} t={t}>
          {isPills ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {cfg.pillOptions.map(opt => (
                <button key={opt} onClick={() => setFields(p => ({ ...p, [fieldKey]: p[fieldKey] === opt ? "" : opt }))}
                  style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${fields[fieldKey] === opt ? t.accent : t.border}`, background: fields[fieldKey] === opt ? t.accentBg : t.card, color: fields[fieldKey] === opt ? t.accentText : t.textMuted, cursor: "pointer", fontSize: 12, fontWeight: fields[fieldKey] === opt ? 700 : 400, transition: "all 0.15s", fontFamily: "inherit" }}>
                  {opt}
                </button>
              ))}
            </div>
          ) : isTextarea ? (
            <textarea style={{ ...getInp(t), minHeight: 72, resize: "vertical" }} value={fields[fieldKey] || ""} onChange={setF(fieldKey)} placeholder={cfg.placeholder}
              onFocus={() => setFocusedField(fieldKey)} onBlur={() => setFocusedField(null)} />
          ) : (
            <input style={getInp(t)} value={fields[fieldKey] || ""} onChange={setF(fieldKey)} placeholder={cfg.placeholder}
              onFocus={() => setFocusedField(fieldKey)} onBlur={() => setFocusedField(null)} />
          )}
          <EmailFieldTip fieldKey={fieldKey} visible={isFocused} t={t} />
        </Field>
      </div>
    );
  }

  function fillExample() {
    const examples = EMAIL_EXAMPLES[selectedType.id] || [];
    if (!examples.length) return;
    const next = (exampleIdx + 1) % examples.length;
    setExampleIdx(next);
    setFields(examples[next]);
  }

  return (
    <div>
      {showSaveModal && <SaveTemplateModal onSave={saveTemplate} onClose={() => setShowSaveModal(false)} t={t} />}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={() => setSelectedType(null)} style={{ background: "transparent", border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, color: t.textMuted, fontFamily: "inherit" }}>← Back</button>
        <div style={{ background: t.accentBg, border: `1px solid ${t.accent}`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600, color: t.accentText }}>{selectedType.icon} {selectedType.label}</div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {EMAIL_EXAMPLES[selectedType.id]?.length > 0 && (
            <button onClick={fillExample} style={{ padding: "4px 10px", borderRadius: 20, border: `1px solid ${t.border}`, background: t.card, color: t.textDim, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit" }}>
              ✦ Try example
            </button>
          )}
          {typeTemplates.slice(0, 3).map(tp => (
            <button key={tp.id} onClick={() => { setFields(tp.fields || {}); setTone(tp.tone || "Professional"); }}
              style={{ padding: "4px 10px", borderRadius: 20, border: `1px solid ${t.accent}`, background: t.accentBg, color: t.accentText, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit" }}>
              📋 {tp.name}
            </button>
          ))}
        </div>
      </div>
      <EmailQualityIndicator fields={fields} typeConfig={typeConfig} t={t} />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
        {allFields.map((fieldKey, idx) => renderEmailField(fieldKey, idx))}
      </div>
      <Field label="Email Length" t={t}>
        <div style={{ display: "flex", gap: 6 }}>
          {EMAIL_LENGTHS.map(l => (
            <button key={l.value} onClick={() => setEmailLength(l.value)}
              style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${emailLength === l.value ? t.accent : t.border}`, background: emailLength === l.value ? t.accentBg : t.card, color: emailLength === l.value ? t.accentText : t.textMuted, cursor: "pointer", fontSize: 12, fontWeight: emailLength === l.value ? 700 : 400, transition: "all 0.15s", fontFamily: "inherit", textAlign: "center" }}>
              <div>{l.label}</div>
              <div style={{ fontSize: 10, opacity: 0.7, marginTop: 2 }}>{l.hint}</div>
            </button>
          ))}
        </div>
      </Field>
      <Field label="Email Tone" t={t}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {TONES_EMAIL.map(tn => (
            <button key={tn} onClick={() => setTone(tn)} style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${tone === tn ? t.accent : t.border}`, background: tone === tn ? t.accentBg : t.card, color: tone === tn ? t.accentText : t.textMuted, cursor: "pointer", fontSize: 12, fontWeight: tone === tn ? 700 : 400, transition: "all 0.15s", fontFamily: "inherit" }}>
              {tn}
            </button>
          ))}
        </div>
        {TONE_PREVIEWS[tone] && (
          <div style={{ marginTop: 8, padding: "8px 12px", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 11, color: t.textDim, lineHeight: 1.6, fontStyle: "italic", animation: "fadeIn 0.2s ease" }}>
            <span style={{ color: t.accent, fontStyle: "normal", fontWeight: 700 }}>Preview: </span>{TONE_PREVIEWS[tone]}
          </div>
        )}
      </Field>
      <SubmitBtn loading={loading} disabled={!hasRequired} onClick={submit} t={t}>
        {loading ? `✦ Writing your ${selectedType.label}...` : `✦ Generate ${selectedType.label}`}
      </SubmitBtn>
      {hasRequired && (
        <button onClick={() => setShowSaveModal(true)} style={{ width: "100%", marginTop: 8, padding: "9px 0", background: "transparent", border: `1px solid ${t.border}`, borderRadius: 10, color: t.textDim, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.03em", transition: "all 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = t.accent; e.currentTarget.style.color = t.accentText; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.textDim; }}>
          📋 Save as Template
        </button>
      )}
    </div>
  );
}

// ── EMAIL RESULT ───────────────────────────────────────────────────────────
function EmailResult({ r, t, onFav, isFav, isPro, onShowPlanModal, emailInput, clerkUserId }) {
  const [tab, setTab] = useState("versionA");
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedBody, setEditedBody] = useState({});
  const [vBData, setVBData] = useState(r.versionB ? { versionB: r.versionB, followUp: r.followUp } : null);
  const [vBLoading, setVBLoading] = useState(false);

  // Merge lazy-loaded versionB into result
  const merged = { ...r, ...(vBData || {}) };

  async function loadVersionB() {
    if (vBData || vBLoading || !isPro) return;
    setVBLoading(true);
    try {
      const res = await callClaude(
        buildEmailPromptB(emailInput.type, emailInput, emailInput.tone || "Professional", r.versionA?.body),
        clerkUserId
      );
      setVBData({ versionB: res.versionB, followUp: res.followUp });
    } catch (e) {
      setVBData({ versionB: { label: "Version B", body: "Could not load — please try again." }, followUp: null });
    }
    setVBLoading(false);
  }

  if (r.error) return <Card t={t} accent={t.danger}><p style={{ color: t.dangerText, margin: 0 }}>⚠ {r.error}</p></Card>;
  const v = merged[tab];
  const bodyText = editedBody[tab] ?? v?.body ?? "";
  const wordCount = bodyText.trim().split(/\s+/).filter(Boolean).length;
  const charCount = bodyText.length;

  function startEdit() { setEditedBody(p => ({ ...p, [tab]: bodyText })); setEditing(true); }
  function stopEdit() { setEditing(false); }

  return (
    <div>
      {/* Version toggle */}
      <TabToggle options={[{ value: "versionA", label: `A — ${merged.versionA?.label || "Version 1"}` }, { value: "versionB", label: `B — ${merged.versionB?.label || "Version 2"}${!isPro ? " 🔒" : ""}` }]} value={tab} onChange={(newTab) => { if (newTab === "versionB" && !isPro) { onShowPlanModal(); return; } setTab(newTab); setEditing(false); if (newTab === "versionB") loadVersionB(); }} t={t} />

      {/* Subject Lines — individual copy per line */}
      {r.subjects?.length > 0 && (
        <Card t={t} animIndex={1}>
          <CardHeader title="✉️ Subject Lines" badge="Pick one" t={t} action={<CopyBtn text={r.subjects?.join("\n")} label="Copy all" t={t} />} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {r.subjects.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, padding: "10px 14px", gap: 10 }}>
                <span style={{ color: t.text, fontSize: 13, flex: 1, lineHeight: 1.5 }}>{s}</span>
                <CopyBtn text={s} t={t} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Version B loading spinner */}
      {tab === "versionB" && vBLoading && (
        <Card t={t} animIndex={2}>
          <div style={{ textAlign: "center", padding: "24px 0", color: t.textMuted, fontSize: 13 }}>
            ⏳ Generating Version B...
          </div>
        </Card>
      )}

      {/* Email Body with word count + inline edit */}
      {!(tab === "versionB" && vBLoading) && <Card t={t} animIndex={2}>
        <CardHeader title="Email Body" action={
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 10, color: t.textDim, whiteSpace: "nowrap" }}>{wordCount}w · {charCount}c</span>
            <button onClick={editing ? stopEdit : startEdit}
              style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${editing ? t.accent : t.border}`, background: editing ? t.accentBg : t.card, color: editing ? t.accentText : t.textMuted, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit", transition: "all 0.15s" }}>
              {editing ? "✓ Done" : "✏ Edit"}
            </button>
            <CopyBtn text={bodyText} label="Copy" t={t} />
            {onFav && <StarBtn isFav={isFav} onClick={onFav} t={t} />}
          </div>
        } t={t} />
        {editing ? (
          <textarea
            value={bodyText}
            onChange={e => setEditedBody(p => ({ ...p, [tab]: e.target.value }))}
            style={{ width: "100%", minHeight: 280, background: t.surface, border: `1px solid ${t.accent}`, borderRadius: 8, color: t.text, fontSize: 13, lineHeight: 1.9, padding: "12px 14px", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
            autoFocus
          />
        ) : (
          <div style={{ whiteSpace: "pre-line", color: t.textMuted, fontSize: 13, lineHeight: 1.9 }}>{bodyText}</div>
        )}
      </Card>}

      {/* WhatsApp — Pro only */}
      {isPro ? (
        <Card t={t} animIndex={3}>
          <CardHeader title="💬 WhatsApp Version" badge="Quick send" action={<CopyBtn text={merged.whatsapp} t={t} />} t={t} />
          <p style={{ color: t.textMuted, fontSize: 13, lineHeight: 1.7, margin: 0, fontStyle: "italic" }}>{merged.whatsapp}</p>
        </Card>
      ) : (
        <Card t={t} animIndex={3}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: t.textMuted }}>💬 WhatsApp Version</span>
            <button onClick={onShowPlanModal} style={{ background: t.accentBg, border: `1px solid ${t.accent}`, borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: t.accentText, cursor: "pointer", fontFamily: "inherit" }}>✦ Pro only</button>
          </div>
          <div style={{ marginTop: 10, filter: "blur(4px)", userSelect: "none", pointerEvents: "none", color: t.textMuted, fontSize: 13, lineHeight: 1.7, fontStyle: "italic" }}>
            Hi, just following up on my earlier message about the proposal. Would love to connect this week — does Thursday work for a quick call?
          </div>
        </Card>
      )}

      {/* Follow-up — Pro only */}
      {merged.followUp && (
        isPro ? (
          <Card t={t} animIndex={4}>
            <CardHeader title="🔁 48h Follow-Up" badge="If no reply" action={
              <div style={{ display: "flex", gap: 6 }}>
                {showFollowUp && <CopyBtn text={merged.followUp} t={t} />}
                <button onClick={() => setShowFollowUp(v => !v)} style={{ background: showFollowUp ? t.accentBg : t.card, color: showFollowUp ? t.accentText : t.textMuted, border: `1px solid ${showFollowUp ? t.accent : t.border}`, borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
                  {showFollowUp ? "Hide" : "Show"}
                </button>
              </div>
            } t={t} />
            {showFollowUp && (
              <div style={{ whiteSpace: "pre-line", color: t.textMuted, fontSize: 13, lineHeight: 1.9, marginTop: 8 }}>{merged.followUp}</div>
            )}
          </Card>
        ) : (
          <Card t={t} animIndex={4}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: t.textMuted }}>🔁 48h Follow-Up</span>
              <button onClick={onShowPlanModal} style={{ background: t.accentBg, border: `1px solid ${t.accent}`, borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: t.accentText, cursor: "pointer", fontFamily: "inherit" }}>✦ Pro only</button>
            </div>
            <div style={{ marginTop: 10, filter: "blur(4px)", userSelect: "none", pointerEvents: "none", color: t.textMuted, fontSize: 13, lineHeight: 1.7 }}>
              Just wanted to follow up on my previous email. I understand you're busy — happy to jump on a quick 10-minute call at your convenience.
            </div>
          </Card>
        )
      )}

      {/* Pro Tip */}
      <Card t={t} animIndex={5}>
        <CardHeader title="💡 Pro Tip" t={t} />
        <p style={{ color: t.accentText, fontSize: 13, margin: 0, lineHeight: 1.7 }}>{merged.tip}</p>
      </Card>
    </div>
  );
}

// ── TEMPLATES ─────────────────────────────────────────────────────────────

function SaveTemplateModal({ onSave, onClose, t }) {
  const [name, setName] = useState("");
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200 }} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 28, zIndex: 201, width: "min(340px, 90vw)", boxShadow: "0 16px 48px rgba(0,0,0,0.35)" }}>
        <div style={{ fontFamily: "'Instrument Serif', serif", fontSize: 20, color: t.text, marginBottom: 6, fontWeight: 400 }}>Save Template</div>
        <p style={{ color: t.textDim, fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>Give this template a name so you can reuse these settings later.</p>
        <input
          autoFocus
          style={{ ...getInp(t), marginBottom: 16 }}
          placeholder="e.g. iPhone resale, Client invoice..."
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && name.trim()) onSave(name.trim()); }}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "10px 0", background: "transparent", border: `1px solid ${t.border}`, borderRadius: 8, color: t.textMuted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={() => name.trim() && onSave(name.trim())} disabled={!name.trim()} style={{ flex: 2, padding: "10px 0", background: name.trim() ? t.accent : t.surface, border: `1px solid ${name.trim() ? t.accent : t.border}`, borderRadius: 8, color: name.trim() ? "#0c0a09" : t.textDim, fontSize: 13, fontWeight: 700, cursor: name.trim() ? "pointer" : "not-allowed", fontFamily: "inherit", transition: "all 0.2s" }}>
            Save Template
          </button>
        </div>
      </div>
    </>
  );
}

function TemplatesPanel({ templates, onLoad, onDelete, onClose, t, isMobile }) {
  const [filter, setFilter] = useState("all");
  const adCatInfo = id => AD_CATEGORIES.find(c => c.id === id);
  const emailTypeInfo = id => ALL_EMAIL_TYPES.find(et => et.id === id);

  const allTools = [...new Set(templates.map(tp => tp.tool))];
  const filterOptions = [{ value: "all", label: "All" }, ...allTools.map(tool => ({ value: tool, label: tool === "ads" ? "📢 Ads" : "✉️ Email" }))];

  const filtered = filter === "all" ? templates : templates.filter(tp => tp.tool === filter);

  function getBadge(tp) {
    if (tp.tool === "ads") {
      const c = adCatInfo(tp.category);
      return `${c?.icon || "📢"} ${c?.label || "Ad"}`;
    }
    const e = emailTypeInfo(tp.category);
    return `${e?.icon || "✉️"} ${e?.label || "Email"}`;
  }

  return (
    <div style={{ position: "fixed", top: 0, right: 0, width: isMobile ? "100%" : 360, height: "100vh", background: t.surface, borderLeft: `1px solid ${t.border}`, zIndex: 100, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.25)", animation: "slideIn 0.25s ease forwards" }}>
      <div style={{ padding: "20px 20px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: t.text }}>Templates</div>
          <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>{templates.length} saved · reusable form presets</div>
        </div>
        <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${t.border}`, color: t.textDim, borderRadius: 6, padding: "4px 10px", fontSize: 13, cursor: "pointer" }}>✕</button>
      </div>

      {templates.length > 1 && (
        <div style={{ padding: "12px 16px 0" }}>
          <div style={{ display: "flex", background: t.card, borderRadius: 8, padding: 3, border: `1px solid ${t.border}`, gap: 2 }}>
            {filterOptions.map(opt => (
              <button key={opt.value} onClick={() => setFilter(opt.value)} style={{ flex: 1, padding: "6px 4px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, background: filter === opt.value ? t.surface : "transparent", color: filter === opt.value ? t.text : t.textDim, transition: "all 0.2s", fontFamily: "inherit" }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: t.textDim }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: t.textMuted }}>No templates yet</div>
            <div style={{ fontSize: 11, marginTop: 6, lineHeight: 1.6 }}>Fill out a form and click<br/>"Save as Template" to save presets</div>
          </div>
        ) : filtered.map(tp => (
          <div key={tp.id} onClick={() => { onLoad(tp); onClose(); }}
            style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: 14, marginBottom: 10, cursor: "pointer", transition: "border 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = t.accent}
            onMouseLeave={e => e.currentTarget.style.borderColor = t.border}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, background: t.pillBg, color: t.pillText, padding: "1px 7px", borderRadius: 10, fontWeight: 600, display: "inline-block", marginBottom: 6 }}>{getBadge(tp)}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 3 }}>{tp.name}</div>
                <div style={{ fontSize: 11, color: t.textDim }}>Saved {new Date(tp.id).toLocaleDateString()}</div>
                {tp.preview && <div style={{ fontSize: 11, color: t.textDim, marginTop: 4, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>{tp.preview}</div>}
              </div>
              <button onClick={e => { e.stopPropagation(); onDelete(tp.id); }}
                style={{ background: "transparent", border: "none", fontSize: 13, cursor: "pointer", color: t.textDim, marginLeft: 8, flexShrink: 0 }}>🗑</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── HISTORY PANEL ──────────────────────────────────────────────────────────
function HistoryPanel({ history, onLoad, onDelete, onClearAll, onToggleFav, onClose, t, isMobile, isPro, onShowPlanModal }) {
  const FREE_HISTORY_LIMIT = 5;
  const favs   = history.filter(h => h.fav);
  const rest   = history.filter(h => !h.fav);
  const sorted = [...favs, ...rest];
  const visible = isPro ? sorted : sorted.slice(0, FREE_HISTORY_LIMIT);
  const hidden  = isPro ? 0 : Math.max(0, sorted.length - FREE_HISTORY_LIMIT);
  const emailTypeInfo = id => ALL_EMAIL_TYPES.find(t => t.id === id);
  const adCatInfo = id => AD_CATEGORIES.find(c => c.id === id);

  function getItemLabel(item) {
    if (item.tool === "ads") return item.input?.title || "Ad";
    if (item.tool === "email") return item.input?.clientName || item.input?.recipientName || item.input?.company || "Email";
    return "Item";
  }

  function getItemBadge(item) {
    if (item.tool === "ads") { const c = adCatInfo(item.input?.category); return `${c?.icon || "📢"} ${c?.label || "Ad"}`; }
    if (item.tool === "email") { const e = emailTypeInfo(item.input?.type); return `${e?.icon || "✉️"} ${e?.label || "Email"}`; }
    return "Item";
  }

  return (
    <div style={{ position: "fixed", top: 0, right: 0, width: isMobile ? "100%" : 360, height: "100vh", background: t.surface, borderLeft: `1px solid ${t.border}`, zIndex: 100, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.25)", animation: "slideIn 0.25s ease forwards" }}>
      <div style={{ padding: "20px 20px 16px", borderBottom: `1px solid ${t.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: t.text }}>History</div>
          <div style={{ fontSize: 11, color: t.textDim, marginTop: 2 }}>{history.length} saved · {favs.length} starred</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {history.length > 0 && <button onClick={onClearAll} style={{ background: "transparent", border: `1px solid ${t.border}`, color: t.textDim, borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Clear all</button>}
          <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${t.border}`, color: t.textDim, borderRadius: 6, padding: "4px 10px", fontSize: 13, cursor: "pointer" }}>✕</button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {sorted.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: t.textDim }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
            <div style={{ fontSize: 13 }}>No history yet</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>Generate something to see it here</div>
          </div>
        ) : visible.map(item => (
          <div key={item.id} onClick={() => { onLoad(item); onClose(); }} style={{ background: t.card, border: `1px solid ${item.fav ? t.accent : t.border}`, borderRadius: 10, padding: 14, marginBottom: 10, cursor: "pointer", transition: "border 0.2s" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 10, background: t.pillBg, color: t.pillText, padding: "1px 7px", borderRadius: 10, fontWeight: 600 }}>{getItemBadge(item)}</span>
                  {item.fav && <span style={{ fontSize: 11, color: t.accent }}>★</span>}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 2 }}>{getItemLabel(item)}</div>
                <div style={{ fontSize: 11, color: t.textDim }}>{new Date(item.id).toLocaleDateString()} · {new Date(item.id).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => onToggleFav(item.id)} style={{ background: "transparent", border: "none", fontSize: 16, cursor: "pointer", color: item.fav ? t.accent : t.textDim }}>{item.fav ? "★" : "☆"}</button>
                <button onClick={() => onDelete(item.id)} style={{ background: "transparent", border: "none", fontSize: 13, cursor: "pointer", color: t.textDim }}>🗑</button>
              </div>
            </div>
          </div>
        ))}
        {hidden > 0 && (
          <div onClick={onShowPlanModal} style={{ background: t.accentBg, border: `1px solid ${t.accent}44`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.accentText }}>🔒 {hidden} older {hidden === 1 ? "item" : "items"} hidden</div>
            <div style={{ fontSize: 11, color: t.textDim, marginTop: 4 }}>Upgrade to Pro for unlimited history</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, marginTop: 6 }}>✦ Go Pro</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MAIN APP ───────────────────────────────────────────────────────────────
const TABS = [
  { id: "ads",    icon: "📢", label: "Smart Ads" },
  { id: "email",  icon: "✉️", label: "Smart Email" },
  { id: "orders", icon: "📦", label: "Smart Orders" },
];

// ── SMART ORDERS ────────────────────────────────────────────────────────────

const ORDER_STATUSES = [
  { id: "received",    label: "Received",    color: "#3b82f6", bg: "rgba(59,130,246,0.1)"  },
  { id: "in_progress", label: "In Progress", color: "#f59e0b", bg: "rgba(245,158,11,0.1)"  },
  { id: "ready",       label: "Ready",       color: "#10b981", bg: "rgba(16,185,129,0.1)"  },
  { id: "delivered",   label: "Delivered",   color: "#6b7280", bg: "rgba(107,114,128,0.08)" },
];

const FREE_ORDER_LIMIT = 10;

function generateOrderId(orders) {
  const nums = orders.map(o => parseInt(o.id.replace("ORD-", ""), 10)).filter(n => !isNaN(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return "ORD-" + String(next).padStart(4, "0");
}

function StatusBadge({ status }) {
  const s = ORDER_STATUSES.find(x => x.id === status) || ORDER_STATUSES[0];
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
      background: s.bg, color: s.color, border: `1px solid ${s.color}33`, letterSpacing: "0.04em",
      textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

function OrderForm({ order, orders, onSave, onCancel, t }) {
  const isEdit = !!order;
  const [form, setForm] = useState(order || {
    customerName: "", customerPhone: "", item: "", price: "", deposit: "",
    dueDate: "", status: "received", notes: "",
  });

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function handleSave() {
    if (!form.customerName.trim() || !form.item.trim()) return;
    const now = Date.now();
    if (isEdit) {
      onSave({ ...form, updatedAt: now });
    } else {
      onSave({ ...form, id: generateOrderId(orders), createdAt: now, updatedAt: now });
    }
  }

  const inp = {
    width: "100%", padding: "10px 14px",
    background: t.card, border: `1px solid ${t.border}`,
    borderRadius: 10, fontSize: 14, color: t.text,
    fontFamily: "inherit", outline: "none",
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Customer Name *</div>
          <input style={inp} value={form.customerName} onChange={e => set("customerName", e.target.value)} placeholder="e.g. Sara Ahmed" />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Phone (WhatsApp)</div>
          <input style={inp} value={form.customerPhone}
            onChange={e => {
              let v = e.target.value.replace(/\s+/g, "");
              if (/^00/.test(v))       v = "+" + v.slice(2);        // 00971 → +971
              if (/^05\d/.test(v))     v = "+971" + v.slice(1);     // 0501234567 → +971501234567
              if (/^5\d{8}$/.test(v)) v = "+971" + v;              // 501234567 → +971501234567
              if (/^07\d/.test(v))     v = "+44" + v.slice(1);      // 07911 → +44
              if (/^01\d/.test(v))     v = "+20" + v.slice(1);      // 010 → +20 (Egypt)
              set("customerPhone", v);
            }}
            placeholder="e.g. 0501234567 or +971501234567"
          />
          {form.customerPhone && !/^\+/.test(form.customerPhone) && (
            <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 4 }}>
              💡 Add country code for WhatsApp — e.g. UAE: +971, UK: +44, Egypt: +20
            </div>
          )}
          {form.customerPhone && /^\+971/.test(form.customerPhone) && (
            <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>✓ UAE number detected</div>
          )}
          {form.customerPhone && /^\+44/.test(form.customerPhone) && (
            <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>✓ UK number detected</div>
          )}
          {form.customerPhone && /^\+20/.test(form.customerPhone) && (
            <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>✓ Egypt number detected</div>
          )}
          {form.customerPhone && /^\+[^9][^7]/.test(form.customerPhone) && !/^\+971|^\+44|^\+20/.test(form.customerPhone) && (
            <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>✓ International number</div>
          )}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Item / Service *</div>
        <input style={inp} value={form.item} onChange={e => set("item", e.target.value)} placeholder="e.g. Wedding dress alterations" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Total Price</div>
          <input style={inp} value={form.price} onChange={e => set("price", e.target.value)} placeholder="e.g. 350" type="number" min="0" />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Deposit Paid</div>
          <input style={inp} value={form.deposit} onChange={e => set("deposit", e.target.value)} placeholder="e.g. 100" type="number" min="0" />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Due Date</div>
          <input style={inp} value={form.dueDate} onChange={e => set("dueDate", e.target.value)} type="date" />
        </div>
      </div>

      {isEdit && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Status</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {ORDER_STATUSES.map(s => (
              <button key={s.id} onClick={() => set("status", s.id)} style={{
                padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                border: `1px solid ${form.status === s.id ? s.color : t.border}`,
                background: form.status === s.id ? s.bg : "transparent",
                color: form.status === s.id ? s.color : t.textDim,
                cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
              }}>{s.label}</button>
            ))}
          </div>
        </div>
      )}

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", marginBottom: 6, textTransform: "uppercase" }}>Notes</div>
        <textarea style={{ ...inp, resize: "vertical", minHeight: 72 }} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="e.g. Take in waist, shorten hem 5cm" />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={handleSave} disabled={!form.customerName.trim() || !form.item.trim()} style={{
          flex: 1, padding: "12px 0", background: t.accent, border: "none", borderRadius: 10,
          fontSize: 14, fontWeight: 800, color: "#0c0a09", cursor: "pointer", fontFamily: "inherit",
          opacity: (!form.customerName.trim() || !form.item.trim()) ? 0.5 : 1,
        }}>
          {isEdit ? "✦ Save Changes" : "✦ Create Order"}
        </button>
        <button onClick={onCancel} style={{
          padding: "12px 20px", background: "transparent", border: `1px solid ${t.border}`,
          borderRadius: 10, fontSize: 14, fontWeight: 600, color: t.textDim,
          cursor: "pointer", fontFamily: "inherit",
        }}>Cancel</button>
      </div>
    </div>
  );
}

function OrderDetail({ order, onEdit, onDelete, onStatusChange, onClose, onWhatsAppLog, t, clerkUserId }) {
  const [msgLoading, setMsgLoading] = useState(false);
  const [generatedMsg, setGeneratedMsg] = useState(null);
  const [msgContext, setMsgContext] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null);

  const balance = order.price && order.deposit
    ? Number(order.price) - Number(order.deposit)
    : order.price ? Number(order.price) : null;

  const STATUS_ACTIONS = {
    received:    { suggestMsg: "confirm",  label: "Send confirmation to customer?" },
    in_progress: { suggestMsg: null },
    ready:       { suggestMsg: "ready",    label: "Notify customer their order is ready?" },
    delivered:   { suggestMsg: null },
  };

  const MSG_TYPES = {
    ready:    { label: "Order Ready",       icon: "✅", prompt: (ctx) => `Write a short friendly WhatsApp message telling a customer their order is ready for pickup. Customer: ${order.customerName}. Order: ${order.item}. Order #: ${order.id}.${balance ? ` Balance due: ${balance}.` : ""}${ctx ? ` Extra context: ${ctx}.` : ""} Max 3 sentences. Warm and professional. No excessive emojis.` },
    delay:    { label: "Need More Time",    icon: "⏳", prompt: (ctx) => `Write a short polite WhatsApp message apologising for a small delay. Customer: ${order.customerName}. Order: ${order.item}. Order #: ${order.id}.${order.dueDate ? ` Original due date: ${order.dueDate}.` : ""}${ctx ? ` Extra context: ${ctx}.` : ""} Max 3 sentences. Apologetic but reassuring.` },
    reminder: { label: "Payment Reminder", icon: "💰", prompt: (ctx) => `Write a polite WhatsApp payment reminder. Customer: ${order.customerName}. Order: ${order.item}. Order #: ${order.id}.${balance ? ` Amount due: ${balance}.` : ""}${ctx ? ` Extra context: ${ctx}.` : ""} Max 3 sentences. Friendly but clear.` },
    confirm:  { label: "Order Confirmed",  icon: "📋", prompt: (ctx) => `Write a short WhatsApp confirmation that a new order has been received. Customer: ${order.customerName}. Order: ${order.item}. Order #: ${order.id}.${order.dueDate ? ` Expected ready by: ${order.dueDate}.` : ""}${ctx ? ` Extra context: ${ctx}.` : ""} Max 3 sentences. Professional and warm.` },
  };

  function handleStatusTap(statusId) {
    if (statusId === order.status) return;
    onStatusChange(order.id, statusId);
    const action = STATUS_ACTIONS[statusId];
    if (action?.suggestMsg) {
      setPendingStatus(statusId);
      setGeneratedMsg(null);
      setMsgContext("");
    } else {
      setPendingStatus(null);
      setGeneratedMsg(null);
    }
  }

  function dismissMsg() {
    setPendingStatus(null);
    setGeneratedMsg(null);
    setMsgContext("");
  }

  const activeMsgType = pendingStatus ? STATUS_ACTIONS[pendingStatus]?.suggestMsg : null;
  const activeMsgDef = activeMsgType ? MSG_TYPES[activeMsgType] : null;

  async function generateMsg() {
    if (!activeMsgDef) return;
    setMsgLoading(true);
    setGeneratedMsg(null);
    try {
      const headers = { "Content-Type": "application/json" };
      if (clerkUserId) headers["x-clerk-user-id"] = clerkUserId;
      else headers["x-anon-id"] = ANON_ID;
      const res = await fetch(`${PROXY_URL}/api/claude`, {
        method: "POST", headers,
        body: JSON.stringify({ max_tokens: 300, messages: [{ role: "user", content: activeMsgDef.prompt(msgContext) }] }),
      });
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("").trim();
      setGeneratedMsg(text);
    } catch {
      setGeneratedMsg("Could not generate message. Please try again.");
    }
    setMsgLoading(false);
  }

  function sendWhatsApp() {
    if (!order.customerPhone || !generatedMsg) return;
    const phone = order.customerPhone.replace(/\D/g, "");
    if (!phone) return;
    onWhatsAppLog?.(`${activeMsgDef?.icon} ${activeMsgDef?.label}`);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(generatedMsg)}`, "_blank");
    dismissMsg();
  }

  const [manualPanel, setManualPanel] = useState(false);
  const [manualType, setManualType] = useState("reminder");
  const [manualMsg, setManualMsg] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualContext, setManualContext] = useState("");

  async function generateManualMsg() {
    setManualLoading(true);
    setManualMsg(null);
    try {
      const def = MSG_TYPES[manualType];
      const headers = { "Content-Type": "application/json" };
      if (clerkUserId) headers["x-clerk-user-id"] = clerkUserId;
      else headers["x-anon-id"] = ANON_ID;
      const res = await fetch(`${PROXY_URL}/api/claude`, {
        method: "POST", headers,
        body: JSON.stringify({ max_tokens: 300, messages: [{ role: "user", content: def.prompt(manualContext) }] }),
      });
      const data = await res.json();
      const text = data.content?.map(b => b.text || "").join("").trim();
      setManualMsg(text);
    } catch {
      setManualMsg("Could not generate message. Please try again.");
    }
    setManualLoading(false);
  }

  function sendManualWhatsApp() {
    if (!order.customerPhone || !manualMsg) return;
    const phone = order.customerPhone.replace(/\D/g, "");
    const def = MSG_TYPES[manualType];
    onWhatsAppLog?.(`${def.icon} ${def.label}`);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(manualMsg)}`, "_blank");
    setManualPanel(false);
    setManualMsg(null);
    setManualContext("");
  }

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onClose} style={{ background: "none", border: "none", color: t.textDim, cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: t.text }}>{order.id}</span>
            <StatusBadge status={order.status} />
          </div>
          <div style={{ fontSize: 13, color: t.textDim, marginTop: 2 }}>{order.customerName} · {order.item}</div>
        </div>
        <button onClick={onEdit} style={{ background: "rgba(217,119,6,0.1)", border: "1px solid rgba(217,119,6,0.35)", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: "#d97706", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
        <button onClick={onDelete} style={{ background: "none", border: "1px solid #ef444433", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#ef4444", cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
      </div>

      {/* Details grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Customer", value: order.customerName },
          { label: "Phone", value: order.customerPhone || "—" },
          { label: "Item", value: order.item },
          { label: "Due Date", value: order.dueDate || "—" },
          { label: "Total Price", value: order.price ? `${order.price} AED` : "—" },
          { label: "Deposit Paid", value: order.deposit ? `${order.deposit} AED` : "—" },
          { label: "Balance Due", value: balance !== null ? `${balance} AED` : "—" },
          { label: "Created", value: new Date(order.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) },
        ].map(item => (
          <div key={item.label} style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{item.label}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{item.value}</div>
          </div>
        ))}
      </div>

      {order.notes && (
        <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Notes</div>
          <div style={{ fontSize: 14, color: t.textDim, lineHeight: 1.6 }}>{order.notes}</div>
        </div>
      )}

      {/* ── Status + Message unified card ── */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, overflow: "hidden", marginBottom: 16 }}>

        {/* Status row */}
        <div style={{ padding: "16px 20px", borderBottom: pendingStatus ? `1px solid ${t.border}` : "none" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12 }}>Update Status</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {ORDER_STATUSES.map(st => (
              <button key={st.id} onClick={() => handleStatusTap(st.id)} style={{
                padding: "9px 18px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                border: `1px solid ${order.status === st.id ? st.color : t.border}`,
                background: order.status === st.id ? st.bg : "transparent",
                color: order.status === st.id ? st.color : t.textDim,
                cursor: st.id === order.status ? "default" : "pointer",
                fontFamily: "inherit", transition: "all 0.15s",
              }}>{st.label}</button>
            ))}
          </div>
        </div>

        {/* Message suggestion — slides in after status tap */}
        {pendingStatus && activeMsgDef && (
          <div style={{ padding: "16px 20px", animation: "fadeUp 0.25s ease" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>
                {activeMsgDef.icon} {STATUS_ACTIONS[pendingStatus].label}
              </div>
              <button onClick={dismissMsg} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1 }}>✕</button>
            </div>
            {!generatedMsg ? (
              <>
                <textarea
                  value={msgContext}
                  onChange={e => setMsgContext(e.target.value)}
                  placeholder='Optional context — e.g. "she already paid in full" or "been waiting 2 weeks"'
                  style={{ width: "100%", padding: "10px 14px", marginBottom: 10, background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 13, color: t.text, fontFamily: "inherit", outline: "none", resize: "none", minHeight: 52, lineHeight: 1.5 }}
                />
                <button onClick={generateMsg} disabled={msgLoading} style={{
                  width: "100%", padding: "11px 0", background: t.accent, border: "none",
                  borderRadius: 10, fontSize: 13, fontWeight: 800, color: "#0c0a09",
                  cursor: msgLoading ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: msgLoading ? 0.7 : 1,
                }}>
                  {msgLoading ? "Writing message..." : "✦ Write message"}
                </button>
              </>
            ) : (
              <div style={{ animation: "fadeUp 0.2s ease" }}>
                <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: 14, fontSize: 14, color: t.text, lineHeight: 1.7, marginBottom: 10, whiteSpace: "pre-line" }}>
                  {generatedMsg}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {order.customerPhone ? (
                    <button onClick={sendWhatsApp} style={{ flex: 1, padding: "11px 0", background: "#25d366", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>📲 Send on WhatsApp</button>
                  ) : (
                    <div style={{ flex: 1, fontSize: 12, color: t.textMuted, padding: "11px 0", textAlign: "center" }}>No phone number — add one to send via WhatsApp</div>
                  )}
                  <button onClick={() => navigator.clipboard.writeText(generatedMsg)} style={{ padding: "11px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#fafaf9", cursor: "pointer", fontFamily: "inherit" }}>📋</button>
                  <button onClick={() => { setGeneratedMsg(null); setMsgContext(""); }} style={{ padding: "11px 14px", background: "transparent", border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 13, color: t.textDim, cursor: "pointer", fontFamily: "inherit" }}>↺</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Manual message (no status change) ── */}
      {!manualPanel ? (
        <button onClick={() => { setManualPanel(true); setManualMsg(null); setManualContext(""); }} style={{
          width: "100%", padding: "10px 0", background: "transparent",
          border: `1px solid ${t.border}`, borderRadius: 12,
          fontSize: 13, fontWeight: 600, color: t.textDim,
          cursor: "pointer", fontFamily: "inherit", marginBottom: 20,
        }}>
          💬 Send a message without changing status
        </button>
      ) : (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: "16px 20px", marginBottom: 20, animation: "fadeUp 0.2s ease" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>Send Message</div>
            <button onClick={() => { setManualPanel(false); setManualMsg(null); }} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", fontSize: 16, padding: 0 }}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {Object.entries(MSG_TYPES).map(([id, def]) => (
              <button key={id} onClick={() => { setManualType(id); setManualMsg(null); setManualContext(""); }} style={{
                padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1px solid ${manualType === id ? t.accent : t.border}`,
                background: manualType === id ? `${t.accent}18` : "transparent",
                color: manualType === id ? t.accent : t.textDim,
                cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
              }}>{def.icon} {def.label}</button>
            ))}
          </div>
          {!manualMsg ? (
            <>
              <textarea
                value={manualContext}
                onChange={e => setManualContext(e.target.value)}
                placeholder='Optional context — e.g. "she already paid half" or "been waiting 2 weeks"'
                style={{ width: "100%", padding: "10px 14px", marginBottom: 10, background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 13, color: t.text, fontFamily: "inherit", outline: "none", resize: "none", minHeight: 52, lineHeight: 1.5 }}
              />
              <button onClick={generateManualMsg} disabled={manualLoading} style={{
                width: "100%", padding: "11px 0", background: t.accent, border: "none",
                borderRadius: 10, fontSize: 13, fontWeight: 800, color: "#0c0a09",
                cursor: manualLoading ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: manualLoading ? 0.7 : 1,
              }}>
                {manualLoading ? "Writing message..." : "✦ Write message"}
              </button>
            </>
          ) : (
            <div style={{ animation: "fadeUp 0.2s ease" }}>
              <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, padding: 14, fontSize: 14, color: t.text, lineHeight: 1.7, marginBottom: 10, whiteSpace: "pre-line" }}>
                {manualMsg}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {order.customerPhone ? (
                  <button onClick={sendManualWhatsApp} style={{ flex: 1, padding: "11px 0", background: "#25d366", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>📲 Send on WhatsApp</button>
                ) : (
                  <div style={{ flex: 1, fontSize: 12, color: t.textMuted, padding: "11px 0", textAlign: "center" }}>No phone number — add one to send via WhatsApp</div>
                )}
                <button onClick={() => navigator.clipboard.writeText(manualMsg)} style={{ padding: "11px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#fafaf9", cursor: "pointer", fontFamily: "inherit" }}>📋</button>
                <button onClick={() => { setManualMsg(null); setManualContext(""); }} style={{ padding: "11px 14px", background: "transparent", border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 13, color: t.textDim, cursor: "pointer", fontFamily: "inherit" }}>↺</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Activity Log ── */}
      {order.log && order.log.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>Activity</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {[...order.log].reverse().map((entry, i) => {
              const isLast = i === order.log.length - 1;
              const icon = entry.type === "created"   ? "✦"
                         : entry.type === "status"    ? "◎"
                         : entry.type === "whatsapp"  ? "💬"
                         : entry.type === "edited"    ? "✎"
                         : "·";
              const iconColor = entry.type === "created"  ? t.accent
                              : entry.type === "whatsapp" ? "#25d366"
                              : entry.type === "status"   ? "#3b82f6"
                              : t.textMuted;
              const d = new Date(entry.ts);
              const timeStr = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                + " · "
                + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
              return (
                <div key={i} style={{ display: "flex", gap: 12, position: "relative" }}>
                  {/* Line */}
                  {!isLast && (
                    <div style={{ position: "absolute", left: 9, top: 22, bottom: -8, width: 1, background: `${t.border}` }} />
                  )}
                  {/* Icon dot */}
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: `${iconColor}18`, border: `1px solid ${iconColor}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: iconColor, flexShrink: 0, marginTop: 2 }}>
                    {icon}
                  </div>
                  {/* Text */}
                  <div style={{ paddingBottom: isLast ? 0 : 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{entry.label}</div>
                    <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{timeStr}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SmartOrders({ t, isPro, isSignedIn, clerkUserId, onShowPlanModal }) {
  const [orders, setOrders] = useState(() => ls.get("limji_orders", []));
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => { ls.set("limji_orders", orders); }, [orders]);

  const activeOrders = orders.filter(o => o.status !== "delivered");
  const atLimit = !isPro && activeOrders.length >= FREE_ORDER_LIMIT;

  function addLog(orderId, entry) {
    setOrders(prev => prev.map(o => o.id === orderId
      ? { ...o, log: [...(o.log || []), { ...entry, ts: Date.now() }] }
      : o
    ));
  }

  function handleCreate(data) {
    const withLog = { ...data, log: [{ type: "created", label: "Order created", ts: Date.now() }] };
    setOrders(prev => [withLog, ...prev]);
    setView("list");
  }

  function handleEdit(data) {
    setOrders(prev => prev.map(o => o.id === data.id ? data : o));
    addLog(data.id, { type: "edited", label: "Order details edited" });
    setSelected(data);
    setView("detail");
  }

  function handleDelete(id) {
    if (!window.confirm("Delete this order?")) return;
    setOrders(prev => prev.filter(o => o.id !== id));
    setView("list");
  }

  function handleStatusChange(id, status) {
    const statusLabel = ORDER_STATUSES.find(s => s.id === status)?.label || status;
    setOrders(prev => prev.map(o => o.id === id ? { ...o, status, updatedAt: Date.now() } : o));
    setSelected(prev => prev?.id === id ? { ...prev, status } : prev);
    addLog(id, { type: "status", label: `Status → ${statusLabel}` });
  }

  function handleWhatsAppLog(id, msgTypeLabel) {
    addLog(id, { type: "whatsapp", label: `WhatsApp sent — ${msgTypeLabel}` });
  }

  const filtered = orders.filter(o => {
    if (filter !== "all" && o.status !== filter) return false;
    if (search) {
      const s = search.toLowerCase();
      return o.customerName.toLowerCase().includes(s) || o.id.toLowerCase().includes(s) || o.item.toLowerCase().includes(s);
    }
    return true;
  });

  if (view === "new") return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => setView("list")} style={{ background: "none", border: "none", color: t.textDim, cursor: "pointer", fontSize: 18, padding: 0 }}>←</button>
        <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: t.text }}>New Order</span>
      </div>
      <OrderForm orders={orders} onSave={handleCreate} onCancel={() => setView("list")} t={t} />
    </div>
  );

  if (view === "edit" && selected) return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => setView("detail")} style={{ background: "none", border: "none", color: t.textDim, cursor: "pointer", fontSize: 18, padding: 0 }}>←</button>
        <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: t.text }}>Edit {selected.id}</span>
      </div>
      <OrderForm order={selected} orders={orders} onSave={handleEdit} onCancel={() => setView("detail")} t={t} />
    </div>
  );

  if (view === "detail" && selected) {
    const current = orders.find(o => o.id === selected.id) || selected;
    return (
      <OrderDetail
        order={current}
        onEdit={() => setView("edit")}
        onDelete={() => handleDelete(current.id)}
        onStatusChange={handleStatusChange}
        onWhatsAppLog={(msgTypeLabel) => handleWhatsAppLog(current.id, msgTypeLabel)}
        onClose={() => setView("list")}
        t={t} clerkUserId={clerkUserId}
      />
    );
  }

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 800, color: t.text }}>Smart Orders</div>
          <div style={{ fontSize: 12, color: t.textDim, marginTop: 2 }}>
            {isPro ? `${orders.length} total orders` : `${activeOrders.length} / ${FREE_ORDER_LIMIT} active orders`}
          </div>
        </div>
        <button onClick={() => atLimit ? onShowPlanModal() : setView("new")} style={{
          padding: "10px 20px", background: t.accent, border: "none", borderRadius: 10,
          fontSize: 13, fontWeight: 800, color: "#0c0a09", cursor: "pointer", fontFamily: "inherit",
        }}>
          {atLimit ? "🔒 Upgrade for more" : "+ New Order"}
        </button>
      </div>

      {!isPro && (
        <div style={{ background: t.accentBg, border: `1px solid ${t.accent}44`, borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: t.textDim, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Free: {activeOrders.length}/{FREE_ORDER_LIMIT} active orders used</span>
          {atLimit && <button onClick={onShowPlanModal} style={{ background: t.accent, border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 800, color: "#0c0a09", cursor: "pointer", fontFamily: "inherit" }}>Upgrade →</button>}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, order #, item..."
          style={{ flex: 1, minWidth: 180, padding: "9px 14px", background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 13, color: t.text, fontFamily: "inherit", outline: "none" }} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["all", ...ORDER_STATUSES.map(s => s.id)].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: "8px 14px", borderRadius: 10, fontSize: 12, fontWeight: 600,
              border: `1px solid ${filter === f ? t.accent : t.border}`,
              background: filter === f ? `${t.accent}18` : "transparent",
              color: filter === f ? t.accent : t.textDim,
              cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
            }}>{f === "all" ? "All" : f.replace("_", " ")}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: t.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: t.textDim, marginBottom: 6 }}>
            {orders.length === 0 ? "No orders yet" : "No orders match your filter"}
          </div>
          <div style={{ fontSize: 13 }}>
            {orders.length === 0 ? "Create your first order to get started" : "Try a different filter or search term"}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {filtered.map(order => {
            const balance = order.price && order.deposit ? Number(order.price) - Number(order.deposit) : null;
            const isOverdue = order.dueDate && order.status !== "delivered" && new Date(order.dueDate) < new Date();
            return (
              <div key={order.id} onClick={() => { setSelected(order); setView("detail"); }} style={{
                background: t.surface, border: `1px solid ${t.border}`, borderRadius: 14,
                padding: "16px 18px", cursor: "pointer", transition: "all 0.15s",
                borderLeft: `3px solid ${ORDER_STATUSES.find(s => s.id === order.status)?.color || t.border}`,
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 800, color: t.accent }}>{order.id}</span>
                      <StatusBadge status={order.status} />
                      {isOverdue && <span style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 20, padding: "2px 8px" }}>OVERDUE</span>}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{order.customerName}</div>
                    <div style={{ fontSize: 12, color: t.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{order.item}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {order.price && <div style={{ fontSize: 14, fontWeight: 800, color: t.text }}>{order.price} AED</div>}
                    {balance !== null && balance > 0 && <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 2 }}>Due: {balance} AED</div>}
                    {order.dueDate && <div style={{ fontSize: 11, color: isOverdue ? "#ef4444" : t.textMuted, marginTop: 2 }}>{order.dueDate}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── END SMART ORDERS ────────────────────────────────────────────────────────

export default function App() {
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    return ["ads", "email", "orders"].includes(t) ? t : "ads";
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(() => ls.get("bizcopy_history", []));
  const [historyOpen, setHistoryOpen] = useState(false);
  const [templates, setTemplates] = useState(() => ls.get("limji_templates", []));
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [dark, setDark] = useState(() => ls.get("bizcopy_theme", "dark") === "dark");
  const [currentId, setCurrentId] = useState(null);
  const [lastFormats, setLastFormats] = useState([]);
  const [showWelcome, setShowWelcome] = useState(() => !ls.get("limji_welcomed", false));
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [remaining, setRemaining] = useState(null); // null = loading from server
  const [serverLimit, setServerLimit] = useState(FREE_LIMIT);
  const [isPro, setIsPro] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const isMobile = useIsMobile();
  const th = getTheme(dark);

  // Pro users bypass the daily limit

  // Reset Pro status when user signs out
  useEffect(() => {
    if (!isSignedIn) {
      setIsPro(false);
      setServerLimit(ANON_LIMIT);
    }
  }, [isSignedIn]);

  // Check subscription status when user signs in
  useEffect(() => {
    if (!isSignedIn || !user) return;
    fetch(`${PROXY_URL}/api/ls/subscription/${user.id}`)
      .then(r => r.json())
      .then(data => { if (data.subscribed) setIsPro(true); })
      .catch(() => {});
  }, [isSignedIn, user]);

  // Fetch real usage from server on load and when user changes
  useEffect(() => {
    function fetchUsage() {
      const headers = {};
      if (isSignedIn && user) headers["x-clerk-user-id"] = user.id;
      else headers["x-anon-id"] = ANON_ID;
      fetch(`${PROXY_URL}/api/usage`, { headers })
        .then(r => r.json())
        .then(data => {
          setRemaining(data.remaining);
          setServerLimit(data.limit);
          if (data.isPro) setIsPro(true);
        })
        .catch(() => setRemaining(isSignedIn ? FREE_LIMIT : ANON_LIMIT));
    }

    fetchUsage();

    // Re-sync when user comes back to the tab (catches back-button navigation)
    window.addEventListener("focus", fetchUsage);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") fetchUsage();
    });

    return () => {
      window.removeEventListener("focus", fetchUsage);
    };
  }, [isSignedIn, user]);

  // Handle ?upgraded=true after Stripe redirect and ?tab= deep links
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgraded") === "true") {
      setIsPro(true);
      window.history.replaceState({}, "", "/app");
    }
    const tabParam = params.get("tab");
    if (tabParam === "email" || tabParam === "ads") {
      setTab(tabParam);
      window.history.replaceState({}, "", "/app");
    }
  }, []);

  async function startCheckout(plan) {
    if (!isSignedIn) { navigate("/sign-up"); return; }
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${PROXY_URL}/api/ls/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clerkUserId: user.id,
          userEmail: user.primaryEmailAddress?.emailAddress,
          plan,
        }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert("Something went wrong. Please try again.");
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  }

  function dismissWelcome() {
    ls.set("limji_welcomed", true);
    setShowWelcome(false);
  }

  useEffect(() => { ls.set("bizcopy_history", history); }, [history]);
  useEffect(() => { ls.set("bizcopy_theme", dark ? "dark" : "light"); }, [dark]);
  useEffect(() => { ls.set("limji_templates", templates); }, [templates]);

  // Listen for server usage updates after each generation
  useEffect(() => {
    const handler = (e) => {
      const { remaining: rem, isPro: serverIsPro } = e.detail;
      setRemaining(rem);
      if (serverIsPro) setIsPro(true);
      // Show modal immediately when this was the last generation
      if (!isPro && !serverIsPro && rem <= 0) setShowLimitModal(true);
    };
    window.addEventListener("limji_usage_update", handler);
    return () => window.removeEventListener("limji_usage_update", handler);
  }, [isPro, isSignedIn]);

  const handleResult = useCallback((r, input) => {
    if (r === null) { setResult(null); setCurrentId(null); return; }
    if (r.error) { setResult({ data: r, tool: tab, input }); return; }
    const id = Date.now();
    setCurrentId(id);
    setResult({ data: r, tool: tab, input });
    setHistory(prev => [{ id, tool: tab, input, data: r, fav: false }, ...prev].slice(0, MAX_HISTORY));
  }, [tab]);

  function checkAndConsumeLimit() {
    if (isPro) return true;
    // If server hasn't responded yet (null), optimistically allow — server will enforce
    if (remaining === null) return true;
    if (remaining <= 0) {
      setShowLimitModal(true);
      return false;
    }
    return true;
  }

  function saveTemplate(tpl) {
    const id = Date.now();
    setTemplates(prev => [{ id, ...tpl }, ...prev].slice(0, MAX_TEMPLATES));
  }
  function deleteTemplate(id) { setTemplates(prev => prev.filter(t => t.id !== id)); }

  function loadFromHistory(item) { setTab(item.tool); setResult({ data: item.data, tool: item.tool, input: item.input }); setCurrentId(item.id); }
  function toggleFav(id) { setHistory(prev => prev.map(h => h.id === id ? { ...h, fav: !h.fav } : h)); }
  function deleteItem(id) { setHistory(prev => prev.filter(h => h.id !== id)); if (currentId === id) { setResult(null); setCurrentId(null); } }

  const favCount = history.filter(h => h.fav).length;
  const isFav = history.find(h => h.id === currentId)?.fav;

  return (
    <div style={{ minHeight: "100vh", background: th.bg, fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif", color: th.text, transition: "background 0.2s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Instrument+Sans:wght@400;600;700&family=Instrument+Serif:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-thumb { background: #44403c; border-radius: 3px; }
        input:focus, textarea:focus, select:focus { outline: none; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.3s ease forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes cardIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.92); } }
        @keyframes msgFade { 0% { opacity: 0; transform: translateY(6px); } 15% { opacity: 1; transform: translateY(0); } 85% { opacity: 1; transform: translateY(0); } 100% { opacity: 0; transform: translateY(-6px); } }
        @keyframes barGrow { from { width: 0%; } to { width: 100%; } }
        .card-in { animation: cardIn 0.4s ease forwards; opacity: 0; }
        .card-in-1 { animation-delay: 0.05s; }
        .card-in-2 { animation-delay: 0.15s; }
        .card-in-3 { animation-delay: 0.25s; }
        .card-in-4 { animation-delay: 0.35s; }
        .card-in-5 { animation-delay: 0.45s; }
        @media (max-width: 767px) {
          .header-buttons .templates-label { display: none; }
          .header-buttons .history-label { display: none; }
          .panel-slide { width: 100% !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: `1px solid ${th.border}`, padding: isMobile ? "16px 16px 0" : "24px 24px 0", background: th.bg }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12, textDecoration: "none", cursor: "pointer" }}>
              <div style={{ width: 32, height: 32, background: th.accent, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0, color: "#0c0a09" }}>✦</div>
              <span style={{ fontFamily: "'Syne', sans-serif", fontSize: isMobile ? 20 : 24, fontWeight: 800, color: th.text, letterSpacing: "-0.01em" }}>Limji</span>
              {!isMobile && <span style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: 20, fontSize: 10, padding: "2px 10px", color: th.textDim, fontWeight: 600, letterSpacing: "0.08em" }}>AI TOOLKIT</span>}
            </a>
            <div className="header-buttons" style={{ display: "flex", gap: isMobile ? 6 : 8 }}>
              <button onClick={() => setDark(d => !d)} style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: 8, padding: isMobile ? "6px 10px" : "7px 12px", cursor: "pointer", fontSize: isMobile ? 14 : 16 }}>{dark ? "☀️" : "🌙"}</button>
              {/* Usage counter */}
              {(() => {
                const rem = remaining ?? (isSignedIn ? FREE_LIMIT : ANON_LIMIT);
                const lim = isSignedIn ? (serverLimit || FREE_LIMIT) : ANON_LIMIT;
                const isRed = !isPro && rem === 0;
                const isOrange = !isPro && rem <= 1 && rem > 0;
                const tooltip = isPro
                  ? "Pro — 150 generations/day"
                  : isSignedIn
                  ? `${rem} of ${lim} generations remaining today`
                  : `${rem} of ${lim} free tries remaining (lifetime)`;
                return (
                  <div
                    title={tooltip}
                    style={{ background: isRed ? "rgba(239,68,68,0.12)" : isPro ? th.accentBg : th.surface, border: `1px solid ${isRed ? "#ef4444" : isOrange ? "#f97316" : isPro ? th.accent : th.border}`, borderRadius: 8, padding: isMobile ? "6px 10px" : "7px 12px", fontSize: 11, fontWeight: 700, color: isRed ? "#ef4444" : isOrange ? "#f97316" : isPro ? th.accentText : th.textDim, display: "flex", alignItems: "center", gap: 4, cursor: "default" }}
                  >
                    {isPro ? "✦ Pro" : `${isRed ? "⏳" : "✦"} ${rem}/${lim}`}
                  </div>
                );
              })()}
              <button onClick={() => setTemplatesOpen(true)} style={{ background: templates.length > 0 ? th.accentBg : th.surface, border: `1px solid ${templates.length > 0 ? th.accent : th.border}`, borderRadius: 8, padding: isMobile ? "6px 10px" : "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: templates.length > 0 ? th.accentText : th.textMuted, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}>
                📋 {!isMobile && <span className="templates-label">Templates </span>}{templates.length > 0 && <span style={{ background: th.accent, color: "#0c0a09", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{templates.length}</span>}
              </button>
              {favCount > 0 && <button onClick={() => setHistoryOpen(true)} style={{ background: th.accentBg, border: `1px solid ${th.accent}`, borderRadius: 8, padding: isMobile ? "6px 10px" : "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: th.accentText, fontFamily: "inherit" }}>★ {favCount}</button>}
              <button onClick={() => setHistoryOpen(true)} style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: 8, padding: isMobile ? "6px 10px" : "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: th.textMuted, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
                🕐 {!isMobile && <span className="history-label">History </span>}{history.length > 0 && <span style={{ background: th.accent, color: "#0c0a09", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{history.length}</span>}
              </button>

              {/* Auth buttons */}
              <SignedOut>
                <SignInButton mode="redirect" redirectUrl="/sign-in">
                  <button style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: 8, padding: isMobile ? "6px 10px" : "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: th.textDim, fontFamily: "inherit" }}>
                    Sign in
                  </button>
                </SignInButton>
                {!isMobile && (
                  <SignUpButton mode="redirect" redirectUrl="/sign-up">
                    <button style={{ background: th.accent, border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#0c0a09", fontFamily: "inherit" }}>
                      Get Pro ✦
                    </button>
                  </SignUpButton>
                )}
              </SignedOut>
              <SignedIn>
                {!isPro && !isMobile && (
                  <button onClick={() => setShowPlanModal(true)} disabled={checkoutLoading} style={{ background: th.accent, border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#0c0a09", fontFamily: "inherit", opacity: checkoutLoading ? 0.7 : 1 }}>
                    {checkoutLoading ? "Loading..." : "Get Pro ✦"}
                  </button>
                )}
                <button onClick={() => navigate("/account")} title="My account" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                  {user?.imageUrl ? (
                    <img src={user.imageUrl} alt="" style={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${th.border}` }} />
                  ) : (
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: th.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#0c0a09" }}>
                      {user?.firstName?.[0] || "?"}
                    </div>
                  )}
                </button>
                <button
                  onClick={() => signOut({ redirectUrl: "/" })}
                  title="Sign out"
                  style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: 8, padding: "6px 9px", cursor: "pointer", fontSize: 14, color: th.textMuted, display: "flex", alignItems: "center", lineHeight: 1 }}
                >
                  ↪
                </button>
              </SignedIn>
            </div>
          </div>
          {!isMobile && <p style={{ color: th.textDim, fontSize: 13, marginBottom: 24, paddingLeft: 46 }}>Copy that sounds like you — not like a robot.</p>}
          {isMobile && <p style={{ color: th.textDim, fontSize: 12, marginBottom: 16, marginTop: 6 }}>Copy that sounds like you — not like a robot.</p>}
          <div style={{ display: "flex", borderBottom: `1px solid ${th.border}` }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); setResult(null); }} style={{ padding: isMobile ? "10px 20px" : "10px 24px", background: "transparent", border: "none", borderBottom: tab === t.id ? `2px solid ${th.accent}` : "2px solid transparent", color: tab === t.id ? th.text : th.textDim, cursor: "pointer", fontSize: isMobile ? 12 : 13, fontWeight: 600, fontFamily: "inherit", transition: "all 0.15s", marginBottom: -1, display: "flex", alignItems: "center", gap: 7 }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: isMobile ? "20px 16px" : "36px 24px",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : (result || loading ? "460px 1fr" : "520px"),
        gap: isMobile ? 20 : 32,
        justifyContent: "center"
      }}>
        <div>
          <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: isMobile ? 12 : 16, padding: isMobile ? 20 : 28 }}>
            {tab === "orders" ? (
              <SmartOrders t={th} isPro={isPro} isSignedIn={isSignedIn} clerkUserId={user?.id} onShowPlanModal={() => isSignedIn ? setShowPlanModal(true) : setShowSignInModal(true)} />
            ) : (
              <>
                <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: isMobile ? 18 : 20, color: th.text, marginBottom: isMobile ? 18 : 24, fontWeight: 400 }}>
                  {tab === "ads" ? "What are you listing?" : "What email do you need?"}
                </h2>
                {tab === "ads"
                  ? <SmartAdsForm onResult={(r, input) => { if (r !== null) setLastFormats(Object.keys(r).filter(k => k !== "tip" && k !== "error")); handleResult(r, input); }} loading={loading} setLoading={setLoading} t={th} templates={templates} onSaveTemplate={saveTemplate} pendingTemplate={tab === "ads" ? pendingTemplate : null} onTemplateMounted={() => setPendingTemplate(null)} isMobile={isMobile} checkLimit={checkAndConsumeLimit} clerkUserId={user?.id} onLimitReached={() => setShowLimitModal(true)} isPro={isPro} onShowPlanModal={() => isSignedIn ? setShowPlanModal(true) : setShowSignInModal(true)} />
                  : <SmartEmailForm onResult={handleResult} loading={loading} setLoading={setLoading} t={th} templates={templates} onSaveTemplate={saveTemplate} pendingTemplate={tab === "email" ? pendingTemplate : null} onTemplateMounted={() => setPendingTemplate(null)} isMobile={isMobile} checkLimit={checkAndConsumeLimit} clerkUserId={user?.id} onLimitReached={() => setShowLimitModal(true)} />}
              </>
            )}
          </div>
        </div>

        {loading && !result && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: 16, width: "100%", maxWidth: 420 }}>
              <LoadingScreen tab={tab} t={th} />
            </div>
          </div>
        )}

        {result && (
          <div className="fade-up">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isMobile ? 14 : 20, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 10 : 0 }}>
              <h2 style={{ fontFamily: "'Instrument Serif', serif", fontSize: isMobile ? 17 : 20, color: th.text, fontWeight: 400 }}>
                {result.tool === "ads" ? (result.input?.category === "store" ? "Your product copy — ready to use" : "Your ads — ready to publish") : "Your emails — 2 versions"}
              </h2>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <ExportBtn result={result} t={th} />
                <button onClick={() => { setResult(null); setCurrentId(null); }} style={{ background: "transparent", border: `1px solid ${th.border}`, color: th.textDim, borderRadius: 8, padding: "4px 12px", cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>✕ Clear</button>
              </div>
            </div>
            {result.tool === "ads"
              ? <SmartAdsResult r={result.data} t={th} onFav={() => currentId && toggleFav(currentId)} isFav={isFav} selectedFormats={lastFormats} isStore={result.input?.category === "store"} />
              : <EmailResult r={result.data} t={th} onFav={() => currentId && toggleFav(currentId)} isFav={isFav} isPro={isPro} onShowPlanModal={() => isSignedIn ? setShowPlanModal(true) : setShowSignInModal(true)} emailInput={result.input} clerkUserId={user?.id} />}
          </div>
        )}
      </div>

      {historyOpen && <>
        <div onClick={() => setHistoryOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }} />
        <HistoryPanel history={history} onLoad={loadFromHistory} onDelete={deleteItem} onClearAll={() => { setHistory([]); setHistoryOpen(false); }} onToggleFav={toggleFav} onClose={() => setHistoryOpen(false)} t={th} isMobile={isMobile} isPro={isPro} onShowPlanModal={() => isSignedIn ? setShowPlanModal(true) : setShowSignInModal(true)} />
      </>}
      {templatesOpen && <>
        <div onClick={() => setTemplatesOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }} />
        <TemplatesPanel templates={templates} onLoad={(tp) => { setTab(tp.tool); setPendingTemplate(tp); setTemplatesOpen(false); }} onDelete={deleteTemplate} onClose={() => setTemplatesOpen(false)} t={th} isMobile={isMobile} />
      </>}
      {showWelcome && <WelcomeModal onClose={dismissWelcome} t={th} />}
      {showLimitModal && <LimitModal onClose={() => setShowLimitModal(false)} t={th} onUpgrade={startCheckout} isSignedIn={isSignedIn} remaining={remaining} anonLimit={ANON_LIMIT} />}
      {showSignInModal && <SignInModal onClose={() => setShowSignInModal(false)} t={th} remaining={remaining} anonLimit={ANON_LIMIT} />}
      {showPlanModal && <PlanModal onClose={() => setShowPlanModal(false)} t={th} onUpgrade={(plan) => { setShowPlanModal(false); startCheckout(plan); }} />}
    </div>
  );
}
